/**
 * Client-portal UI strings (en / zh / th).
 *
 * The portal was written with its labels inline in English, several of them hand-built bilingual
 * ("Sign in / เข้าสู่ระบบ"). Those are split here: each locale carries only its own half. A handful
 * keep a Thai gloss in every locale on purpose — where the Thai is the STATUTORY name of a document
 * or form (ใบลดหนี้, 50 ทวิ) rather than a translation, the operator has to see which Thai document
 * they are issuing, because that is the name the server prints on the document face.
 *
 * Nothing here decides tax treatment. Thai wording for the credit/debit-note reasons is copied
 * verbatim from server/src/services/thaiTaxDocs.ts, which is what actually prints on the note.
 */

export const portalEn: Record<string, string> = {
  // admin
  p_admin_title: '{{brand}} Client Portal — Admin',
  // bank
  p_bank_acct_name: 'Account name',
  p_bank_acct_no: 'Account number',
  p_bank_add: '+ Add bank',
  p_bank_address: 'Bank address',
  p_bank_address_hint: '— needed for overseas transfers',
  p_bank_address_ph: '1 Bangkok Blvd, Pathum Wan, Bangkok 10330, Thailand',
  p_bank_confirm_remove: 'Remove this bank account?',
  p_bank_currency: 'Currency',
  p_bank_empty: 'No bank account yet — add one so it prints on your documents.',
  p_bank_name: 'Bank & branch',
  p_bank_name_ph: 'Kasikornbank (KBank), One Bangkok Branch',
  p_bank_no_swift: '⚠ No SWIFT / bank address — overseas payers cannot wire to this account.',
  p_bank_note: 'Note (optional)',
  p_bank_note_ph: 'e.g. for USD invoices only',
  p_bank_qr_alt: 'QR',
  p_bank_qr_hint: 'PromptPay / bank QR — printed in the Payment box on invoices & quotations',
  p_bank_qr_label: 'Payment QR',
  p_bank_qr_title: 'Payment QR printed on documents',
  p_bank_swift: 'SWIFT / BIC',
  p_bank_swift_ph: 'KASITHBK',
  p_bank_title: 'Bank accounts on documents',
  // bills
  p_bills_loading: 'Loading…',
  p_bills_open_pdf: 'Open PDF',
  p_bills_subtitle: 'The quotations, invoices and receipts {{brand}} issued to you.',
  p_bills_tab_invoices: 'Invoices',
  p_bills_tab_quotations: 'Quotations',
  p_bills_tab_receipts: 'Receipts',
  p_bills_th_date: 'Date',
  p_bills_th_due: 'Due',
  p_bills_th_issued_by: 'Issued by',
  p_bills_th_net: 'Net to pay',
  p_bills_th_no: 'No.',
  p_bills_th_paid_on: 'Paid on',
  p_bills_th_status: 'Status',
  p_bills_th_total: 'Total ({{cur}})',
  p_bills_th_valid_until: 'Valid until',
  p_bills_title: 'Bills from {{brand}}',
  p_menu_group_settings: 'Settings',
  p_menu_files: 'Documents', p_menu_files_hint: 'Everything we hold for you, in one place',
  p_files_title: 'Your documents',
  p_files_subtitle: 'Everything {{brand}} holds for you — filed returns, Revenue Department receipts, your company papers and what you have sent in.',
  p_files_loading: 'Loading…', p_files_none: 'Nothing here.', p_files_all: 'All',
  p_files_search: 'Search documents…',
  p_files_name: 'Document', p_files_folder: 'Type', p_files_size: 'Size', p_files_added: 'Added',
  p_files_view: 'View', p_files_download: 'Download',
  p_files_f_tax: 'Tax & filings', p_files_f_company: 'Company papers', p_files_f_uploads: 'You sent us',
  p_files_f_deliverables: 'Delivered to you', p_files_f_workflow: 'Service files', p_files_f_library: 'Other',
  p_files_elsewhere: 'Your invoices and receipts from {{brand}} are under Bills from {{brand}}; the filed returns are also listed under Tax & Filings.',
  p_nav_finance: 'P&L',
  p_fin_title: 'Profit & loss {{year}}',
  p_fin_subtitle: 'From what is actually recorded in your books — no estimates and nothing projected forward.',
  p_fin_loading: 'Loading…',
  p_fin_no_data: 'Nothing recorded for this year yet.',
  p_fin_revenue: 'Revenue',
  p_fin_expenses: 'Expenses',
  p_fin_purchases: 'Purchases',
  p_fin_salaries: 'Wages',
  p_fin_sso: 'Social security (employer share)',
  p_fin_pnd3: 'Fees paid to individuals',
  p_fin_rent53: 'Rent',
  p_fin_expenses_total: 'Total expenses',
  p_fin_net: 'Net',
  p_fin_by_month: 'Month by month',
  p_fin_month: 'Month',
  p_fin_no_payroll: 'payroll not prepared',
  p_fin_incomplete: 'Payroll has not been prepared for month {{months}}, so wages and social security show as zero there. Those months are not final.',
  p_fin_note: 'Revenue and purchases are before VAT. Social security shows the employer half only — the employee half is withheld from wages and is not an extra cost to the company. Fees paid to companies are already inside purchases, so only fees paid to individuals are listed separately.',
  p_fin_unposted: 'Some documents recorded for month {{months}} are not posted to your books yet, so those months are not final.',
  p_fin_not_posted: 'not yet posted',
  p_fin_note_books: 'Taken from the entries posted to your books, each in the month of its date — the same entries the profit or loss statement under Financial statements is built from.',
  p_fin_outside: 'Documents are recorded for month {{months}} with no date, or on dates that none of your accounting periods covers, so they are not in these figures.',
  p_fin_outside_short: 'outside your accounting periods',
  p_fin_no_voucher: 'Your PND.1, social security, PND.3 or company-landlord rent (PND.53) records show wages, fees or rent for month {{months}}, but no entry in your books dated in those months records them as an expense. If they were booked in another month, they are in that month\'s figures; otherwise they are not in these figures.',
  p_fin_no_voucher_short: 'no entry this month for wages, fees or rent',
  p_fin_rental_no_voucher: 'You have an active rental with a company landlord, but there is no rent (PND.53) record for it for month {{months}}, and no entry in your books dated in those months records rent as an expense. If rent for those months was booked in another month, it is in that month\'s figures; otherwise it is not in these figures.',
  p_fin_rental_no_voucher_short: 'active rental: no rent recorded or entered this month',
  p_fin_closed_unposted: 'Some documents recorded for month {{months}} are not in the closed books for that accounting year, so they are not in these figures.',
  p_fin_closed_unposted_short: 'recorded, but not in the closed books',
  p_nav_calendar: 'Calendar',
  p_cal_title: 'What is coming up',
  p_cal_subtitle: 'Your filing dates and the work on either side. Anything marked "you" is waiting on you.',
  p_cal_loading: 'Loading…', p_cal_nothing: 'Nothing scheduled here.',
  p_cal_all_year: 'Whole year', p_cal_today: 'today', p_cal_you: 'you',
  p_cal_only_mine: 'Only mine ({{count}})', p_cal_yours_count: '{{count}} waiting on you',
  p_cal_is_filing: 'filing deadline', p_cal_done: 'done', p_cal_blocked: 'blocked',
  p_cal_k_pp30: 'VAT (PP.30)', p_cal_k_pnd1: 'PND.1', p_cal_k_pnd3: 'PND.3', p_cal_k_pnd53: 'PND.53',
  p_cal_k_sso: 'Social security', p_cal_k_pnd51: 'Half-year CIT (PND.51)', p_cal_k_pnd50: 'Annual CIT (PND.50)',
  p_cal_k_pnd90: 'Personal income tax', p_cal_k_audit: 'Audit', p_cal_k_annual_fs: 'Annual financial statements',
  p_cal_k_bookkeeping: 'Bookkeeping', p_cal_k_property_tax: 'Property tax',
  p_cal_k_invoice_data_upload: 'Send us your invoices', p_cal_k_tax_payment: 'Pay your tax',
  p_cal_k_service_payment: 'Pay our fee', p_cal_k_service_invoice: 'Our invoice to you',
  p_cal_k_tax_remit: 'We remit your tax', p_cal_k_lead_quotation: 'Quotation',
  p_cal_k_lead_contract: 'Contract', p_cal_k_lead_collect_info: 'Send us your details',
  p_doc_got_bill: 'Received a bill from a supplier?',
  p_doc_got_receipt: 'Got a receipt for something you paid?',
  p_doc_send_to_slf: 'Send it to us to be read and booked →',
  p_nav_hr: 'Staff',
  p_hr_title: 'Your staff',
  p_hr_subtitle: 'Your people and what they cost in social security. To change anything, send us a request — we apply it.',
  p_hr_loading: 'Loading…',
  p_hr_new_request: 'Request a change',
  p_hr_pending_note: '{{count}} request(s) waiting for {{brand}} to review.',
  p_hr_name: 'Name', p_hr_position: 'Position', p_hr_salary: 'Monthly salary',
  p_hr_joined: 'Joined', p_hr_status: 'Status', p_hr_nationality: 'Nationality',
  p_hr_no_staff: 'No staff on record yet.',
  p_hr_active: 'Active', p_hr_left: 'Left', p_hr_not_in_sso: 'Not in social security',
  p_hr_joined_this_month: 'Joined this month',
  p_hr_left_this_month: 'Leaving this month',
  p_hr_change_salary: 'Salary', p_hr_leaving: 'Leaving',
  p_hr_sso_title: 'Social security this month',
  p_hr_sso_how: 'Each side pays {{rate}}% of the monthly wage. Wages below {{floor}} are treated as {{floor}}, and each side is capped at {{cap}} — so above that salary the contribution stops rising. The employee half is withheld from the wage; the employer pays the same again. A month someone was employed in for even one day is a month they are counted for — the month they join and the month they leave both count in full.',
  p_hr_check_these: 'Worth checking this month:',
  p_hr_mover_note: 'Anyone joining or leaving mid-month is where an automatic figure most often needs a person to look. Tell us if it should differ and we will set it by hand.',
  p_hr_employee_side: 'Employee', p_hr_employer_side: 'Employer', p_hr_total: 'Total',
  p_hr_month_total: 'Total · {{count}} people', p_hr_manual: 'set by hand',
  p_hr_no_sso: 'Nobody is in social security for this month.',
  p_hr_snapshot: 'This month is fixed — it is what was filed.',
  p_hr_preview: 'This month is not filed yet, so it is calculated from today\'s staff list and can still change.',
  p_hr_year_title: 'Social security across {{year}}',
  p_hr_month: 'Month', p_hr_headcount: 'People', p_hr_wages: 'Wages',
  p_hr_filed_snap: 'fixed', p_hr_preview_short: 'preview',
  p_hr_requests: 'Your requests',
  p_hr_k_salary_change: 'Salary change', p_hr_k_employee_add: 'New employee', p_hr_k_employee_leave: 'Employee leaving',
  p_hr_s_pending: 'Waiting for {{brand}}', p_hr_s_applied: 'Applied', p_hr_s_rejected: 'Not applied',
  p_hr_request_title: 'Request a change',
  p_hr_request_note: 'This does not change anything by itself. {{brand}} reviews it, because your social security and PND.1 change with it.',
  p_hr_kind: 'What changes', p_hr_employee: 'Employee', p_hr_pick_employee: 'Choose…',
  p_hr_start_date: 'Start date', p_hr_last_day: 'Last working day',
  p_hr_new_salary: 'New monthly salary', p_hr_effective_from: 'Effective from',
  p_hr_note: 'Anything we should know', p_hr_cancel: 'Cancel', p_hr_send: 'Send request', p_hr_sending: 'Sending…',
  p_hr_sso_preview: 'At this salary social security would be {{employee}} from the employee and the same from you — {{total}} in total each month.',
  p_hr_err: 'Could not send that. Please try again.',
  // ── PandaAI Tax (read-only) ──
  p_nav_tax: 'Tax',
  p_tax_title: 'Tax filings',
  p_tax_subtitle: 'Every tax we file for you: how the figure was worked out, what it came to, who has paid what, and the filed paperwork.',
  p_tax_loading: 'Loading…',
  p_tax_pick: 'Choose a filing on the left.',
  p_tax_not_configured: 'Not handled by {{brand}}',
  p_tax_nothing_filed: 'Nothing filed yet',
  p_tax_half_year: 'Filed once per half-year',
  p_tax_annual: 'Filed once per year',
  p_tax_calc: 'How this was worked out',
  p_tax_no_calc: 'No breakdown recorded for this period.',
  p_tax_amount: 'Amount due',
  p_tax_you_paid_slf: 'You paid {{brand}}',
  p_tax_slf_paid_rd: '{{brand}} paid the Revenue Department',
  p_tax_settled: 'Settled in full',
  p_tax_outstanding: 'Still outstanding',
  p_tax_nothing_billed: 'Nothing billed to you for this period',
  p_tax_remitted: 'Remitted',
  p_tax_not_remitted: 'Not yet remitted',
  p_tax_filed: 'Return filed',
  p_tax_method: 'Payment method',
  p_tax_method_na: 'Not recorded',
  p_tax_docs: 'Filed paperwork',
  p_tax_no_docs: 'No documents for this period yet.',
  p_tax_l_out_base: 'Sales (excl. VAT)',
  p_tax_l_out_vat: 'Output VAT',
  p_tax_l_in_base: 'Purchases (excl. VAT)',
  p_tax_l_in_vat: 'Input VAT',
  p_tax_l_net: 'Output − Input',
  p_tax_l_brought: 'Credit brought forward',
  p_tax_l_carry: 'Credit carried to next month',
  p_tax_l_surcharge: 'Surcharge',
  p_tax_l_fines: 'Fines',
  p_tax_l_headcount: 'Employees',
  p_tax_l_gross_wages: 'Gross wages',
  // ── What SLF does (read-only) ──
  p_menu_services: 'Our services',
  p_menu_services_hint: 'What we do, what it costs, and how each one runs',
  p_svc_title: 'What {{brand}} does',
  p_svc_subtitle: 'Our full service list, with your price where one is agreed. Open any service to see how it runs.',
  p_svc_search: 'Search services…',
  p_svc_loading: 'Loading services…',
  p_svc_none: 'No service matches that.',
  p_svc_yours: 'Yours',
  p_svc_your_price: 'Your price',
  p_svc_list_price: 'List price',
  p_svc_how: 'How this service runs:',
  p_svc_steps_loading: 'Loading steps…',
  p_svc_no_steps: 'No steps recorded for this service yet.',
  p_svc_step_you: 'you',
  p_svc_step_slf: '{{brand}}',
  // ── Jobs from SLF (read-only) ──
  p_nav_jobs: 'Jobs',
  p_jobs_title: 'Jobs at {{brand}}',
  p_jobs_subtitle: 'What we are working on for you, and where each one stands. View only.',
  p_jobs_loading: 'Loading jobs…',
  p_jobs_all: 'All',
  p_jobs_no: 'Job',
  p_jobs_what: 'What it is',
  p_jobs_status: 'Status',
  p_jobs_due_date: 'Target / done',
  p_jobs_amount: 'Amount',
  p_jobs_paid: 'Paid',
  p_jobs_outstanding: 'Outstanding',
  p_jobs_date_tbc: 'To be confirmed',
  p_jobs_waiting_on: 'Waiting on',
  p_jobs_opened: 'Opened',
  p_jobs_updated: 'updated',
  p_jobs_st_not_started: 'Not started',
  p_jobs_st_in_progress: 'In progress',
  p_jobs_st_with_you: 'Needs you',
  p_jobs_st_with_authority: 'With a government office',
  p_jobs_st_with_third_party: 'With a third party',
  p_jobs_st_done: 'Completed',
  p_jobs_st_cancelled: 'Cancelled',
  // bs — bank statements the client sends in (owner 2026-10-13)
  p_bs_title: 'Send us your bank statements',
  p_bs_subtitle: 'Statements of your own bank accounts, straight from the bank. {{brand}} matches each line against your invoices and receipts by hand — uploading here changes nothing in your books or your tax return on its own.',
  p_bs_account: 'Bank account',
  p_bs_account_auto: 'Auto-detect from the file',
  p_bs_no_accounts: 'No bank account on your profile yet — add your accounts there first so a statement can be recognised.',
  p_bs_pw_label: 'PDF password',
  p_bs_pw_ph: 'Only if the bank protects the file',
  p_bs_pw_hint: 'Used only to open this file, never stored',
  p_bs_upload: '＋ Upload statements',
  p_bs_uploading: 'Reading…',
  p_bs_file_hint: 'PDF statements from your bank or a CSV export — up to {{max}} files at a time, {{mb}} MB each. Photos and Excel cannot be read.',
  p_bs_too_many: 'Please send at most {{max}} files at a time.',
  p_bs_upload_failed: 'Upload failed',
  p_bs_res_saved: '✓ {{filename}} — account {{account}} · {{rows}} lines read, {{added}} new, {{skipped}} already held',
  p_bs_res_failed: '✗ {{filename}} — {{error}}',
  p_bs_err_account_not_registered: 'the account on this statement is not on your profile yet — add it there first',
  p_bs_err_statement_account_mismatch: 'the statement is for a different account than the one you chose',
  p_bs_list_title: 'Statements received',
  p_bs_th_file: 'File',
  p_bs_th_account: 'Account',
  p_bs_th_period: 'Period',
  p_bs_th_rows: 'Lines',
  p_bs_rows_cell: '{{added}} new of {{rows}}',
  p_bs_closing: 'closing {{amount}}',
  p_bs_th_uploaded: 'Uploaded',
  p_bs_via_portal: 'by you',
  p_bs_via_workspace: 'by {{brand}}',
  p_bs_act_view: 'View',
  p_bs_act_withdraw: 'Withdraw',
  p_bs_confirm_withdraw: 'Withdraw {{filename}}? The lines nobody has matched yet go with it.',
  p_bs_withdraw_locked: 'This statement cannot be withdrawn — {{brand}} has already matched some of its lines.',
  p_bs_empty: 'No statements yet.',
  p_bs_err_open: 'Could not open the file',
  p_bs_file_fallback: 'statement',
  // btn
  p_btn_cancel: 'Cancel',
  p_btn_save: 'Save',
  p_btn_save_bilingual: 'Save',
  // dlg — the document window's chrome (MerchantDoc/shell.tsx). The discard question is worded
  // exactly as Admin/JobRequiredDocs' rqd_discard and the workspace's aw_dlg_discard, because it is
  // the same question asked by the same idiom; three screens must not ask it three ways.
  p_dlg_close: 'Close',
  p_dlg_discard: 'Discard the unsaved changes?',
  // cdoc
  p_cdoc_add_line: '+ line',
  p_cdoc_bank_cash: 'Paid in cash — no bank details',
  p_cdoc_bank_label: 'Bank account printed on this {{docKind}}',
  p_cdoc_bank_none: '— none on file (add one under Profile) —', p_cdoc_bank_unset: '— none printed on this document —',
  // ── Whose charge is this line? ──────────────────────────────────────────────────────────────
  // One control, two stored fields (server: charge_role + supplier_invoice_billed_to). The question
  // asked is the one the person invoicing can actually answer — WHOSE NAME IS ON THE SUPPLIER'S
  // BILL — never "is this out of scope", which is a tax position and not theirs to take. The words
  // have to work for a forwarder recharging a port handling fee they paid for their customer AND
  // for someone billing their own hour of work; no section numbers, nothing about a return.
  p_cdoc_charge_hint_cust: 'You paid a third party for your customer and are passing the same amount on with nothing added — and the supplier’s receipt or tax invoice is made out to your customer, not to you.',
  p_cdoc_charge_hint_own: 'You are charging for something your own company did or supplied. This is the usual choice.',
  p_cdoc_charge_hint_self: 'You paid a third party for your customer, but the supplier’s receipt or tax invoice is made out to your own company.',
  p_cdoc_charge_opt_cust: 'Paid out for the customer — billed in their name',
  p_cdoc_charge_opt_own: 'Our own service or goods',
  p_cdoc_charge_opt_self: 'Paid out for the customer — billed in our name',
  p_cdoc_charge_title: 'Is this your own charge, or money you paid out for your customer?',
  p_cdoc_charge_unset: '— not stated —',
  p_cdoc_charge_w503: 'The supplier’s invoice is in your own company’s name, so this line stays a charge of your own — nothing is passed through. Change the choice if the supplier billed your customer directly. You can still issue this document.',
  p_cdoc_charge_w505: 'This line also states a VAT treatment of its own, so it stays a charge of your own — nothing is passed through. Only a line with no VAT treatment of its own is treated as money you paid out and passed on.',
  // The operator's own two figures, compared. ⛔ No rule is quoted and none is invented: the hint on
  // the "billed in their name" option has always said that what is passed on is passed on with
  // nothing added, and this says what their own numbers say about that. Nothing is blocked.
  p_cdoc_charge_w506: 'You have said the supplier charged {{cost}} in total, and this line charges your customer {{charged}} for it — {{diff}} more. (Any VAT this line adds is left out of that figure: you collect it and pay it over, so it is not part of what you charge.) Money paid out for your customer is passed on at what it cost; anything added to it is a charge of your own. Both figures stay on the line, flagged for review. You can still issue this document.',
  p_cdoc_choose_service: '— choose service —',
  p_cdoc_correct_prompt: 'Why is {{docNo}} being corrected?\n\nThis is recorded against the document together with its previous figures.',
  p_cdoc_cost_ph: 'Supplier charged',
  p_cdoc_cost_title: 'The total the supplier charged you for this line, as it appears on their bill — including any VAT shown on it. Money paid out for your customer is passed on at what it cost, so stating it here lets this line be checked against what you charge for it (not counting any VAT this line adds). When you issue a new document in baht and this line has no VAT of its own but charges more than this, the amount added is saved as a separate line of your own, with VAT — both lines are shown before you save. Optional — leaving it blank states nothing, changes no figure on this document and stops nothing.',
  // The two lines a marked-up pass-through is saved as (owner 2026-10-20: the amount added carries
  // 7%). Shown under the line BEFORE saving; the server splits it the same way (portalDocs.ts
  // splitMarkups). ⛔ No rule is quoted: it says what the operator's own two figures mean.
  p_cdoc_split_title: 'Saved as two lines',
  p_cdoc_split_why: 'The supplier charged {{cost}} and this line charges {{charged}}. The {{diff}} added is a charge of your own, so it is saved as its own line, with VAT; the {{cost}} stays money paid out for your customer, passed on at cost.',
  p_cdoc_split_cost_line: 'paid out for your customer, at cost — no VAT',
  p_cdoc_split_markup_line: 'your own charge — {{vatLabel}} · VAT {{vat}}',
  p_cdoc_split_name: 'Name of the added line, as printed on the document',
  p_cdoc_currency: 'Currency',
  p_cdoc_remark_ph: 'Notes printed on the document — formatting, line breaks and pasted pictures are kept.',
  p_cdoc_pay_method: 'Payment terms',
  p_cdoc_pay_method_none: '— not stated —',
  p_cdoc_pm_tt: 'T/T — Telegraphic transfer',
  p_cdoc_pm_lc: 'L/C — Letter of credit',
  p_cdoc_pm_sblc: 'SBLC — Standby letter of credit',
  p_cdoc_pm_bg: 'BG — Bank guarantee',
  p_cdoc_pay_method_note: 'Printed in the document header, and written out in full beside the bank details.',
  p_cdoc_pay_method_blank: 'Domestic invoices normally leave this blank. Set it when the deal is settled through a bank instrument.',
  p_cdoc_customer: 'Customer *',
  p_cdoc_doc_type: 'Document type',
  p_cdoc_doc_type_locked: '— fixed once issued',
  p_cdoc_due_date: 'Due date *',
  p_cdoc_due_hint: '— issue + {{days}} days',
  p_cdoc_edit_note: 'The document number stays {{docNo}}. If this document has already been sent to the customer, send them the corrected copy.',
  p_cdoc_entered_incl: 'Entered (VAT incl.) {{amount}} — split below',
  p_cdoc_err_due: 'A due date is required — when is this invoice payable?',
  p_cdoc_err_due_before: 'The due date cannot be before the issue date.',
  p_cdoc_err_line: 'Every line needs a service — choose one, or remove the empty line.',
  p_cdoc_err_reason: 'A reason is required to correct a tax document.',
  p_cdoc_fx_date: 'Rate date',
  p_cdoc_fx_note: 'A tax invoice is normally issued in Thai baht; a foreign currency needs the Revenue Department\'s approval (s.86/4). The THB equivalent is printed on the document.',
  p_cdoc_fx_published: 'Published rate on {{date}}: {{rate}}',
  p_cdoc_fx_published_near: 'No published rate for {{asked}}; the nearest earlier one is {{rate}} of {{date}}',
  p_cdoc_fx_far: 'The rate typed here is more than 2% away from the published one — check the figure and the currency.',
  p_cdoc_fx_ph: 'e.g. 33.50',
  p_cdoc_fx_rate: 'Rate to {{cur}} *',
  p_cdoc_issue_btn: 'Issue {{docKind}}',
  p_cdoc_issue_date: 'Issue date',
  p_cdoc_issuing: 'Issuing…',
  p_cdoc_items: 'Items',
  p_cdoc_less_wht: 'Less withholding tax {{amount}}',
  p_cdoc_net: 'Net you receive',
  p_cdoc_qty_title: 'Qty',
  p_cdoc_remark: 'Remark (optional)',
  p_cdoc_save_changes: 'Save changes',
  p_cdoc_saving: 'Saving…',
  p_cdoc_subtotal: 'Subtotal',
  p_cdoc_title_edit: 'Edit {{docKind}} {{docNo}}',
  p_cdoc_title_new: 'New {{docKind}}',
  p_cdoc_total: 'Total',
  p_cdoc_type_invoice: 'Invoice / ใบแจ้งหนี้ — services, tax invoice follows at payment',
  p_cdoc_type_proforma: 'Proforma Invoice — not a tax invoice, no VAT due yet',
  p_cdoc_type_tax_invoice: 'Tax Invoice / Delivery Note · ใบกำกับภาษี/ใบส่งของ — goods, VAT due on delivery',
  p_cdoc_type_tir: 'Tax Invoice / Receipt · ใบกำกับภาษี/ใบเสร็จรับเงิน — cash sale, paid on the spot',
  p_cdoc_unitprice_title: 'Unit price',
  p_cdoc_valid_until: 'Valid until',
  p_cdoc_vat: 'VAT',
  p_cdoc_vat_title: 'VAT treatment',
  // crec
  p_crec_already_paid: 'Already paid',
  p_crec_attach_hint: 'Attach the buyer\'s 50 ทวิ certificate afterwards from the receipt list.',
  p_crec_cash: 'Cash received ({{cur}})',
  p_crec_date_ok: 'Receipt will be dated and numbered in {{yyyymm}}',
  p_crec_date_required: 'Required — check the bank statement, not the day you type this in',
  p_crec_differs: '· differs from the {{amount}} outstanding — this will be recorded as a part payment',
  p_crec_err: 'Failed',
  p_crec_err_exceeds: 'Receipt of {{received}} is more than the {{outstanding}} still outstanding on {{docWordLc}} {{invoiceNo}} — a receipt cannot exceed what is owed. Enter at most {{outstanding}} (cash + WHT).',
  p_crec_err_wht_exceeds: 'WHT of {{wht}} is more than the {{left}} of withholding still to come on {{docWordLc}} {{invoiceNo}} — a receipt cannot record more WHT than the buyer still has to withhold. Enter at most {{left}} as WHT.',
  p_crec_err_zero: 'Amount received must be greater than zero.',
  p_crec_err_settled_doc: '{{docWord}} {{invoiceNo}} is already fully receipted ({{receiptNos}}) — nothing is outstanding on it, so another receipt would record the same sale twice.',
  p_crec_err_nothing_owed_doc: '{{docWord}} {{invoiceNo}} has nothing outstanding — there is no payment left to receipt.',
  p_crec_err_void: '{{docWord}} {{invoiceNo}} is void — nothing is outstanding on it, so there is no payment to receipt.',
  p_crec_err_credited: 'Nothing is outstanding on {{docWordLc}} {{invoiceNo}} (receipted {{receipted}}, credited {{credited}}) — there is no payment left to receipt.',
  p_crec_err_negative: 'Amounts cannot be negative — enter the cash received and the WHT the buyer withheld as 0 or more.',
  p_crec_err_nothing_owed: 'Invoice {{invoiceNo}} has nothing outstanding — there is no payment left to receipt.',
  p_crec_err_period_closed: 'The payment date {{date}} falls in a closed accounting period ({{from}} – {{to}}) — nothing may be entered into it. Check the day the money actually arrived.',
  p_crec_err_filed: 'VAT for {{ym}} is already marked FILED in the Filing Center: a receipt dated {{date}} would add an output tax line to a return already submitted. Check the day the money arrived, or unmark {{ym}} there first.',
  p_crec_err_fx_rate: '{{docNo}} is in {{currency}} and has no conversion rate this receipt could follow, so it would not be booked. Put the rate on {{docNo}} first.',
  p_crec_err_settled: 'Invoice {{invoiceNo}} is already fully receipted ({{receiptNos}}) — nothing is outstanding on it, so another receipt would record the same sale twice.',
  p_crec_exceeds: '· more than the {{amount}} outstanding — a receipt cannot exceed what is owed, so it cannot be issued',
  p_crec_expected_wht: 'Expected WHT',
  p_crec_invoice: 'Document *',
  p_crec_invoice_total: 'Document total',
  p_crec_issue: 'Issue receipt',
  p_crec_negative: '· an amount below zero — a receipt records money that arrived, so it cannot be issued',
  p_crec_of_which: '— of which WHT to come {{wht}}, cash {{cash}}',
  p_crec_wht_over: '· WHT is more than the {{wht}} of withholding still to come — a receipt cannot record more, so it cannot be issued',
  p_crec_zero: '· enter the cash received or the WHT withheld — a receipt of nothing cannot be issued',
  // Round 4.1 (decision 5): WHT typed for a document issued WITHOUT withholding — what to do next, the way out that exists.
  p_crec_wht_none_edit: '{{docWord}} {{invoiceNo}} was issued without withholding: if the buyer did withhold tax, edit {{invoiceNo}} first to give its lines their WHT rate, then record this payment.',
  p_crec_wht_none_cash: '{{docWord}} {{invoiceNo}} was issued without withholding and can no longer be edited: record only the cash received, and pass the buyer\'s withholding certificate to the accountant.',
  p_crec_wht_none_void: '{{docWord}} {{invoiceNo}} was issued without withholding: if the buyer did withhold tax, void {{receiptNos}} first, edit {{invoiceNo}} to give its lines their WHT rate, then record the cash and the WHT together in one receipt.',
  p_crec_wht_none_void_staff: '{{docWord}} {{invoiceNo}} was issued without withholding: if the buyer did withhold tax, ask the client to void {{receiptNos}} in their portal first (a receipt is voided there, not in this workspace), then edit {{invoiceNo}} to give its lines their WHT rate and record the cash and the WHT together in one receipt.',
  // Round 4.1 (decision 2): beside an Outstanding that credit notes moved (it is then not Total − Paid).
  p_owed_received_credited: 'Received {{received}} · Credited {{credited}}',
  p_crec_option_outstanding: '{{cur}} {{amount}} outstanding',
  p_crec_option_outstanding_wht: '{{cur}} {{amount}} outstanding — of which WHT to come {{wht}}, cash {{cash}}',
  p_crec_outstanding: 'Outstanding',
  p_crec_payment_date: 'Payment date (day the money arrived) *',
  p_crec_saving: 'Saving…',
  p_crec_select_invoice: '— select an unpaid document —',
  p_crec_settling: 'Settling {{total}} = cash {{cash}} + WHT {{wht}}',
  p_crec_subtitle: 'Choosing a document fills in what is still owed and the withholding the buyer is expected to take. Adjust either figure if the money that actually arrived differs.',
  p_crec_title: 'Record a payment',
  p_crec_wht: 'WHT withheld by buyer',
  // cust
  p_cust_add: '+ Add customer',
  p_cust_confirm_remove: 'Remove {{name}}?',
  p_cust_empty: 'No customers yet — add your first buyer.',
  p_cust_f_address: 'Address',
  p_cust_f_branch: 'Branch',
  p_cust_f_email: 'Email',
  p_cust_f_name: 'Company / person name *',
  p_cust_f_name_th: 'Thai name (optional)',
  p_cust_f_notes: 'Notes',
  p_cust_f_notes_hint: '— internal only, not shown on documents',
  p_cust_f_phone: 'Phone',
  p_cust_f_taxid: 'Tax ID',
  p_cust_modal_edit: 'Edit customer',
  p_cust_modal_new: 'New customer',
  p_cust_search_ph: 'Search name, code, tax ID, email…',
  p_cust_th_branch: 'Branch',
  p_cust_th_email: 'Email',
  p_cust_th_name: 'Name',
  p_cust_th_no: 'No.',
  p_cust_th_taxid: 'Tax ID',
  p_cust_title: 'My Customers',
  // disc
  p_disc_accept: 'I have read and accept the above.',
  p_disc_body_bold: 'You are solely responsible for the correctness and continuity of your document numbers',
  p_disc_body_p1: 'All of your sales documents (quotations, invoices, receipts) must be issued through this system so their running numbers stay continuous. Numbers are never reused; cancelling a document keeps its number as VOID.',
  p_disc_body_p2: ', including any documents you issue outside this system.',
  p_disc_continue: 'Continue',
  p_disc_title: 'Document numbering — your responsibility',
  // doc
  p_doc_act_attach_wht: 'Attach 50 ทวิ',
  p_doc_act_attach_wht_title: 'Attach the 50 ทวิ certificate from your buyer',
  p_doc_act_delete: 'Delete & free number',
  p_conv_already: 'Already billed from this quotation: {{list}}.',
  p_conv_already_stages: 'Already billed from this quotation: {{list}}. Issuing another is allowed — a quotation can be billed in stages — but your customer will receive a second invoice.',   // (owner 2026-11-03) main's sentence, where the workspace is off: billed in stages
  p_conv_cancel: 'Cancel',
  p_conv_done: 'Invoice {{docNo}} has been issued.',
  p_conv_go: 'Issue invoice',
  p_conv_loading: 'Checking this quotation…',
  p_conv_receipts: 'Payments already received against this quotation ({{list}}) stay where they are, so the new invoice opens unpaid for its full value. Moving them would rewrite documents your customer already holds.',
  p_conv_subtitle: 'The invoice copies this quotation line for line. It is dated today and starts unpaid.',
  p_conv_rejected: 'Quotation {{docNo}} is marked Rejected: it cannot be billed, edited or sent until its status is changed.',
  p_conv_err_already: '{{nos}} already issued from {{docNo}}, and a quotation is billed once. Void that document to bill this quotation again.',
  p_conv_err_tax_unpaid: '{{docNo}} has not been paid, so a tax invoice now would book this sale and its receipt would book it again. Record its payment with a receipt, then issue the tax invoice for it; or void {{docNo}} and issue the tax invoice instead.',
  p_conv_err_void: 'This quotation is void. Issue a new one instead.',
  p_conv_err_world_plain: 'This quotation was issued as a plain document, and this company now issues Thai tax documents. Issue a new quotation, then bill that one.',
  p_conv_err_world_thai: 'This quotation was issued under Thai tax rules, and this company now issues plain documents. Issue a new quotation, then bill that one.',
  p_conv_err_customer_gone: 'The customer on {{docNo}} is no longer in My Customers. Add them back, or issue a new quotation.',
  p_conv_err_customer: '{{docNo}} was quoted to {{customer}}, and a document issued from it goes to the same customer.',
  p_conv_err_no_lines: 'This quotation has no lines to bill.',
  p_conv_err_type: 'This company does not issue that kind of document from a quotation.',
  p_conv_err_not_found: 'The quotation this document names was not found among this company\'s documents.',
  p_conv_err_receipt_needs_bill: 'A receipt is written on a bill. Issue the invoice from {{docNo}} first.',
  p_conv_err_nothing_owed: 'Nothing is owed on {{nos}}, so there is nothing to receipt.',
  p_conv_err_cash_date: 'A cash sale says the money has arrived: enter the day it did.',
  p_conv_err_cash_future: 'A cash sale says the money has arrived, so it is not dated after today. Date it the day the money arrived.',
  p_conv_err_filed: 'VAT for {{ym}} is already marked FILED in the Filing Center: a document dated {{date}} would add an output tax line to a return already submitted. Choose another date, or unmark {{ym}} there first.',
  p_conv_err_repriced_vat: 'This quotation totals {{quoted}} but would bill as {{total}}: your VAT registration has changed since it was issued. Issue a new quotation so your customer sees the new figure, then bill that one.',
  p_conv_err_repriced_split: 'This quotation would not bill as quoted: a line on it passes a cost on for more than the supplier charged, and the amount added is now billed as your own charge with VAT. Issue a new quotation with these lines so your customer sees the new figure, then bill that one. Editing this quotation keeps its lines as they are, so it would still not bill.',
  p_conv_proformas: 'Proforma already raised from this quotation: {{list}}. A proforma bills nothing, so it stops nothing here, but your customer may already hold it.',
  p_conv_err_fx_approval: 'A tax invoice is in Thai baht unless the Revenue Department has approved another currency, and no approval is recorded for {{currency}}.',
  p_copy_err_customer_gone: 'This customer is no longer in My Customers. Choose another.',
  p_doc_edit_billed: '{{nos}} issued from this quotation, so it cannot be edited. Void that first.',
  p_conv_title: 'Issue an invoice from {{docNo}}',
  p_conv_type_invoice: 'Invoice',
  p_conv_type_invoice_hint: 'A bill. For services, VAT is due when your customer pays (s.78/1), so the tax invoice is the receipt.',
  p_conv_type_label: 'What kind of document is this?',
  p_conv_type_locked: 'This cannot be changed afterwards \u2014 it sets when the tax is due. To correct it you would have to void the invoice and issue a new one.',
  p_conv_type_proforma: 'Proforma invoice',
  p_conv_type_proforma_hint: 'Not a tax document. Creates no tax point and books nothing.',
  p_conv_type_tax_invoice: 'Tax invoice',
  p_conv_type_tax_invoice_hint: 'For goods, VAT is due on delivery (s.78), so the VAT is booked on this document \u2014 in today\u2019s month.',
  p_conv_working: 'Issuing\u2026',
  // Mark-up round 5, item 4: a foreign-currency quotation with no rate, billed as a tax invoice.
  p_conv_fx_rate_missing: '{{docNo}} is in {{currency}} and carries no conversion rate, so it cannot become a tax invoice yet: edit the quotation, enter the rate and its date, then issue the tax invoice. Nothing is issued and no number is used until then.',
  p_doc_act_to_invoice: 'Issue invoice',
  p_doc_act_edit: 'Edit',
  p_doc_act_pdf: 'PDF',
  p_doc_act_send: 'Send To…',
  p_doc_act_send_title: 'Email this document to your customer, PDF attached',
  p_doc_act_void: 'Void',
  p_doc_add_new: '+ New {{docKind}}',
  p_doc_add_note: '+ New credit / debit note',
  p_doc_add_receipt: '+ Record payment',
  p_doc_confirm_handed_over: '{{docNo}} has already been downloaded as a PDF, so the buyer may be holding a copy.\n\nEditing it changes what that copy verifies against. If they already have it, issue a credit/debit note instead, or void and reissue.\n\nEdit anyway?',
  p_doc_delete_prompt: 'Delete {{docNo}} and give its number back?\n\nOnly the latest number in a series can be given back. The next document you issue will reuse {{docNo}}.\n\nWhy? (recorded)',
  p_doc_empty: 'Nothing here yet.',
  p_doc_empty_notes: 'No credit or debit notes yet. Issue one from a tax invoice that needs correcting.',
  p_doc_err_open: 'Could not open this document.',
  p_doc_err_period_closed: 'The date {{date}} falls in a closed accounting period ({{from}} – {{to}}) — this document cannot be issued into it. Nothing may be entered into a closed period.',
  p_doc_err_period_closed_edit: '{{docNo}} is dated {{date}}, inside a closed accounting period ({{from}} – {{to}}) — it cannot be edited: that would change books that are already closed.',
  p_doc_err_period_closed_void: '{{docNo}} is dated {{date}}, inside a closed accounting period ({{from}} – {{to}}) — it cannot be voided or deleted: that would change books that are already closed.',
  p_doc_err_void_receipts_first: '{{docNo}} still has live receipts against it — void its receipt(s) first, then the document.',
  p_doc_freed: '{{docNo}} is free again — the next document will use it.',
  p_doc_head_invoices: 'invoices',
  p_doc_head_notes: 'notes',
  p_doc_head_quotations: 'quotations',
  p_doc_head_receipts: 'receipts',
  p_doc_kind_invoice: 'invoice',
  p_doc_kind_quotation: 'quotation',
  p_doc_not_editable: 'This document cannot be edited.',
  // Batch 11 R1 (COA-D9): the edit window's read-only note on a document already in the accounting books.
  p_ib_title: 'Recorded in the accounting books',
  p_ib_note: 'This document is recorded in the accounting books ({{docNos}}). If it needs changing, ask your accountant first.',
  // Batch 11 R3 (COA-D9): a document recorded in the accounting books — what the client may still do (never "reverse").
  p_ll_edit_limited: 'Edit (limited)',
  p_ll_void_locked: 'Recorded in the accounting books ({{docNos}}) — it can no longer be voided. To change it, contact your accountant.',
  p_ll_void_filed: 'The VAT return for {{month}} is already filed — this document can no longer be voided. To change it, contact your accountant (a credit or debit note corrects it).',   // batch 11 W4 P9 (LB-36)
  p_ll_delete_filed: 'The VAT return for {{month}} is already filed — this document can no longer be deleted. To change it, contact your accountant.',
  p_ll_delete_locked: 'Recorded in the accounting books ({{docNos}}) — its number can no longer be given back.',
  p_ll_limited_title: 'Recorded in the accounting books — limited edit',
  p_ll_tax_for_title: 'Issued for a paid invoice — limited edit',
  p_ll_tax_for_adjusted: 'This tax invoice is for {{forNo}} ({{receiptNos}}), which has since been adjusted by {{nos}}. It records nothing in the books, and its amounts, lines, date, buyer and currency stay as issued: only the remark, bank account, payment method and due date can be changed here.',
  p_ll_tax_for_changed: 'This tax invoice is for {{forNo}} ({{receiptNos}}), whose paid sale has changed since this tax invoice was issued. It records nothing in the books, and its amounts, lines, date, buyer and currency stay as issued: only the remark, bank account, payment method and due date can be changed here.',
  p_ll_tax_for_filed: 'This tax invoice is for {{forNo}} ({{receiptNos}}) and is dated {{date}}, in {{ym}}, whose VAT is already marked FILED. It records nothing in the books, but a tax invoice dated there is not re-issued: its amounts, lines, date, buyer and currency stay as issued — only the remark, bank account, payment method and due date can be changed here.',
  p_ll_tax_for_closed: 'This tax invoice is for {{forNo}} ({{receiptNos}}) and is dated {{date}}, inside a closed accounting period ({{from}} – {{to}}). It records nothing in the books, but a tax invoice dated there is not re-issued: its amounts, lines, date, buyer and currency stay as issued — only the remark, bank account, payment method and due date can be changed here.',
  p_ll_limited_note: 'Its amounts, lines, date, buyer and currency are recorded in the accounting books ({{docNos}}). Only the remark, bank account, payment method and due date can be changed here; for anything else, contact your accountant.',
  p_ll_receipt_superseded: 'A VAT line for this invoice ({{lineNo}}) is already recorded in the accounting books ({{docNos}}), so this receipt cannot be recorded yet. Please contact your accountant.',
  p_ib_hand_changed: '{{docNos}} was entered in your books by hand, and what it records has changed since — ask your accountant to check it.',
  p_doc_note_credit: 'Credit',
  p_doc_note_debit: 'Debit',
  p_doc_reason_required: 'A reason is required.',
  p_doc_search_ph: 'Search number, customer, address…',
  p_doc_send_tooltip: '{{count}} send(s) · last to {{email}}',
  p_doc_status_open: 'Open',
  p_doc_status_paid: 'Paid',
  p_doc_status_partial: 'Partial',
  p_doc_status_unpaid: 'Unpaid',
  p_doc_status_rejected: 'Rejected',
  p_doc_status_void: 'VOID',
  p_doc_th_adjusts: 'Adjusts',
  p_doc_th_customer: 'Customer',
  p_doc_th_date: 'Date',
  p_doc_th_difference: 'Difference ({{cur}})',
  p_doc_th_no: 'No.',
  p_doc_th_due: 'Due',
  p_doc_th_owing: 'Outstanding',
  p_doc_th_paid: 'Paid',
  p_sum_n_inv: '{{count}} invoice(s)',
  p_sum_n_doc: '{{count}} document(s)',
  p_sum_oldest: 'oldest {{days}} days \u2014 {{docNo}}',
  p_sum_outstanding: 'Awaiting payment',
  p_sum_overdue: 'Overdue',
  p_sum_scope: 'All periods, as of today',
  p_doc_th_invoice_no: 'Invoice',
  p_doc_link_self: 'This document',
  p_doc_th_property: 'Property',
  p_doc_th_receipt_no: 'Receipt',
  p_doc_th_received: 'Received ({{cur}})',
  p_doc_th_sent: 'Sent',
  p_doc_th_status: 'Status',
  p_doc_th_total: 'Total',
  p_doc_th_wht: 'WHT',
  p_doc_void_prompt: 'Void {{docNo}}?\n\nThe number stays in the sequence as VOID and is never reused.\n\nWhy is it being voided? (recorded)',
  p_doc_wht_saved: 'WHT certificate saved ✓',
  // empty
  p_empty_no_match: 'No match for “{{query}}”.',
  // erec
  p_erec_bank: 'Bank account shown on the document',
  p_erec_bank_none: '— none —',
  p_erec_received: 'Received {{amount}}',
  p_erec_remark: 'Remark',
  p_erec_saving: 'Saving…',
  p_erec_title: 'Edit receipt {{docNo}}',
  p_erec_warning: 'This receipt is a tax invoice. The customer, the amount, the VAT and the date are what the buyer claims input tax on, so they cannot be changed here — to correct any of those, void this receipt and record the payment again.',
  p_erec_wht: '· WHT {{amount}}',
  // err
  p_err_create_failed: 'Create failed',
  p_err_save_failed: 'Save failed',
  // exp
  p_exp_act_remove: 'Remove',
  p_exp_act_rescan: 'Re-read figures',
  p_exp_act_rescan_title: 'Read the amounts off this scan again',
  p_exp_act_view: 'View attachment',
  p_exp_confirm_remove: 'Remove {{filename}}?',
  p_exp_empty: 'Nothing uploaded for {{month}} {{year}} yet.',
  p_exp_err_open: 'Could not open the file',
  p_exp_file_fallback: 'file',
  p_exp_file_hint: 'Photos and PDF only — Word and Excel cannot be read; please export to PDF. A multi-page invoice should be sent as one PDF, not as separate photos.',
  p_exp_filed_warn: 'The VAT return for {{month}} {{year}} has already been filed. Anything you send for this month will be entered as an additional submission, not into the return that was already sent.',
  p_exp_filed_warn_bold: 'additional submission',
  p_exp_filter_all: 'All statuses',
  p_exp_filter_approved: 'Entered in your books',
  p_exp_filter_pending: 'Waiting for {{brand}}',
  p_exp_filter_rejected: 'Returned to you',
  p_exp_list_title: 'Uploaded — {{month}} {{year}}',
  p_exp_month: 'Month',
  p_exp_msg_appended: '· {{filename}} — added as page {{page}} of invoice {{docNo}}',
  p_exp_msg_ocr_error: 'Some files could not be read automatically — {{brand}} will type the figures in by hand.',
  p_exp_msg_saved: '{{n}} invoice(s) received and waiting for {{brand}}.',
  p_exp_msg_skipped: '{{n}} skipped as duplicates:',
  p_exp_msg_skipped_line: '· {{filename}}{{docNo}} — {{why}}',
  p_exp_no_vat: 'No VAT',
  p_exp_ocr_error_row: 'Could not be read automatically',
  p_exp_page_chip: 'p{{n}}',
  p_exp_page_title: 'Page {{n}}',
  p_exp_quota_unlimited: '{{used}} uploaded for {{month}} {{year}} · no monthly limit',
  p_exp_quota_full: 'Monthly limit of {{cap}} reached for {{month}} {{year}} — for a larger batch please email {{contact}}',
  p_exp_quota_left: '{{used}} of {{cap}} used for {{month}} {{year}} · {{left}} left',
  p_exp_reading: 'Reading…',
  p_exp_search_ph: 'Search supplier, invoice no, file…',
  p_exp_status_approved: 'Entered in your books',
  p_exp_status_pending: 'Waiting for {{brand}}',
  p_exp_status_rejected: 'Returned to you',
  p_exp_status_cancelled: 'Cancelled by firm',
  p_exp_subtitle: 'Photos or PDF of the bills you paid. {{brand}} checks each one against the scan before it goes into your books — nothing here changes your tax return on its own.',
  p_exp_th_amount: 'Amount',
  p_exp_th_attachment: 'Attachment',
  p_exp_th_bucket: 'Bucket',
  p_exp_th_date: 'Date',
  p_exp_th_docno: 'Invoice no.',
  p_exp_th_status: 'Status',
  p_exp_th_supplier: 'Supplier',
  p_exp_th_vat: 'VAT',
  p_exp_title: 'Send us your purchase invoices',
  p_exp_upload: '＋ Upload invoices',
  p_exp_upload_failed: 'Upload failed',
  p_exp_with_vat: 'With VAT',
  p_exp_year: 'Year',
  // fil
  p_fil_chip_additional: 'A{{n}}',
  p_fil_chip_attachment: 'Attachment',
  p_fil_chip_form: 'Form',
  p_fil_chip_receipt: 'Receipt',
  p_fil_legend: 'Green = filed, documents available below · Form = the filed return · Receipt = the Revenue Department payment receipt · ✓ = filed, document not uploaded yet. Missing something? Contact {{brand}} and we will add it.',
  p_fil_loading: 'Loading…',
  p_fil_subtitle: 'The returns {{brand}} filed for you, month by month. Click a month to open the filed form or the Revenue Department receipt.',
  p_fil_th_filing: 'Filing',
  p_fil_title: 'Tax & Filings',
  // filing
  p_filing_doc_gone: 'This document is no longer available. Please contact {{brand}}.',
  // img
  p_img_err_generic: 'Could not read that image',
  p_img_err_not_image: 'That file is not an image',
  p_img_err_read: 'Could not read the file',
  p_img_none: 'None',
  p_img_remove: 'Remove',
  p_img_replace: 'Replace',
  p_img_upload: 'Upload',
  // imp
  p_imp_already_bold: '{{n}} document number(s) already exist',
  p_imp_already_rest: 'and will stop the import: {{list}}',
  p_imp_btn: 'Import',
  p_imp_btn_n: 'Import {{n}}',
  p_imp_confirm: 'Create {{n}} {{docTypeWord}} using the document numbers in the sheet?\n\n{{m}} new customer record(s) will be created.\n\nThis cannot be undone in bulk — each document would have to be voided one by one.',
  p_imp_create_as: 'Create as',
  p_imp_done: '✓ {{created}} invoice(s) created · {{customers}} new customer(s)',
  p_imp_done_btn: 'Done',
  p_imp_err_failed: 'Import failed',
  p_imp_err_period_closed: 'Nothing was imported — {{n}} of the {{rows}} rows in this sheet are dated inside a closed accounting period ({{periods}}). Nothing may be entered into a closed period: take those rows out of the sheet and import it again.',
  p_imp_err_read: 'Could not read that file.',
  p_imp_open: 'Import from spreadsheet',
  p_imp_map_title: 'Match the spreadsheet columns to our fields',
  p_imp_map_saved: '· using the mapping you saved for this sheet',
  p_imp_map_edit: 'Edit mapping',
  p_imp_map_hide: 'Hide mapping',
  p_imp_map_th_column: 'Spreadsheet column',
  p_imp_map_th_sample: 'Example values',
  p_imp_map_th_field: 'Becomes',
  p_imp_map_th_vat: 'VAT treatment',
  p_imp_map_th_label: 'Invoice line name',
  p_imp_map_no_header: 'Column {{n}} (no header)',
  p_imp_map_required: 'Nothing is mapped to "{{field}}" — every document needs it.',
  p_imp_map_need_amount: 'Map at least one column of amounts — that is what becomes the invoice lines.',
  p_imp_field_ignore: 'Do not import',
  p_imp_field_customer: 'Customer name',
  p_imp_field_docNo: 'Invoice number',
  p_imp_field_issueDate: 'Issue date',
  p_imp_field_dueDate: 'Due date',
  p_imp_field_address: 'Address on the document',
  p_imp_field_description: 'Description',
  p_imp_field_remark: 'Remark',
  p_imp_field_fee: 'Amount (an invoice line)',
  p_imp_field_total: "Sheet's own total (cross-check only)",
  p_imp_fee_q: 'How is each fee column taxed?',
  p_imp_fee_warn: '— getting this wrong changes what every buyer pays by 7%',
  p_imp_file_label: 'Spreadsheet (.xlsx)',
  p_imp_header_incl: '· header says “include VAT”',
  p_imp_header_unknown: '· header does not say — please confirm',
  p_imp_mismatch_body: 'The invoice will be issued for the fee columns above; the sheet’s total may be measuring something different. Check a few before importing:',
  p_imp_mismatch_bold: '{{n}} row(s) do not reconcile to the sheet’s own total column.',
  p_imp_mismatch_line: 'row {{n}} · {{docNo}} · invoice {{a}} vs sheet {{b}}',
  p_imp_more: '… and {{n}} more',
  p_imp_prev_th_customer: 'Customer',
  p_imp_prev_th_date: 'Date',
  p_imp_prev_th_lines: 'Lines',
  p_imp_prev_th_total: 'Total',
  p_imp_sheet: 'Sheet',
  p_imp_skipped_head: 'Skipped — these will NOT be imported',
  p_imp_skipped_row: 'row {{n}}',
  p_imp_skipped_title: 'Skipped rows — nothing was created for these:',
  p_imp_subtitle: 'One row per invoice. The document numbers, dates and amounts come from your file exactly as they are — nothing is generated or adjusted.',
  p_imp_summary: '{{rows}} invoice(s) will be created · {{customers}} new customer(s) · total {{amount}} {{cur}}',
  p_imp_summary_skipped: '{{skipped}} row(s) skipped',
  p_imp_th_docno: 'Invoice no.',
  p_imp_th_owner: 'Owner',
  p_imp_th_property: 'Property',
  p_imp_th_row: 'Row',
  p_imp_th_why: 'Why',
  p_imp_title: 'Import invoices from a spreadsheet',
  p_imp_type_inv: 'Invoice — billing only, tax invoice follows at payment',
  p_imp_type_ti: 'Tax Invoice / Delivery Note — VAT due on delivery',
  p_imp_type_tir: 'Tax Invoice / Receipt — cash sale, already settled',
  p_imp_word_invoices: 'invoices',
  p_imp_word_tax_invoices: 'tax invoices',
  p_imp_working: 'Working…',
  // lang
  p_lang_en: 'English',
  p_lang_en_short: 'EN',
  p_lang_th_short: 'ไทย',
  p_lang_zh_short: '中',
  // loading
  p_loading: 'Loading…',
  // login
  p_login_email: 'Email',
  p_login_err_failed: 'Login failed',
  p_login_err_staff: 'This is the client portal — staff should use the internal system.',
  p_cdoc_line_account: 'Books to this income account (set per service on Products & accounts)',
  p_cdoc_line_account_default: 'Default from the service ({{code}})',
  p_cdoc_line_account_none: 'Account — not chosen',
  // Cross-installation sign-in (owner 2026-10-15): the hop is shown and confirmed before the
  // password goes anywhere but this build's own host.
  p_login_err_blank: 'Enter your email and password.',
  p_login_hop_text: 'This sign-in belongs to another office of the firm ({{host}}). Continue there?',
  p_login_hop_go: 'Yes, sign in there',
  p_login_hop_cancel: 'Cancel',
  p_login_password: 'Password',
  p_login_signin: 'Sign in',
  p_login_signing_in: 'Signing in…',
  p_login_subtitle: 'Invoicing & billing for your business',
  p_login_title: '{{brand}} Client Portal',
  // logo
  p_logo_alt: '{{brand}}',
  // menu
  p_menu_actions_tooltip: 'Actions',
  p_menu_bills: 'Bills from {{brand}}',
  p_menu_bills_hint: 'What {{brand}} has charged you',
  p_menu_customers: 'Customers',
  p_menu_customers_hint: 'The buyers you invoice',
  p_menu_filings: 'Tax & Filings',
  p_menu_filings_hint: 'The returns {{brand}} filed for you',
  p_menu_group_fromslf: 'From {{brand}}',
  p_menu_group_mydata: 'My data',
  p_menu_group_company: 'Company',
  p_menu_products: 'Products & Services',
  p_menu_products_hint: 'What you sell',
  p_menu_profile: 'Company Profile',
  p_menu_profile_hint: 'Address, logo, document numbering',
  p_menu_tooltip: 'Menu',
  p_menu_working: 'Working…',
  // month
  p_month_apr: 'Apr',
  p_month_aug: 'Aug',
  p_month_dec: 'Dec',
  p_month_feb: 'Feb',
  p_month_jan: 'Jan',
  p_month_jul: 'Jul',
  p_month_jun: 'Jun',
  p_month_mar: 'Mar',
  p_month_may: 'May',
  p_month_nov: 'Nov',
  p_month_oct: 'Oct',
  p_month_sep: 'Sep',
  // nav
  p_nav_bankstmt: 'Bank Statements',
  p_nav_expenses: 'Purchase Invoices',
  p_office_label: 'Your {{brand}} office',
  p_office_hint: 'The {{brand}} office that keeps your books and issues your documents',
  p_nav_owner: 'Owner console',
  p_nav_backoffice: 'Accounting books',
  p_nav_backoffice_hint: 'Open this company\'s books in the accounting workspace. Opens in a new tab and asks for a staff sign-in.',
  p_nav_mybooks: 'My books',
  p_nav_mybooks_hint: 'Your ledgers, trial balance and financial statements, as we keep them. Read-only.',
  p_own_title: 'Owner console',
  p_own_lede: 'Your business at a glance — what you are owed, what you owe, and what falls due next.',
  p_own_empty: 'Nothing to show here yet. This page fills in as you issue invoices and as we file on your behalf.',
  p_own_owed_you: 'OWED TO YOU',
  p_own_overdue_suffix: 'overdue',
  p_own_chase: 'OLDEST UNPAID',
  p_own_days_late: '{{days}} days past due',
  p_own_owed_slf: 'YOU OWE {{brand}}',
  p_own_next_due: 'Next due {{date}}',
  p_own_waiting: 'WAITING ON YOU',
  p_own_and_more: '+{{n}} more',
  p_own_task_upload: 'Send us this month\u2019s paperwork',
  p_own_task_tax_pay: 'Pay tax',
  p_own_task_svc_pay: 'Pay our fee',
  p_own_task_info: 'Send us the information we asked for',
  p_own_upcoming: 'DUE NEXT',
  p_own_today: 'today',
  p_own_in_days: 'in {{days}}d',
  p_own_amount_tbc: 'amount to come',
  p_own_amount_credit: '{{amount}} credit c/f',
  p_own_efiling_note: 'These are the Revenue Department e-filing deadlines, which fall later than the over-the-counter dates.',
  p_own_booked: 'BOOKED IN {{year}}',
  p_own_revenue: 'Sales',
  p_own_purchases: 'Purchases',
  p_own_booked_note: 'From the {{months}} month(s) recorded so far. This is not a profit figure — payroll only counts once a month has been closed.',
  p_own_filed: 'FILED FOR YOU IN {{year}}',
  p_own_filed_note: 'Returns we have submitted',
  p_nav_books: 'My books',
  p_menu_company_find: 'Find a company…',
  p_menu_books_locked: 'Appears once your books are opened',
  p_books_period: 'Accounting period',
  p_menu_company_none: 'No company matches that.',
  p_menu_company_n_of: '{{n}} of {{total}}',
  p_menu_policy: 'How we keep your books',
  p_menu_policy_hint: 'The treatment we actually apply to your records',
  p_menu_chart: 'Chart of accounts',
  p_menu_chart_hint: 'The accounts your books are kept in',
  p_menu_ledgers: 'Ledger & trial balance',
  p_menu_ledgers_hint: 'The five journals, the ledgers and the trial balance',
  p_books_no_entries: 'No entries this period',
  p_menu_statements: 'Financial statements',
  p_menu_statements_hint: 'Statement of financial position, profit or loss, changes in equity',
  p_pol_title: 'How we keep your books',
  p_pol_lede: 'What our system actually does with your records. This describes the rules the software applies — it is not a statement of Thai law. Where law is involved, your accountant\u2019s written advice governs.',
  p_pol_readonly: 'This page is read-only.',
  p_pol_s1_t: 'When your sale enters your books',
  p_pol_s1_b: 'A TAX INVOICE (including a tax invoice / receipt) is recorded as output in the month the document is issued. A PLAIN INVOICE is recorded only once payment is received and a receipt has been entered. So a plain invoice left unpaid this month does not appear in this month\u2019s VAT basis — and if it is never paid, it never appears.',
  p_pol_s2_t: 'When withholding tax is recorded',
  p_pol_s2_b: 'Tax withheld by your customer is recorded when the money arrives, not in the month of the invoice. Your receivable therefore shows the gross amount, not the sum you will actually bank.',
  p_pol_s3_t: 'Credit and debit notes',
  p_pol_s3_b: 'To change the value of an invoice already issued we raise a separate credit or debit note, recorded in its own month. The original invoice\u2019s record is never altered — that trail is what an auditor looks for.',
  p_pol_s4_t: 'Foreign currency',
  p_pol_s4_b: 'We hold no exchange rates of our own. On a foreign-currency document the rate is entered per document by whoever raises it, and any rate needed at year end must be supplied to us. Without a rate we decline to convert — we will not estimate one. Foreign-currency receivables are therefore shown per currency and never combined into a $t(cur_adj) total.',
  p_pol_s5_t: 'Input VAT you cannot reclaim',
  p_pol_s5_b: 'Input VAT that cannot be reclaimed is charged to the expense account kept for non-reclaimable input VAT, rather than left among recoverable input tax.',
  p_pol_s6_t: 'Valuing your stock',
  p_pol_s6_b: 'Two cost formulas are supported: FIFO and weighted average. Once a period has been valued on one, we cannot switch you to the other. Where the stock records do not reconcile — an issue with no matching receipt, or more going out than came in — we decline to produce a valuation rather than give you a figure with nothing behind it.',
  p_pol_s7_t: 'Depreciation',
  p_pol_s7_b: 'Depreciation is calculated straight line and prorated by the days you actually owned the asset within the period, over a 365-day year. It never takes an asset below its residual value, and it never posts itself: every charge is prepared as a draft and reviewed by a person before it enters your books. The ceilings we work to are below.',
  p_pol_s7_note: 'Rates are maximums. Where an asset is given no rate of its own, the maximum for its class is applied.',
  p_pol_s8_t: 'Tax losses carried forward',
  p_pol_s8_b: 'We keep your carried-forward losses year by year. The expiry column reads \u201cnot established\u201d, because we hold no source we can cite for it. Recording a limit requires recording its legal basis at the same time.',
  p_pol_s9_t: 'What happens to a document you send us',
  p_pol_s9_b: 'Every document is filed as one of three kinds. It first becomes a draft entry, reviewed by a person before it is posted. A posted entry is never deleted — an error is corrected by a visible reversing entry. Every entry lands in one of five named journals, and bank entries name the account they belong to.',
  p_pol_th_class: 'Asset class',
  p_pol_th_rate: 'Maximum rate',
  p_pol_not_dep: 'not depreciable',
  p_books_none_t: 'Your books have not been written up yet',
  p_books_none_b: 'Once we have opened an accounting period for you and posted the first entries, your chart of accounts, ledgers and financial statements appear here.',
  p_nav_invoices: 'Invoices',
  p_nav_notes: 'Credit / Debit Notes',
  p_nav_quotations: 'Quotations',
  p_nav_receipts: 'Receipts',
  p_nav_sign_out: 'Sign out',
  // note
  p_note_amount_ph: 'Amount',
  p_note_difference_label: 'Difference in value',
  p_note_err: 'Could not issue the note',
  p_note_info: 'Enter only the difference this note adjusts — not the whole original amount. The VAT on that difference moves your output tax in the month this note is issued.',
  p_note_issue_btn: 'Issue note',
  p_note_issue_date: 'Issue date',
  p_note_issuing: 'Issuing…',
  p_note_item_ph: 'What is being adjusted',
  p_note_kind_credit: 'credit note (ใบลดหนี้)',
  p_note_kind_debit: 'debit note (ใบเพิ่มหนี้)',
  p_note_no_sources: 'No tax invoice on file yet — a note can only adjust a document that carried VAT.',
  // A credit note is filled with the adjusted document's stored lines (owner, second round, 2026-10-20).
  // ⛔ Says what was done and what the operator does next — it is not a warning (option C was not chosen).
  p_note_prefilled: 'Filled in with the lines of {{docNo}} as it was issued, each with its own VAT treatment. Lower the quantity or amount of each line to what is being credited — leave a line as it is only to credit all of it.',
  // Third round (owner 2026-10-20): what is LEFT to credit, and a receipt that only settles a tax
  // invoice pointed at that tax invoice. ⛔ They state facts and the next step — no rule is quoted.
  p_note_prefilled_left: 'Filled in with what is left to credit on {{docNo}}: {{left}} of {{total}} — {{credited}} has already been credited ({{nos}}). Each line keeps its own VAT treatment; lower a line to credit less of it.',
  p_note_nothing_left: 'Nothing is left to credit on {{docNo}}: all of its {{total}} has already been credited ({{nos}}).',
  // The server's refusal of a credit note that takes off more than is left (createNoteRoute's `code` +
  // `params`), in the operator's own language — operator lens, third round. Same figures, same order.
  p_note_cap_line: 'The line "{{line}}" of {{docNo}} has {{left}} left to credit ({{charged}} charged, {{credited}} already credited); this note would take {{takes}} off it.',
  p_note_cap_doc: '{{docNo}} has {{left}} left to credit ({{total}} issued, {{credited}} already credited); this credit note would take {{takes}} off it.',
  p_note_cap_doc_by: '{{docNo}} has {{left}} left to credit ({{total}} issued, {{credited}} already credited by {{nos}}); this credit note would take {{takes}} off it.',
  p_note_unlinked: 'Earlier credit notes on {{docNo}} ({{nos}}) were entered without saying which of its lines they reduced, so nothing is filled in. {{left}} of {{total}} is left to credit — enter the lines being credited.',
  p_note_rcpt_settles: '{{rcptNo}} is the receipt for tax invoice {{invNo}} and states no VAT of its own — a credit or debit note adjusts the tax invoice.',
  p_note_rcpt_use: 'Adjust {{invNo}} instead',
  p_note_opt_credit: 'Credit note — reduce the value (ใบลดหนี้)',
  p_note_opt_debit: 'Debit note — increase the value (ใบเพิ่มหนี้)',
  p_note_reason_discount: 'Discount granted after the tax invoice (ลดราคาภายหลัง)',
  p_note_reason_extra_goods: 'Additional goods or services delivered (ส่งมอบเพิ่มเติม)',
  p_note_reason_goods_defective: 'Goods defective / not as ordered (สินค้าชำรุดบกพร่อง)',
  p_note_reason_goods_returned: 'Goods returned (สินค้าถูกส่งคืน)',
  p_note_reason_label: 'Reason * (printed on the note)',
  p_note_reason_other: 'Other (อื่น ๆ)',
  p_note_reason_other_ph: 'Describe the reason',
  p_note_reason_overcharge: 'Value overstated on the original (คำนวณราคาสูงกว่าที่เป็นจริง)',
  p_note_reason_service_not_provided: 'Service partly or wholly not provided (บอกเลิกการให้บริการ)',
  p_note_reason_undercharge: 'Value understated on the original (คำนวณราคาต่ำกว่าที่เป็นจริง)',
  p_note_ref: 'Tax invoice being adjusted *',
  p_note_title_new: 'New {{noteKind}}',
  p_note_total_difference: 'Difference',
  // Third round (owner 2026-10-20, Q4): the withholding a note PRINTS was carried from the original
  // line and printed without the window ever showing it. Shown and editable now; nothing is decided
  // about whether a note should carry one — the default is what it always was.
  p_note_wht_title: 'The withholding tax rate this note prints for the line. A line filled from the original document keeps that line\'s rate; change it here if the note should print another.',
  p_note_wht_printed: 'Printed on this note: WHT Amount {{wht}} · Payment Amount {{net}} — from each line\'s withholding rate above.',
  // Fourth round (2026-10-20, decided for the owner): a debit note raises what may be credited; output
  // VAT is capped as well as the total; a filled line keeps its VAT category; an edit from a page that
  // does not name its lines is refused on a document with a mark-up line; a foreign tax invoice with no
  // rate is refused before a number is taken; a debit note credit notes draw on cannot be voided.
  // The server's coded refusals (shared.ts refusalText) — the en text is the server's own sentence.
  p_note_cap_doc_dn: '{{docNo}} has {{left}} left to credit ({{issued}} issued + {{debited}} added by {{dnNos}}, {{credited}} already credited); this credit note would take {{takes}} off it.',
  p_note_cap_doc_dn_by: '{{docNo}} has {{left}} left to credit ({{issued}} issued + {{debited}} added by {{dnNos}}, {{credited}} already credited by {{nos}}); this credit note would take {{takes}} off it.',
  p_note_vatcap_line: 'The line "{{line}}" of {{docNo}} has {{left}} of VAT left to credit ({{charged}} charged, {{credited}} already credited); this note would take {{takes}} of VAT off it.',
  p_note_vatcap_doc: '{{docNo}} has {{left}} of output VAT left to credit ({{total}} charged, {{credited}} already credited); this credit note would take {{takes}} of VAT off it.',
  p_note_cat_locked: 'Line {{n}} credits "{{line}}" of {{docNo}}, which was charged as {{category}}. A credit note takes a line back with the VAT category it was charged with — keep {{category}} on this line.',
  p_note_cat_locked_title: 'A line filled from the document being credited keeps the VAT category it was charged with — a credit note takes it back as it was charged.',
  p_note_debited: '{{debited}} of what may be credited on {{docNo}} was added by debit note {{dnNos}}; its lines are filled in after the document\'s own.',
  p_note_vatcap_doc_dn: '{{docNo}} has {{left}} of output VAT left to credit ({{issued}} charged + {{debited}} added by {{dnNos}}, {{credited}} already credited); this credit note would take {{takes}} of VAT off it.',
  p_note_debited_in: '{{total}} on {{docNo}} includes {{debited}} added by debit note {{dnNos}}.',
  p_cdoc_edit_stale: 'This page is out of date: {{docNo}} has a mark-up line, and this page does not say which saved line each of its lines came from, so saving it would lose the mark-up line\'s mark. Nothing was saved — reload the page and make the change again.',
  p_cdoc_fx_rate_missing: 'This tax invoice is in {{currency}}: enter the conversion rate and its date before issuing it. Nothing was issued and no number was used.',
  // ── COA-D6 (owner 2026-10-28): a tax invoice issued FOR an ordinary invoice already paid — books nothing again ──
  p_cdoc_taxfor_label: 'For which paid invoice',
  p_cdoc_taxfor_optional: '(only when this sale was already billed on an ordinary invoice and paid)',
  p_cdoc_taxfor_none: '— None: a new sale (this tax invoice records the sale) —',
  p_cdoc_taxfor_none_avail: '— None: this customer has no paid invoice without a tax invoice —',
  p_cdoc_taxfor_pick_customer: '— Choose the customer first —',
  p_cdoc_taxfor_opt: '{{docNo}} · paid {{paid}} · {{receiptNos}}',
  p_cdoc_taxfor_hint: 'If this customer was already billed for this sale on an ordinary invoice and has paid it, choose that invoice: the tax invoice is then issued for it, records nothing in the books again and states “for INV-… / RE-…”.',
  p_cdoc_taxfor_note: 'This tax invoice is for {{docNo}} / {{receiptNos}}. The sale is already in the books through {{receiptNos}}, so this tax invoice records nothing again — no revenue, no output VAT, no receivable — and it is for exactly what was paid: {{paid}} with VAT {{paidVat}}.',
  p_cdoc_taxfor_edit_note: 'This tax invoice is for {{docNo}} / {{receiptNos}} and records nothing in the books — its figures stay exactly what was paid: {{paid}} with VAT {{paidVat}}.',
  p_cdoc_taxfor_fill: 'Use the lines of {{docNo}}',
  p_taxfor_err_type: 'Only a tax invoice is issued for an invoice that was already paid — choose “Tax invoice” as the document type.',
  p_taxfor_err_not_vat: 'Only a VAT-registered company issuing Thai tax documents issues tax invoices — there is no tax invoice to issue for a paid invoice.',
  p_taxfor_err_not_found: 'The paid invoice this tax invoice is for was not found among this company’s documents.',
  p_taxfor_err_void: '{{docNo}} is void — there is no paid sale behind it for a tax invoice to be issued for.',
  p_taxfor_err_not_invoice: '{{docNo}} is not an ordinary invoice — a tax invoice that records nothing again is issued only for an ordinary invoice whose receipts recorded the sale.',
  p_taxfor_err_plain: '{{docNo}} is a plain document, not a Thai invoice — its receipts recorded no Thai sale for a tax invoice to stand on.',
  p_taxfor_err_customer: '{{docNo}} was issued to {{customer}} — a tax invoice for it is issued to the same customer.',
  p_taxfor_err_unpaid_part: '{{docNo}} is not paid in full — {{received}} of {{total}} has been received ({{receiptNos}}), so only part of its sale is in the books. Record the rest of the payment on {{docNo}} first; then issue its tax invoice.',
  p_taxfor_err_unpaid_none: '{{docNo}} has not been paid — no receipt has recorded its sale, so there is nothing in the books for this tax invoice to stand on.',
  p_taxfor_err_adjusted: '{{docNo}} or its receipt has been adjusted by {{nos}} — what the books recorded for the sale is no longer what was paid, so a tax invoice cannot stand on it.',
  p_taxfor_err_taken: 'Tax invoice {{tivNo}} has already been issued for {{docNo}} — one paid invoice has one such tax invoice. Void {{tivNo}} first to issue another.',
  p_taxfor_err_currency: '{{docNo}} was paid in {{currency}} — its tax invoice is made out in {{currency}} too.',
  p_taxfor_err_amount: '{{docNo}} was paid {{paid}} with VAT {{paidVat}} ({{receiptNos}}); this tax invoice is {{total}} with VAT {{vat}}. A tax invoice for a paid invoice records nothing again, so it is for exactly what was paid — make its lines come to {{paid}} with VAT {{paidVat}}.',
  p_taxfor_err_not_chain: 'The invoice chosen was not raised from quotation {{quoteNo}} — a conversion is for a paid invoice of its own quotation.',
  p_taxfor_err_choose: 'Invoices {{docNos}} raised from quotation {{quoteNo}} were each paid in full — choose which one this tax invoice is for.',
  p_taxfor_err_before: 'This tax invoice is dated {{date}}, before {{earliest}} — {{docNo}} is dated {{invoiceDate}} and its payment arrived on {{paidDate}} ({{receiptNos}}). A tax invoice for a paid invoice is dated on or after both: date it {{earliest}} or later.',
  p_taxfor_err_closed: 'The date {{date}} falls in a closed accounting period ({{from}} – {{to}}) — a tax invoice for {{docNo}} cannot be dated in it. It records nothing in the books, but no tax invoice is issued into a closed period.',
  p_taxfor_err_filed: 'VAT for {{ym}} is already marked FILED in the Filing Center — a tax invoice for {{docNo}} dated {{date}} would be a new tax invoice in a month whose return has already been submitted. It records nothing in the books, but no tax invoice is issued into a filed month: unmark {{ym}} there first, then issue it.',
  p_crec_err_tax_for: 'Tax invoice {{invoiceNo}} was issued for invoice {{forNo}}, already paid by {{receiptNos}} — nothing is outstanding on it, and a receipt here would record the same payment a second time.',
  p_note_err_debit_rebilled: '{{docNo}} was credited in full, and {{nos}} now bills {{quoteNo}}. {{docNo}} is no longer the sale, so a debit note on it would stand beside {{nos}}. Issue the debit note on {{nos}} instead.',
  p_note_err_debit_rebilled_invoice: '{{docNo}} was credited in full, and {{nos}} now bills {{quoteNo}}. {{docNo}} is no longer the sale, so a debit note on it would stand beside {{nos}}. Change {{nos}} instead: edit it, or once it is paid, issue the debit note on its receipt.',
  p_note_err_tax_for: 'Tax invoice {{docNo}} was issued for invoice {{forNo}}, already paid by {{receiptNos}}, and records nothing in the books — the sale is in the books through {{receiptNos}}. A credit or debit note on this sale is issued on that receipt, once {{docNo}} is void: void {{docNo}} first.',
  p_doc_void_tax_for_first: '{{docNo}} paid invoice {{invoiceNo}}, for which tax invoice {{tivNo}} was issued — {{tivNo}} records nothing in the books because this payment did. Void {{tivNo}} first.',
  p_note_err_tax_for_first_receipt: '{{docNo}} paid invoice {{invoiceNo}}, for which tax invoice {{tivNo}} was issued — {{tivNo}} stands on this sale and states its VAT, so no credit or debit note changes the sale while {{tivNo}} stands. Void {{tivNo}} first; then issue the note on {{docNo}}.',
  p_note_err_tax_for_first_invoice: 'Invoice {{docNo}} was paid by {{receiptNos}}, and tax invoice {{tivNo}} was issued for it — no credit or debit note changes this sale while {{tivNo}} stands. Void {{tivNo}} first; then issue the note on its receipt ({{receiptNos}}) — an ordinary invoice is not adjusted by a note itself.',
  p_conv_taxfor_for: 'This tax invoice will be for {{docNo}} / {{receiptNos}}: {{docNo}}, raised from this quotation, was already paid ({{paid}}, VAT {{paidVat}}) and its sale is in the books through {{receiptNos}}. So the tax invoice records nothing again, and states that it is for {{docNo}} / {{receiptNos}}.',
  p_conv_taxfor_choose: 'More than one invoice raised from this quotation was paid in full. Which one is this tax invoice for? It records nothing again and states that invoice and its receipt.',
  p_conv_taxfor_choose_ph: '— Choose the paid invoice —',
  p_doc_void_dn_credited: 'Credit notes against {{docNo}} ({{nos}}) already take back part of what {{dnNo}} added — voided, more would stand credited than was charged. Void those credit notes first.',
  p_doc_void_credit_rebilled: '{{noteNo}} reversed {{docNo}}, and {{nos}} now bills {{quoteNo}}. Voided, it would bring {{docNo}} back beside {{nos}} and book the same sale twice. Void {{nos}} first.',
  p_doc_void_credit_rejected: '{{noteNo}} reversed {{docNo}}, and {{quoteNo}} is marked Rejected. Voided, it would leave a live bill on a rejected quotation. Change its status first.',
  // Round 4.1 (decision 1): a credit note on a receipt stands only against a bill paid in full.
  p_doc_void_receipt_credited: 'Voiding {{docNo}} would leave {{docWordLc}} {{invoiceNo}} owing {{outstanding}} while credit note {{nos}} stands against its receipt {{receiptNos}} — void the credit note first.',
  p_note_err_receipt_bill_owes: '{{docWord}} {{invoiceNo}} still has {{outstanding}} outstanding, so no credit note can be issued against its receipt {{receiptNo}} — a credit note on a receipt is accepted only once the {{docWordLc}} it settles is paid in full. To change the price of {{invoiceNo}} before it is paid in full, void its receipt(s) and edit {{invoiceNo}}.',
  p_note_err_receipt_debit_owes: 'Debit note {{dnNo}}, issued against receipt {{receiptNo}}, still has {{outstanding}} outstanding, so no credit note can be issued against {{receiptNo}} — a credit note on a receipt is accepted only once the debit notes issued against it are paid in full.',
  // Mark-up round 5 (2026-10-27): a credit note dated before a document it draws on (item 1); typed lines over a NON-VAT
  // return box (item 2), named by the box (p_note_box_*).
  p_note_err_before_source: 'This credit note is dated {{noteDate}}, before {{docWordLc}} {{docNo}} (dated {{date}}), which it credits — a credit note cannot be dated before a document it draws on. Date it {{date}} or later.',
  p_note_err_cat_left: '{{docNo}} has {{left}} left to credit as {{category}}; the lines typed on this credit note take {{takes}} as {{category}}. A credit note takes value back in the VAT category it was charged in, so no more can be credited as {{category}}.',
  p_note_err_cat_left_dn: '{{docNo}} and its debit notes ({{dnNos}}) have {{left}} left to credit as {{category}}; the lines typed on this credit note take {{takes}} as {{category}}. A credit note takes value back in the VAT category it was charged in, so no more can be credited as {{category}}.',
  p_note_box_zero: 'VAT 0% (zero-rated)', p_note_box_exempt: 'VAT exempt', p_note_box_outscope: 'out of scope (a disbursement at cost)', p_note_box_none: 'no VAT',
  p_note_err_none_flat_vat: 'This credit note takes back {{amount}} of no-VAT value that {{docNos}} booked in the 7% column at no VAT, together with lines that carry VAT — one credit note cannot reverse both exactly. Credit the 7% lines on a credit note of their own, and everything else on another (it then carries no VAT).',
  // Mark-up round 5, review fixes (2026-10-27): a filled line sent after value was taken off without naming lines; a filled
  // line's charge choice (its return box) kept; the two no-VAT boxes told apart by the charge choice; a debit note credit
  // notes take value from in other ways than its lines.
  p_note_err_fill_unlinked: 'Value was taken off {{docNo}} by credit notes that do not name its lines ({{nos}}), so what is left of each of its lines is not known and a line can no longer be credited by pointing at it. Remove the lines filled in from {{docNo}} (×) and add the lines of this credit note again by hand.',
  p_note_err_box_locked: 'Line {{n}} credits "{{line}}" of {{docNo}}, which was charged as {{box}}. A credit note takes a line back the way it was charged, so its charge choice stays as it was filled — value is never moved between the boxes of the VAT return.',
  p_note_charge_locked_title: 'A line filled from the document being credited keeps the charge choice it was charged with — a credit note takes it back as it was charged.',
  p_note_err_cat_left_out: '{{docs}}: {{left}} left to credit as no VAT; the lines typed on this credit note take {{takes}} as no VAT. Another {{sibLeft}} of no-VAT value still to credit there was charged as money paid out for the customer at cost (out of scope) — for that part, choose the charge "{{charge}}" on a typed line.',
  p_note_err_cat_left_own: '{{docs}}: {{left}} left to credit as out of scope (a disbursement at cost); the lines typed on this credit note take {{takes}} as out of scope (a disbursement at cost). Another {{sibLeft}} of no-VAT value still to credit there was not charged as money paid out for the customer — for that part, choose the charge "{{charge}}" on a typed line.',
  p_doc_void_dn_drawn: 'Credit notes against {{docNo}} ({{nos}}) take back value that {{dnNo}} charged as {{box}} — voided, more would stand credited as {{box}} than was charged. Void those credit notes first.',
  p_doc_void_dn_drawn_later: 'Credit notes against {{docNo}} ({{nos}}) take part of their value from {{dnNo}} — voided, that part would pass to debit note {{nextNo}}, dated {{date}}, after credit note {{cnNo}} (dated {{cnDate}}), and a credit note cannot draw on a document dated after it. Void those credit notes first.',
  p_doc_void_receipt_debit_credited: 'Voiding {{docNo}} would leave debit note {{invoiceNo}} owing {{outstanding}} while credit note {{nos}} stands against receipt {{receiptNos}}, which that debit note adjusts — void the credit note first.',
  // (Round 4.1 re-verification) A void that must wait for a note which cannot itself be voided now names that note and
  // its own refusal ({{reason}}, already in the reader's language), never "void the note first" for nothing.
  p_doc_void_note_held: '{{docNo}} cannot be voided until {{noteWordLc}} {{noteNo}} is voided, and {{noteNo}} cannot be voided now: {{reason}}',
  p_note_type: 'Type *',
  // pager
  p_pager_first: 'First page',
  p_pager_last: 'Last page',
  p_pager_next: 'Next',
  p_pager_no_records: 'No records',
  p_pager_page_of: 'Page {{page}} of {{pages}}',
  p_pager_per_page: 'Per page',
  p_pager_prev: 'Previous',
  p_pager_showing: 'Showing {{from}}–{{to}} of {{total}}',
  // pdf
  p_pdf_failed: 'PDF failed',
  // prod
  p_prod_add: '+ Add item',
  p_prod_confirm_remove: 'Remove {{name}}?',
  p_prod_empty: 'No items yet — add what you sell.',
  p_prod_f_name: 'Name *',
  p_prod_f_price: 'Unit price ({{cur}})',
  p_prod_f_unit: 'Unit (e.g. pcs, hr)',
  p_prod_f_vat_checkbox: 'Subject to 7% VAT',
  p_prod_f_wht: 'Default withholding tax (WHT)',
  p_prod_f_wht_hint: 'Goods normally 0%; services 3%; rent 5%. The buyer deducts it and gives you a 50 ทวิ certificate.',
  p_prod_modal_edit: 'Edit item',
  p_prod_modal_new: 'New item',
  p_prod_photo_hint: 'shown in your catalog list',
  p_prod_photo_label: 'Photo (optional)',
  p_prod_search_ph: 'Search name or unit…',
  p_prod_th_name: 'Name',
  p_prod_th_price: 'Price ({{cur}})',
  p_prod_th_unit: 'Unit',
  p_prod_th_vat: 'VAT',
  p_prod_title: 'My Products & Services',
  p_prod_vat_yes: '{{rate}}%',
  // prof
  p_prof_address: 'Address on documents',
  p_prof_biz_goods: 'Goods — VAT is due on delivery, so the tax invoice goes out with the goods (s.78)',
  p_prof_biz_hint: 'This only sets the default document type when you create one — you can change it on each document.',
  p_prof_biz_mixed: 'Both goods and services',
  p_prof_biz_service: 'Services — VAT is due when the customer pays (s.78/1)',
  p_prof_biz_type: 'What you sell',
  p_prof_company_name: 'Company name (locked)',
  p_prof_default_wht: 'Default WHT on new lines',
  p_prof_default_wht_hint: 'Used when a line has no catalog item of its own.',
  p_prof_doc_cn: 'Credit note',
  p_prof_doc_dn: 'Debit note',
  p_prof_doc_inv: 'Invoice',
  p_prof_doc_pro: 'Proforma',
  p_prof_doc_quo: 'Quotation',
  p_prof_doc_rec: 'Receipt',
  p_prof_doc_tax: 'Tax invoice',
  p_prof_email: 'Email',
  p_prof_failed: 'Failed',
  p_prof_identity_note: 'Identity fields are verified from your company documents and can only be changed by our team.',
  p_prof_logo_hint: 'printed top-left on invoices, quotations and receipts',
  p_prof_logo_label: 'Company logo',
  p_prof_month_seg: 'Include the year-month in document numbers',
  p_prof_month_seg_hint: '— on: INV-{{yyyymm}}-001 · off: INV-001. Turn this off if your own numbers already contain the period.',
  p_prof_numbering: 'Document numbering',
  p_prof_numbering_note: '— continuity is your responsibility (accepted {{date}})',
  p_prof_phone: 'Phone',
  p_prof_prefix_next: '{{docType}} prefix / next number',
  p_prof_pw_changed: 'Password changed ✓',
  p_prof_saved: 'Saved ✓',
  p_prof_branch: 'Branch (locked)',
  p_prof_branch_head: 'Head Office',
  p_prof_branch_hint: 'Printed next to your Tax ID on tax invoices and credit / debit notes.',
  p_prof_branch_n: 'Branch',
  p_prof_tax_id: 'Tax ID (locked)',
  p_prof_title: 'Company Profile',
  p_prof_vat_hint: 'Registered ⇒ your invoices add 7% VAT and your receipt is a tax invoice.',
  p_prof_vat_not_registered: 'Not registered',
  p_prof_vat_reg: 'VAT registration',
  p_prof_vat_registered: 'Registered (7% VAT)',
  p_prof_website: 'Website',
  // pw
  p_pw_confirm: 'Confirm new password',
  p_pw_current: 'Current password',
  p_pw_err_current_required: 'Enter your current password.',
  p_pw_err_failed: 'Failed',
  p_pw_err_min_length: 'At least 8 characters.',
  p_pw_err_mismatch: 'Passwords do not match.',
  p_pw_hint_current: 'Your current password is required.',
  p_pw_hint_first: 'For security, please replace the temporary password.',
  p_pw_new: 'New password',
  p_pw_title_change: 'Change password',
  p_pw_title_first: 'Set your own password',
  // row
  p_row_delete: 'Delete',
  p_row_edit: 'Edit',
  // search
  p_search_clear: 'Clear',
  // select
  p_select_placeholder: '— select —',
  p_pick_search: 'Type to search…',
  p_pick_none: 'No match — try fewer words.',
  p_pick_more: 'Showing {{shown}} of {{total}} — type to search the rest.',
  p_cust_add_title: 'Add customer',
  p_cust_add_save: 'Add & select',
  p_cust_name_req: 'Customer name is required.',
  p_cust_add_failed: 'Could not add the customer.',
  p_prof_f_prefix: 'Prefix',
  p_prof_f_next: 'Next number',
  p_prof_f_date: 'Date in number',
  p_prof_f_reset: 'Restart',
  p_prof_f_pad: 'Digits',
  p_prof_date_none: 'No date',
  p_prof_reset_none: 'Never',
  p_prof_reset_year: 'Every year',
  p_prof_reset_month: 'Every month',
  p_prof_reset_day: 'Every day',
  p_prof_scheme_bad: 'Add {{need}} to the number, or the same number comes round again next period.',
  p_prof_numbering_hint: 'The number is prefix · date · running number. Restarting the run needs a date fine enough to tell one period from the next — yearly needs YYYY, monthly YYYYMM, daily YYYYMMDD. Changing any of this affects only documents issued from now on; everything already issued keeps its number.',
  // The company's own EXP / PMT numbering (owner 2026-10-28, EXP-8): the two rows under the document series.
  p_regno_head: 'Expense records and supplier payments',
  p_regno_exp: 'Expense record',
  p_regno_pmt: 'Supplier payment',
  p_regno_po: 'Purchase order',
  p_regno_claim: 'Reimbursement claim',
  p_regno_f_sep: 'Separator',
  p_regno_sep_dash: 'With “-”',
  p_regno_sep_none: 'None',
  p_regno_hint: 'Each expense record and each supplier payment is numbered when it is created; here you choose what those numbers look like. A number already issued never changes. The next number can only go up. A changed format starts from the next number shown for it — 1 unless you choose a higher one; a format used before carries on after the numbers it already issued — and a number already used, even by a voided or deleted record, is never issued again. A prefix numbers already issued begin with stays theirs. A next number you choose holds until the run next restarts.',
  p_regno_err_prefix_empty: 'The prefix must be 1–8 letters or digits.',
  p_regno_err_prefix_digit_end: 'Without separators the prefix must end with a letter — “{{prefix}}” would run straight into the date or the number.',
  p_regno_err_prefix_taken: '{{prefix}} is already the prefix of {{other}} — each series needs its own, so a number always says what it is.',
  p_regno_err_prefix_held: '{{other}} numbers already issued begin with {{prefix}} — each series needs its own prefix, so a number always says what it is.',
  p_regno_err_scheme_bad: 'This format is not valid.',
  p_regno_err_start_bad: 'The next number must be a whole number from 1 to {{max}}.',
  p_regno_err_start_down: 'The next number is already {{next}} — it can only go up.',
  p_regno_checking: 'Checking…',
  p_regno_failed: 'Could not check this format — try again.',
  p_flt_all: 'All',
  p_flt_custom: 'Custom',
  p_flt_clear: 'Clear the date filter',
  p_flt_period: 'Period',
  p_flt_show_voided: 'Show voided',
  p_imp_open_short: 'Import',
  p_menu_notes_hint: 'Correct a document already issued',
  p_flt_this_month: 'This month',
  p_flt_last_month: 'Last month',
  p_flt_from: 'From date',
  p_flt_to: 'To date',
  // send
  p_send_attachment: 'Attachment',
  p_send_bcc: 'Copy to you',
  p_send_btn: 'Send email',
  p_send_done: 'Done',
  p_send_err_prepare: 'Could not prepare the email.',
  p_send_err_send: 'The email could not be sent.',
  p_send_from: 'From',
  p_send_history: 'Previously sent',
  p_send_message: 'Message',
  p_send_no_replyto: '— add your company email under Profile first —',
  p_send_preparing: 'Preparing…',
  p_send_preview: 'Preview',
  p_send_preview_hint: 'Opens the exact PDF your customer will receive — both sheets, Original and Copy.',
  p_send_replyto: 'Reply to',
  p_send_save_addr: 'Save this address to {{customerName}} so it fills in next time',
  p_send_sending: 'Sending…',
  p_send_sent_ok: '✓ Sent to {{email}}. A copy is in your own inbox.',
  p_send_subject: 'Subject',
  p_send_subtitle: 'The PDF is attached automatically. Your customer’s reply comes straight back to you.',
  p_send_title: 'Send {{docNo}}',
  p_send_to_label: 'Customer email *',
  p_send_to_ph: 'name@example.com',
  // sort
  p_sort_th_tooltip: 'Sort by {{label}}',
  // vat
  p_vat_hint_exempt: 'Exempt supply (s.81).',
  p_vat_hint_none: 'Out of scope / not a VAT registrant.',
  p_vat_hint_standard: 'VAT is added on top of the price you enter.',
  p_vat_hint_standard_incl: 'The price you enter already includes VAT.',
  p_vat_hint_zero: 'Zero-rated (s.80/1) — taxable at 0%, not the same as exempt.',
  p_vat_opt_exempt: 'VAT Exempt',
  p_vat_opt_none: 'No VAT',
  p_vat_opt_standard: 'VAT {{rate}}% — Exclusive',
  p_vat_opt_standard_incl: 'VAT {{rate}}% — Inclusive',
  p_vat_opt_zero: 'VAT 0%',
  // wht
  p_wht_none: 'No WHT',
  p_wht_rate: 'WHT {{rate}}%',
  p_wht_select_title: 'Withholding tax the buyer deducts',
};

export const portalZh: Record<string, string> = {
  // admin
  p_admin_title: '{{brand}} 客户门户 — 管理端',
  // bank
  p_bank_acct_name: '账户名称',
  p_bank_acct_no: '账号',
  p_bank_add: '+ 添加银行账户',
  p_bank_address: '银行地址',
  p_bank_address_hint: '— 境外汇款需要',
  p_bank_address_ph: '1 Bangkok Blvd, Pathum Wan, Bangkok 10330, Thailand',
  p_bank_confirm_remove: '确定删除该银行账户?',
  p_bank_currency: '币种',
  p_bank_empty: '尚未添加银行账户 — 添加后会打印在单据上。',
  p_bank_name: '银行及分行',
  p_bank_name_ph: '开泰银行(KBank)One Bangkok 支行',
  p_bank_no_swift: '⚠ 未填 SWIFT / 银行地址 — 境外付款方无法电汇到此账户。',
  p_bank_note: '备注(选填)',
  p_bank_note_ph: '例如:仅用于 USD 发票',
  p_bank_qr_alt: 'QR',
  p_bank_qr_hint: 'PromptPay / 银行二维码 — 打印在发票和报价单的付款栏内',
  p_bank_qr_label: '收款二维码',
  p_bank_qr_title: '打印在单据上的收款二维码',
  p_bank_swift: 'SWIFT / BIC',
  p_bank_swift_ph: 'KASITHBK',
  p_bank_title: '单据上的银行账户',
  // bills
  p_bills_loading: '加载中…',
  p_bills_open_pdf: '打开 PDF',
  p_bills_subtitle: '{{brand}} 向您开具的报价单、发票和收据。',
  p_bills_tab_invoices: '发票',
  p_bills_tab_quotations: '报价单',
  p_bills_tab_receipts: '收据',
  p_bills_th_date: '日期',
  p_bills_th_due: '到期',
  p_bills_th_issued_by: '开具方',
  p_bills_th_net: '应付净额',
  p_bills_th_no: '编号',
  p_bills_th_paid_on: '付款日期',
  p_bills_th_status: '状态',
  p_bills_th_total: '合计({{cur}})',
  p_bills_th_valid_until: '有效期至',
  p_bills_title: '{{brand}} 账单',
  p_menu_group_settings: '设置',
  p_menu_files: '文件', p_menu_files_hint: '我们为您保管的全部文件,集中在一处',
  p_files_title: '您的文件',
  p_files_subtitle: '{{brand}} 为您保管的全部文件 —— 申报表、税务局回执、公司证照,以及您发给我们的单据。',
  p_files_loading: '正在加载…', p_files_none: '这里暂无文件。', p_files_all: '全部',
  p_files_search: '搜索文件…',
  p_files_name: '文件', p_files_folder: '类别', p_files_size: '大小', p_files_added: '加入时间',
  p_files_view: '查看', p_files_download: '下载',
  p_files_f_tax: '税务与申报', p_files_f_company: '公司证照', p_files_f_uploads: '您发给我们的',
  p_files_f_deliverables: '交付给您的', p_files_f_workflow: '服务过程文件', p_files_f_library: '其他',
  p_files_elsewhere: '{{brand}} 开给您的发票和收据在「{{brand}} 账单」里;申报表在「税务申报」里也能找到。',
  p_nav_finance: '损益',
  p_fin_title: '{{year}} 年损益表',
  p_fin_subtitle: '取自账上真实记录的数据 —— 不含预估,也不向后推算。',
  p_fin_loading: '正在加载…',
  p_fin_no_data: '本年度暂无记录。',
  p_fin_revenue: '收入',
  p_fin_expenses: '支出',
  p_fin_purchases: '采购',
  p_fin_salaries: '工资',
  p_fin_sso: '社保(公司部分)',
  p_fin_pnd3: '支付给个人的费用',
  p_fin_rent53: '租金',
  p_fin_expenses_total: '支出合计',
  p_fin_net: '净额',
  p_fin_by_month: '按月明细',
  p_fin_month: '月份',
  p_fin_no_payroll: '工资未生成',
  p_fin_incomplete: '{{months}} 月的工资尚未生成,因此这些月份的工资和社保显示为 0。这几个月的数字不是最终数。',
  p_fin_note: '收入和采购均为不含税金额。社保只显示公司承担的部分 —— 个人部分从工资中代扣,不是公司的额外成本。支付给公司的费用已包含在采购里,所以只单列支付给个人的费用。',
  p_fin_unposted: '{{months}} 月有已录入的单据尚未过入账簿,因此这几个月的数字不是最终数。',
  p_fin_not_posted: '尚未过账',
  p_fin_note_books: '取自已过入账簿的分录,按分录日期归月 —— 与「财务报表」中的损益表取自同一批分录。',
  p_fin_outside: '{{months}} 月有已录入的单据没有日期,或日期不在您的任何一个会计期间内,因此没有计入这些数字。',
  p_fin_outside_short: '不在会计期间内',
  p_fin_no_voucher: '{{months}} 月的 PND.1、社保、PND.3 或付给公司房东的租金(PND.53)记录里有工资、费用或租金,但账簿中没有日期在这几个月、把这些金额记为费用的分录。如果这些金额记在了其他月份,就包含在那个月的数字里;否则没有计入这些数字。',
  p_fin_no_voucher_short: '本月没有工资、费用或租金的分录',
  p_fin_rental_no_voucher: '您有一份与公司房东的有效租约,但 {{months}} 月没有该租约的租金(PND.53)记录,账簿中也没有日期在这几个月、把租金记为费用的分录。这几个月的租金如果记在了其他月份,就包含在那个月的数字里;否则没有计入这些数字。',
  p_fin_rental_no_voucher_short: '租约有效:本月没有租金记录或分录',
  p_fin_closed_unposted: '{{months}} 月有已录入的单据不在该会计年度已结账的账簿中,因此没有计入这些数字。',
  p_fin_closed_unposted_short: '已录入,但不在已结账的账簿中',
  p_nav_calendar: '日程',
  p_cal_title: '接下来要办的事',
  p_cal_subtitle: '您的申报期限,以及双方各自要做的事。标着「您」的都在等您。',
  p_cal_loading: '正在加载…', p_cal_nothing: '这里暂无安排。',
  p_cal_all_year: '全年', p_cal_today: '今天', p_cal_you: '您',
  p_cal_only_mine: '只看我的({{count}})', p_cal_yours_count: '{{count}} 件在等您',
  p_cal_is_filing: '申报期限', p_cal_done: '已完成', p_cal_blocked: '受阻',
  p_cal_k_pp30: '增值税 (PP.30)', p_cal_k_pnd1: 'PND.1', p_cal_k_pnd3: 'PND.3', p_cal_k_pnd53: 'PND.53',
  p_cal_k_sso: '社会保险', p_cal_k_pnd51: '半年度企业所得税 (PND.51)', p_cal_k_pnd50: '年度企业所得税 (PND.50)',
  p_cal_k_pnd90: '个人所得税', p_cal_k_audit: '审计', p_cal_k_annual_fs: '年度财务报表',
  p_cal_k_bookkeeping: '记账', p_cal_k_property_tax: '房产税',
  p_cal_k_invoice_data_upload: '把发票发给我们', p_cal_k_tax_payment: '缴纳税款',
  p_cal_k_service_payment: '支付我们的服务费', p_cal_k_service_invoice: '我们开给您的账单',
  p_cal_k_tax_remit: '我们代您缴税', p_cal_k_lead_quotation: '报价',
  p_cal_k_lead_contract: '合同', p_cal_k_lead_collect_info: '把资料发给我们',
  p_doc_got_bill: '收到供应商开来的发票?',
  p_doc_got_receipt: '有您付款后取得的收据?',
  p_doc_send_to_slf: '发给我们识别并入账 →',
  p_nav_hr: '员工',
  p_hr_title: '您的员工',
  p_hr_subtitle: '您的员工名册,以及各自的社保金额。需要变更请提交申请,由我们代为执行。',
  p_hr_loading: '正在加载…',
  p_hr_new_request: '提交变更申请',
  p_hr_pending_note: '有 {{count}} 条申请正在等待 {{brand}} 审核。',
  p_hr_name: '姓名', p_hr_position: '职位', p_hr_salary: '月薪',
  p_hr_joined: '入职', p_hr_status: '状态', p_hr_nationality: '国籍',
  p_hr_no_staff: '暂无员工记录。',
  p_hr_active: '在职', p_hr_left: '已离职', p_hr_not_in_sso: '不在社保',
  p_hr_joined_this_month: '本月入职',
  p_hr_left_this_month: '本月离职',
  p_hr_change_salary: '调薪', p_hr_leaving: '离职',
  p_hr_sso_title: '本月社保',
  p_hr_sso_how: '劳资双方各按月薪的 {{rate}}% 缴纳。月薪低于 {{floor}} 按 {{floor}} 计,每方上限 {{cap}} —— 超过该薪资后缴费不再增加。个人部分从工资中代扣,公司再缴同等金额。只要当月在职过哪怕一天,就按整月计算 —— 入职当月和离职当月都算。',
  p_hr_check_these: '本月需要留意:',
  p_hr_mover_note: '当月入职或离职的人,最容易需要人工确认。如果金额应当不同,告诉我们,我们手工设定。',
  p_hr_employee_side: '个人', p_hr_employer_side: '公司', p_hr_total: '合计',
  p_hr_month_total: '合计 · {{count}} 人', p_hr_manual: '手工设定',
  p_hr_no_sso: '本月没有需要缴纳社保的员工。',
  p_hr_snapshot: '本月已固定 —— 这是当时申报的数据。',
  p_hr_preview: '本月尚未申报,按今天的员工名册试算,仍可能变化。',
  p_hr_year_title: '{{year}} 年全年社保',
  p_hr_month: '月份', p_hr_headcount: '人数', p_hr_wages: '工资总额',
  p_hr_filed_snap: '已固定', p_hr_preview_short: '试算',
  p_hr_requests: '您的申请',
  p_hr_k_salary_change: '调整工资', p_hr_k_employee_add: '新员工入职', p_hr_k_employee_leave: '员工离职',
  p_hr_s_pending: '等待 {{brand}} 处理', p_hr_s_applied: '已执行', p_hr_s_rejected: '未执行',
  p_hr_request_title: '提交变更申请',
  p_hr_request_note: '提交本身不会改动任何数据。{{brand}} 会先审核,因为您的社保和 PND.1 会随之变化。',
  p_hr_kind: '变更类型', p_hr_employee: '员工', p_hr_pick_employee: '请选择…',
  p_hr_start_date: '入职日期', p_hr_last_day: '最后工作日',
  p_hr_new_salary: '新月薪', p_hr_effective_from: '生效日期',
  p_hr_note: '需要我们知道的情况', p_hr_cancel: '取消', p_hr_send: '提交申请', p_hr_sending: '正在提交…',
  p_hr_sso_preview: '按此薪资,社保为个人 {{employee}}、公司同额,每月合计 {{total}}。',
  p_hr_err: '提交失败,请重试。',
  // ── PandaAI Tax(只读)──
  p_nav_tax: '税务',
  p_tax_title: '税务申报',
  p_tax_subtitle: '我们为您申报的每一项税:数字怎么算出来的、金额多少、谁付了什么、以及最终的申报文件。',
  p_tax_loading: '正在加载…',
  p_tax_pick: '请在左侧选择一个税种。',
  p_tax_not_configured: '{{brand}} 不办理此项',
  p_tax_nothing_filed: '尚无申报记录',
  p_tax_half_year: '半年申报一次',
  p_tax_annual: '一年申报一次',
  p_tax_calc: '计算过程',
  p_tax_no_calc: '本期没有计算明细。',
  p_tax_amount: '应缴金额',
  p_tax_you_paid_slf: '您付给 {{brand}}',
  p_tax_slf_paid_rd: '{{brand}} 付给税务局',
  p_tax_settled: '已全额结清',
  p_tax_outstanding: '尚欠',
  p_tax_nothing_billed: '本期未向您计费',
  p_tax_remitted: '已缴纳',
  p_tax_not_remitted: '尚未缴纳',
  p_tax_filed: '申报表已提交',
  p_tax_method: '支付方式',
  p_tax_method_na: '系统未记录',
  p_tax_docs: '申报文件',
  p_tax_no_docs: '本期暂无文件。',
  p_tax_l_out_base: '销售额(不含税)',
  p_tax_l_out_vat: '销项税',
  p_tax_l_in_base: '采购额(不含税)',
  p_tax_l_in_vat: '进项税',
  p_tax_l_net: '销项 − 进项',
  p_tax_l_brought: '上期结转留抵',
  p_tax_l_carry: '结转下期留抵',
  p_tax_l_surcharge: '滞纳金',
  p_tax_l_fines: '罚款',
  p_tax_l_headcount: '员工人数',
  p_tax_l_gross_wages: '工资总额',
  // ── SLF 服务介绍(只读)──
  p_menu_services: '服务介绍',
  p_menu_services_hint: '我们做什么、多少钱、流程怎么走',
  p_svc_title: '{{brand}} 提供的服务',
  p_svc_subtitle: '完整服务清单。已与您约定价格的显示您的价格。点开任一服务可看到办理流程。',
  p_svc_search: '搜索服务…',
  p_svc_loading: '正在加载…',
  p_svc_none: '没有匹配的服务。',
  p_svc_yours: '您已订',
  p_svc_your_price: '您的价格',
  p_svc_list_price: '目录价',
  p_svc_how: '这项服务怎么走:',
  p_svc_steps_loading: '正在加载流程…',
  p_svc_no_steps: '这项服务暂未录入流程。',
  p_svc_step_you: '您',
  p_svc_step_slf: '{{brand}}',
  // ── SLF 业务进度(只读)──
  p_nav_jobs: '业务进度',
  p_jobs_title: '{{brand}} 业务进度',
  p_jobs_subtitle: '我们正在为您办理的事项,以及各自进行到哪一步。仅供查看。',
  p_jobs_loading: '正在加载…',
  p_jobs_all: '全部',
  p_jobs_no: '编号',
  p_jobs_what: '事项',
  p_jobs_status: '状态',
  p_jobs_due_date: '预计 / 完成',
  p_jobs_amount: '金额',
  p_jobs_paid: '已付',
  p_jobs_outstanding: '未付',
  p_jobs_date_tbc: '待定',
  p_jobs_waiting_on: '等待',
  p_jobs_opened: '受理于',
  p_jobs_updated: '更新于',
  p_jobs_st_not_started: '尚未开始',
  p_jobs_st_in_progress: '进行中',
  p_jobs_st_with_you: '等您提供',
  p_jobs_st_with_authority: '等政府部门',
  p_jobs_st_with_third_party: '等第三方',
  p_jobs_st_done: '已完成',
  p_jobs_st_cancelled: '已取消',
  // bs — bank statements the client sends in (owner 2026-10-13)
  p_bs_title: '请上传您的银行流水',
  p_bs_subtitle: '您自己银行账户的对账单,直接来自银行。{{brand}} 会逐行与您的发票、收据人工核对 — 这里上传的内容本身不会改变您的账簿或报税。',
  p_bs_account: '银行账户',
  p_bs_account_auto: '从文件自动识别',
  p_bs_no_accounts: '您的资料中尚无银行账户 — 请先在公司资料中添加账户,流水才能被识别。',
  p_bs_pw_label: 'PDF 密码',
  p_bs_pw_ph: '仅当银行给文件加了密码时填写',
  p_bs_pw_hint: '仅用于打开此文件,不会保存',
  p_bs_upload: '＋ 上传流水',
  p_bs_uploading: '读取中…',
  p_bs_file_hint: '银行出具的 PDF 对账单或 CSV 导出文件 — 每次最多 {{max}} 个文件,每个不超过 {{mb}} MB。照片和 Excel 无法读取。',
  p_bs_too_many: '每次最多上传 {{max}} 个文件。',
  p_bs_upload_failed: '上传失败',
  p_bs_res_saved: '✓ {{filename}} — 账户 {{account}} · 读取 {{rows}} 行,新增 {{added}} 行,{{skipped}} 行已存在',
  p_bs_res_failed: '✗ {{filename}} — {{error}}',
  p_bs_err_account_not_registered: '该流水所属账户尚未登记在您的资料中 — 请先添加该账户',
  p_bs_err_statement_account_mismatch: '该流水所属账户与您选择的账户不一致',
  p_bs_list_title: '已收到的流水',
  p_bs_th_file: '文件',
  p_bs_th_account: '账户',
  p_bs_th_period: '期间',
  p_bs_th_rows: '行数',
  p_bs_rows_cell: '{{rows}} 行,新增 {{added}}',
  p_bs_closing: '期末余额 {{amount}}',
  p_bs_th_uploaded: '上传时间',
  p_bs_via_portal: '您上传',
  p_bs_via_workspace: '{{brand}} 上传',
  p_bs_act_view: '查看',
  p_bs_act_withdraw: '撤回',
  p_bs_confirm_withdraw: '撤回 {{filename}}?其中尚未核对的行将一并移除。',
  p_bs_withdraw_locked: '无法撤回该流水 — {{brand}} 已核对了其中部分行。',
  p_bs_empty: '尚无流水。',
  p_bs_err_open: '无法打开该文件',
  p_bs_file_fallback: '流水',
  // btn
  p_btn_cancel: '取消',
  p_btn_save: '保存',
  p_btn_save_bilingual: '保存',
  // dlg
  p_dlg_close: '关闭',
  p_dlg_discard: '放弃未保存的修改?',
  // cdoc
  p_cdoc_add_line: '+ 添加行',
  p_cdoc_bank_cash: '现金支付 —— 不显示银行信息',
  p_cdoc_bank_label: '打印在此 {{docKind}} 上的银行账户',
  p_cdoc_bank_none: '— 尚未添加(请在公司资料中添加)—', p_cdoc_bank_unset: '— 此单据未打印银行账户 —',
  p_cdoc_charge_hint_cust: '您替客户向第三方付款,并按原价转收、未加任何加价;供应商的收据或税务发票开给客户,不是开给您。',
  p_cdoc_charge_hint_own: '您收取的是本公司自己提供的服务或货物。这是通常的选项。',
  p_cdoc_charge_hint_self: '您替客户向第三方付款,但供应商的收据或税务发票开的是您本公司。',
  p_cdoc_charge_opt_cust: '替客户垫付 —— 供应商发票开给客户',
  p_cdoc_charge_opt_own: '我方自己的服务或货物',
  p_cdoc_charge_opt_self: '替客户垫付 —— 供应商发票开给我方',
  p_cdoc_charge_title: '本行是您自己的收费,还是替客户垫付后按原价转收的款项?',
  p_cdoc_charge_unset: '—— 未填写 ——',
  p_cdoc_charge_w503: '供应商的发票开的是您本公司,因此本行仍计为您自己的收费 —— 不作垫付转收处理。若供应商实际是直接开票给客户,请改选。本单据仍可开出。',
  p_cdoc_charge_w505: '本行同时还选择了自己的 VAT 处理方式,因此仍按您自己的收费记账 —— 不作垫付转收处理。只有未选择自己 VAT 处理方式的行,才会按您垫付后转收的款项处理。',
  p_cdoc_charge_w506: '您填写的供应商收费合计为 {{cost}},而本行向客户收取 {{charged}},多出 {{diff}}。(本行加收的 VAT 不计入该金额 —— 该税款由您代收后上缴,不属于您的收费。)替客户垫付的款项应按原价转收;加价部分属于您自己的收费。两个金额都会留在本行上并标记待复核。本单据仍可开出。',
  p_cdoc_choose_service: '— 选择服务 —',
  p_cdoc_correct_prompt: '为什么要更正 {{docNo}}?\n\n此说明会连同修改前的数据一并记录在该单据上。',
  p_cdoc_cost_ph: '供应商收取',
  p_cdoc_cost_title: '供应商就本行向您收取的合计金额,按其账单填写,含其账单上列示的 VAT。替客户垫付的款项应按原价转收,填写后即可与本行向客户收取的金额(不含本行加收的 VAT)核对。新开以泰铢计价的单据时,若本行未选自己的 VAT 处理方式且向客户收取的金额高于此数,多出部分会另存为一行您自己的收费并计 VAT —— 保存前会显示这两行。此项可不填 —— 留空不构成任何声明,不改变本单据任何金额,也不会阻止开票。',
  p_cdoc_split_title: '拆成两行保存',
  p_cdoc_split_why: '供应商收取 {{cost}},本行向客户收取 {{charged}}。多出的 {{diff}} 属于您自己的收费,单独成行保存并计 VAT;{{cost}} 仍是替客户垫付、按原价转收的款项。',
  p_cdoc_split_cost_line: '替客户垫付,按原价转收 —— 不计 VAT',
  p_cdoc_split_markup_line: '您自己的收费 —— {{vatLabel}} · VAT {{vat}}',
  p_cdoc_split_name: '新增一行的名称(印在单据上)',
  p_cdoc_currency: '币种',
  p_cdoc_remark_ph: '打印在单据上的备注 — 格式、换行和粘贴的图片都会保留。',
  p_cdoc_pay_method: '付款方式',
  p_cdoc_pay_method_none: '— 未注明 —',
  p_cdoc_pm_tt: 'T/T — 电汇',
  p_cdoc_pm_lc: 'L/C — 信用证',
  p_cdoc_pm_sblc: 'SBLC — 备用信用证',
  p_cdoc_pm_bg: 'BG — 银行保函',
  p_cdoc_pay_method_note: '会打印在单据抬头，并在银行信息旁写出全称。',
  p_cdoc_pay_method_blank: '境内发票通常留空；以银行工具结算时才需要选择。',
  p_cdoc_customer: '客户 *',
  p_cdoc_doc_type: '单据类型',
  p_cdoc_doc_type_locked: '— 开具后不可更改',
  p_cdoc_due_date: '到期日 *',
  p_cdoc_due_hint: '— 开具日 + {{days}} 天',
  p_cdoc_edit_note: '单据编号仍为 {{docNo}}。若本单据已发给客户,请将更正后的单据再发一次。',
  p_cdoc_entered_incl: '录入金额(含 VAT){{amount}} — 拆分见下',
  p_cdoc_err_due: '必须填写到期日 — 此发票何时应付?',
  p_cdoc_err_due_before: '到期日不能早于开具日期。',
  p_cdoc_err_line: '每一行都必须选择服务项目 —— 请选择,或删除空行。',
  p_cdoc_err_reason: '更正税务单据必须填写原因。',
  p_cdoc_fx_date: '汇率日期',
  p_cdoc_fx_note: '税务发票通常应以泰铢开具;使用外币需经税务局批准 (s.86/4)。单据上会打印折合泰铢的金额。',
  p_cdoc_fx_published: '{{date}} 公布汇率：{{rate}}',
  p_cdoc_fx_published_near: '{{asked}} 无公布汇率；最近一次为 {{date}} 的 {{rate}}',
  p_cdoc_fx_far: '此处填写的汇率与公布汇率相差超过 2%——请核对数值与币种。',
  p_cdoc_fx_ph: '例如 33.50',
  p_cdoc_fx_rate: '兑 {{cur}} 汇率 *',
  p_cdoc_issue_btn: '开具 {{docKind}}',
  p_cdoc_issue_date: '开具日期',
  p_cdoc_issuing: '开具中…',
  p_cdoc_items: '明细',
  p_cdoc_less_wht: '减:预扣税 {{amount}}',
  p_cdoc_net: '实收净额',
  p_cdoc_qty_title: '数量',
  p_cdoc_remark: '备注(选填)',
  p_cdoc_save_changes: '保存修改',
  p_cdoc_saving: '保存中…',
  p_cdoc_subtotal: '小计',
  p_cdoc_title_edit: '编辑 {{docKind}} {{docNo}}',
  p_cdoc_title_new: '新建{{docKind}}',
  p_cdoc_total: '合计',
  p_cdoc_type_invoice: '发票 / ใบแจ้งหนี้ — 服务类,税务发票于收款时开具',
  p_cdoc_type_proforma: '形式发票 —— 非税务发票,尚未产生 VAT 纳税义务',
  p_cdoc_type_tax_invoice: '税务发票/送货单 · ใบกำกับภาษี/ใบส่งของ — 货物,交货时计缴 VAT',
  p_cdoc_type_tir: '税务发票 / 收据 · ใบกำกับภาษี/ใบเสร็จรับเงิน — 现销,当场付清',
  p_cdoc_unitprice_title: '单价',
  p_cdoc_valid_until: '有效期至',
  p_cdoc_vat: 'VAT',
  p_cdoc_vat_title: 'VAT 处理方式',
  // crec
  p_crec_already_paid: '已付',
  p_crec_attach_hint: '之后可在收据列表中上传买方的 50 ทวิ 凭证。',
  p_crec_cash: '实收金额({{cur}})',
  p_crec_date_ok: '收据将按 {{yyyymm}} 计日期和编号',
  p_crec_date_required: '必填 — 以银行对账单为准,不是您录入的当天日期',
  p_crec_differs: '· 与未付金额 {{amount}} 不符 — 将记为部分付款',
  p_crec_err: '失败',
  p_crec_err_exceeds: '收款 {{received}} 超过{{docWord}} {{invoiceNo}} 尚未收的 {{outstanding}} — 收据金额不得超过未收额。最多填 {{outstanding}}(现金 + 预扣税)。',
  p_crec_err_wht_exceeds: '预扣税 {{wht}} 超过{{docWord}} {{invoiceNo}} 尚待收取的预扣税 {{left}} — 收据记录的预扣税不得超过买方尚需代扣的金额。预扣税最多填 {{left}}。',
  p_crec_err_zero: '实收金额必须大于零。',
  p_crec_err_settled_doc: '{{docWord}} {{invoiceNo}} 已全额开具收据({{receiptNos}})— 已无未收金额,再开一张会把同一笔销售记两次。',
  p_crec_err_nothing_owed_doc: '{{docWord}} {{invoiceNo}} 已无未收金额 — 没有可开收据的款项。',
  p_crec_err_void: '{{docWord}} {{invoiceNo}} 已作废 — 没有未收金额,没有可开收据的款项。',
  p_crec_err_credited: '{{docWord}} {{invoiceNo}} 已无未收金额(已开收据 {{receipted}},已折让 {{credited}})— 没有可开收据的款项。',
  p_crec_err_negative: '金额不能为负数 — 实收现金与买方代扣的预扣税请填 0 或以上。',
  p_crec_err_nothing_owed: '发票 {{invoiceNo}} 已无未收金额 — 没有可开收据的款项。',
  p_crec_err_period_closed: '收款日期 {{date}} 落在已结账的会计期间({{from}} – {{to}})内 — 该期间不得再记账。请核对款项实际到账的日期。',
  p_crec_err_filed: '{{ym}} 的增值税已在申报中心标记为已申报：日期为 {{date}} 的收据会给已提交的申报表增加销项税行。请核对款项到账日期，或先在那里取消 {{ym}} 的标记。',
  p_crec_err_fx_rate: '{{docNo}} 以 {{currency}} 计价，没有可供此收据沿用的汇率，因此无法记账。请先在 {{docNo}} 上填写汇率。',
  p_crec_err_settled: '发票 {{invoiceNo}} 已全额开具收据({{receiptNos}})— 已无未收金额,再开一张会把同一笔销售记两次。',
  p_crec_exceeds: '· 超过未收金额 {{amount}} — 收据不得超过未收额,无法开具',
  p_crec_expected_wht: '预计预扣税',
  p_crec_invoice: '单据 *',
  p_crec_invoice_total: '单据总额',
  p_crec_issue: '开具收据',
  p_crec_negative: '· 金额为负数 — 收据记录的是实际到账的款项,无法开具',
  p_crec_of_which: '— 其中待收预扣税 {{wht}},现金 {{cash}}',
  p_crec_wht_over: '· 预扣税超过尚待收取的 {{wht}} — 收据不得记录更多预扣税,无法开具',
  p_crec_zero: '· 请填写实收现金或买方代扣的预扣税 — 金额为零的收据无法开具',
  p_crec_wht_none_edit: '{{docWord}} {{invoiceNo}} 开具时没有预扣税:如果买方确实预扣了税款,请先编辑 {{invoiceNo}},给其明细行填上预扣税率,再登记这笔收款。',
  p_crec_wht_none_cash: '{{docWord}} {{invoiceNo}} 开具时没有预扣税,且已不能再编辑:请只登记实收现金,并把买方的预扣税凭证交给会计。',
  p_crec_wht_none_void: '{{docWord}} {{invoiceNo}} 开具时没有预扣税:如果买方确实预扣了税款,请先作废 {{receiptNos}},再编辑 {{invoiceNo}} 给其明细行填上预扣税率,然后在同一张收据上登记现金和预扣税。',
  p_crec_wht_none_void_staff: '{{docWord}} {{invoiceNo}} 开具时没有预扣税:如果买方确实预扣了税款,请先让客户在其门户中作废 {{receiptNos}}(收据只能在门户作废,本工作台不能),再编辑 {{invoiceNo}} 给其明细行填上预扣税率,然后在同一张收据上登记现金和预扣税。',
  p_owed_received_credited: '已收 {{received}} · 已折让 {{credited}}',
  p_crec_option_outstanding: '未收 {{cur}} {{amount}}',
  p_crec_option_outstanding_wht: '未收 {{cur}} {{amount}} — 其中待收预扣税 {{wht}},现金 {{cash}}',
  p_crec_outstanding: '未收',
  p_crec_payment_date: '收款日期(款项到账日)*',
  p_crec_saving: '保存中…',
  p_crec_select_invoice: '— 选择未付单据 —',
  p_crec_settling: '结清 {{total}} = 现金 {{cash}} + 预扣税 {{wht}}',
  p_crec_subtitle: '选择单据后会自动带出尚未收到的金额,以及买方预计代扣的预扣税。若实际到账金额不同,可自行调整这两个数字。',
  p_crec_title: '登记收款',
  p_crec_wht: '买方代扣的预扣税',
  // cust
  p_cust_add: '+ 新增客户',
  p_cust_confirm_remove: '移除 {{name}}?',
  p_cust_empty: '暂无客户 — 添加第一位买方。',
  p_cust_f_address: '地址',
  p_cust_f_branch: '分支编号',
  p_cust_f_email: '邮箱',
  p_cust_f_name: '公司 / 个人名称 *',
  p_cust_f_name_th: '泰文名称(选填)',
  p_cust_f_notes: '备注',
  p_cust_f_notes_hint: '— 仅内部可见,不显示在单据上',
  p_cust_f_phone: '电话',
  p_cust_f_taxid: '税号',
  p_cust_modal_edit: '编辑客户',
  p_cust_modal_new: '新增客户',
  p_cust_search_ph: '搜索名称、编号、税号、邮箱…',
  p_cust_th_branch: '分支编号',
  p_cust_th_email: '邮箱',
  p_cust_th_name: '名称',
  p_cust_th_no: '编号',
  p_cust_th_taxid: '税号',
  p_cust_title: '我的客户',
  // disc
  p_disc_accept: '本人已阅读并接受上述内容。',
  p_disc_body_bold: '单据编号的正确性与连续性由您自行负责',
  p_disc_body_p1: '您的所有销售单据(报价单、发票、收据)都必须通过本系统开具,以保持编号连续。编号不会重复使用;单据作废后,其编号保留为 VOID。',
  p_disc_body_p2: ',包括您在本系统之外开具的单据。',
  p_disc_continue: '继续',
  p_disc_title: '单据编号由您负责',
  // doc
  p_doc_act_attach_wht: '上传 50 ทวิ',
  p_doc_act_attach_wht_title: '上传买方出具的 50 ทวิ 凭证',
  p_doc_act_delete: '删除并释放编号',
  p_doc_act_edit: '编辑',
  p_doc_act_pdf: 'PDF',
  p_doc_act_send: '发送至…',
  p_doc_act_send_title: '将此单据邮件发送给客户,并附 PDF',
  p_doc_act_void: '作废',
  p_doc_add_new: '+ 新建{{docKind}}',
  p_doc_add_note: '+ 新建折让单 / 增额单',
  p_doc_add_receipt: '+ 登记收款',
  p_doc_confirm_handed_over: '{{docNo}} 已被下载为 PDF,买方可能已持有该副本。\n\n修改后,对方手中的副本将与系统记录不一致。若对方已持有,请改为开具折让单/增额单,或作废后重新开具。\n\n仍要修改?',
  p_doc_delete_prompt: '删除 {{docNo}} 并释放该编号?\n\n仅同一系列中最新的编号可以释放。您下次开具的单据将重新使用 {{docNo}}。\n\n原因?(将被记录)',
  p_doc_empty: '暂无内容。',
  p_doc_empty_notes: '尚无折让单或增额单。可从需更正的税务发票开具。',
  p_doc_err_open: '无法打开此单据。',
  p_doc_freed: '{{docNo}} 已释放 — 下一份单据将使用该编号。',
  p_doc_head_invoices: '发票',
  p_doc_head_notes: '折让/增额单',
  p_doc_head_quotations: '报价单',
  p_doc_head_receipts: '收据',
  p_doc_kind_invoice: '发票',
  p_doc_kind_quotation: '报价单',
  p_doc_not_editable: '该单据不可编辑。',
  // Batch 11 R1 (COA-D9): the edit window's read-only note on a document already in the accounting books.
  p_ib_title: '已记入会计账簿',
  p_ib_note: '这张单据已记入会计账簿（{{docNos}}）。如需更改，请先联系您的会计师。',
  // Batch 11 R3（COA-D9）：已记入会计账簿的单据——客户还能做什么（从不出现「冲销」）。
  p_ll_edit_limited: '编辑（受限）',
  p_ll_void_locked: '已记入会计账簿（{{docNos}}）——不能再作废。如需更改，请联系您的会计师。',
  p_ll_void_filed: '{{month}} 的增值税申报已提交——本单据不能再作废。如需更改，请联系您的会计师（可以开具贷项或借项通知单更正）。',
  p_ll_delete_filed: '{{month}} 的增值税申报已提交——本单据不能再删除。如需更改，请联系您的会计师。',
  p_ll_delete_locked: '已记入会计账簿（{{docNos}}）——编号不能再交回。',
  p_ll_limited_title: '已记入会计账簿——受限编辑',
  p_ll_tax_for_title: '为已收款发票开具——受限编辑',
  p_ll_tax_for_adjusted: '这张税务发票是为 {{forNo}}（{{receiptNos}}）开具的，该笔销售此后已被 {{nos}} 调整。它不在会计账簿里记任何内容，金额、明细、日期、买方和币种保持开具时的样子：这里只能改备注、银行账户、付款方式和到期日。',
  p_ll_tax_for_changed: '这张税务发票是为 {{forNo}}（{{receiptNos}}）开具的，该笔已收款的销售在开出这张税务发票后已有变化。它不在会计账簿里记任何内容，金额、明细、日期、买方和币种保持开具时的样子：这里只能改备注、银行账户、付款方式和到期日。',
  p_ll_tax_for_filed: '这张税务发票是为 {{forNo}}（{{receiptNos}}）开具的，日期为 {{date}}，属于增值税已标记为已申报的 {{ym}}。它不在会计账簿里记任何内容，但不会在该月重新开具：金额、明细、日期、买方和币种保持开具时的样子 —— 这里只能改备注、银行账户、付款方式和到期日。',
  p_ll_tax_for_closed: '这张税务发票是为 {{forNo}}（{{receiptNos}}）开具的，日期为 {{date}}，落在已结账的会计期间（{{from}} – {{to}}）内。它不在会计账簿里记任何内容，但不会在该期间重新开具：金额、明细、日期、买方和币种保持开具时的样子 —— 这里只能改备注、银行账户、付款方式和到期日。',
  p_ll_limited_note: '它的金额、明细、日期、买方和币种已记入会计账簿（{{docNos}}）。这里只能改备注、银行账户、付款方式和到期日；其他内容请联系您的会计师。',
  p_ll_receipt_superseded: '这张发票的一条增值税行（{{lineNo}}）已记入会计账簿（{{docNos}}），暂时不能开这张收据。请联系您的会计师。',
  p_ib_hand_changed: '{{docNos}} 是以手工方式记入您账簿的分录，之后它所记录的内容有了改动——请联系您的会计师核对。',
  p_doc_note_credit: '折让',
  p_doc_note_debit: '增额',
  p_doc_reason_required: '必须填写原因。',
  p_doc_search_ph: '搜索编号、客户、地址…',
  p_doc_send_tooltip: '已发送 {{count}} 次 · 最近发往 {{email}}',
  p_doc_status_open: '未结',
  p_doc_status_paid: '已付款',
  p_doc_status_partial: '部分付款',
  p_doc_status_unpaid: '未付款',
  p_doc_status_rejected: '已拒绝',
  p_doc_status_void: 'VOID',
  p_doc_th_adjusts: '调整发票',
  p_doc_th_customer: '客户',
  p_doc_th_date: '日期',
  p_doc_th_difference: '差额({{cur}})',
  p_doc_th_no: '编号',
  p_doc_th_due: '\u5230\u671f\u65e5',
  p_doc_th_owing: '\u672a\u6536\u4f59\u989d',
  p_doc_th_paid: '\u5df2\u4ed8',
  p_sum_n_inv: '{{count}} \u5f20\u53d1\u7968',
  p_sum_n_doc: '{{count}} \u5f20\u5355\u636e',
  p_sum_oldest: '\u6700\u4e45 {{days}} \u5929 \u2014 {{docNo}}',
  p_sum_outstanding: '\u5f85\u6536\u6b3e',
  p_sum_overdue: '\u5df2\u903e\u671f',
  p_sum_scope: '\u5168\u90e8\u671f\u95f4 \u00b7 \u622a\u81f3\u4eca\u65e5',
  p_doc_th_invoice_no: '对应发票',
  p_conv_already: '这张报价单已经开过：{{list}}。',
  p_conv_already_stages: '这张报价已经开过：{{list}}。再开一张是允许的（报价可以分期开票），但你的客户会收到第二张发票。',   // (owner 2026-11-03) main's sentence, where the workspace is off: billed in stages
  p_conv_cancel: '取消',
  p_conv_done: '发票 {{docNo}} 已开具。',
  p_conv_go: '开具发票',
  p_conv_loading: '正在检查这张报价…',
  p_conv_receipts: '已经挂在这张报价下的收款（{{list}}）不会转过去，所以新发票按全额未付开出。把它们搬过去会改写客户手上已经持有的单据。',
  p_conv_subtitle: '发票会逐行复制这张报价，日期为今天，初始状态为未付款。',
  p_conv_rejected: '报价单 {{docNo}} 已标记为已拒绝：在更改其状态之前，不能开票、编辑或发送。',
  p_conv_err_already: '{{nos}} 已由 {{docNo}} 开出，一张报价单只开票一次。如需重新开票，请先作废该单据。',
  p_conv_err_tax_unpaid: '{{docNo}} 尚未收款，现在开税务发票会记一次销售，它的收据还会再记一次。请先开收据记录收款，再为它开税务发票；或作废 {{docNo}}，改开税务发票。',
  p_conv_err_void: '此报价单已作废。请另开一张新的报价单。',
  p_conv_err_world_plain: '此报价单按普通单据开具，而公司现在开具泰国税务单据。请开一张新报价单，再据此开票。',
  p_conv_err_world_thai: '此报价单按泰国税务规则开具，而公司现在开具普通单据。请开一张新报价单，再据此开票。',
  p_conv_err_customer_gone: '{{docNo}} 上的客户已不在客户列表中。请重新添加，或另开一张报价单。',
  p_conv_err_customer: '{{docNo}} 的报价对象是 {{customer}}，由它开出的单据必须开给同一客户。',
  p_conv_err_no_lines: '此报价单没有可开票的行。',
  p_conv_err_type: '此公司不从报价单开具这类单据。',
  p_conv_err_not_found: '在此公司的单据中找不到本单据引用的报价单。',
  p_conv_err_receipt_needs_bill: '收据要开在单据上。请先由 {{docNo}} 开具发票。',
  p_conv_err_nothing_owed: '{{nos}} 已无欠款，无需开收据。',
  p_conv_err_cash_date: '现金销售表示款项已到账：请填写到账日期。',
  p_conv_err_cash_future: '现金销售表示款项已到账，因此日期不能晚于今天。请填写实际到账日期。',
  p_conv_err_filed: '{{ym}} 的增值税已在申报中心标记为已申报：日期为 {{date}} 的单据会给已提交的申报表增加销项税行。请改用其他日期，或先在那里取消 {{ym}} 的标记。',
  p_conv_err_repriced_vat: '这张报价单总额为 {{quoted}}，但开票金额会变成 {{total}}：报价后您的增值税登记状态已变更。请重新开一张报价单，让客户看到新金额，再据此开票。',
  p_conv_err_repriced_split: '这张报价单无法按报价金额开票：其中一行转嫁成本时高于供应商收费，加价部分现在要作为您自己的收费并计增值税。请用这些行重新开一张报价单，让客户看到新金额，再据此开票。编辑这张报价单不会改变其行，仍然无法开票。',
  p_conv_proformas: '已由此报价单开出的形式发票：{{list}}。形式发票不计费，不影响此次开票，但客户可能已经收到。',
  p_conv_err_fx_approval: '除非税务局批准其他币种，税务发票须以泰铢开具；{{currency}} 没有批准记录。',
  p_copy_err_customer_gone: '此客户已不在客户列表中。请另选一位。',
  p_doc_edit_billed: '{{nos}} 已由此报价单开出，因此不能编辑。请先作废。',
  p_conv_title: '由 {{docNo}} 开具发票',
  p_conv_type_invoice: '普通发票 Invoice',
  p_conv_type_invoice_hint: '帐单。服务业的销项税在客户付款时才发生（s.78/1），所以税票是后面那张收据。',
  p_conv_type_label: '这张开成哪种单据？',
  p_conv_type_locked: '开出后不能再改——它决定纳税义务什么时候发生。要改只能作废重开。',
  p_conv_type_proforma: '形式发票 Proforma',
  p_conv_type_proforma_hint: '不是税务单据。不产生纳税义务，也不入账。',
  p_conv_type_tax_invoice: '税务发票 Tax invoice',
  p_conv_type_tax_invoice_hint: '卖货的销项税在交货时发生（s.78），所以 VAT 记在这张单上——算在本月。',
  p_conv_working: '开具中…',
  p_conv_fx_rate_missing: '{{docNo}} 以 {{currency}} 开具且没有汇率,所以暂时不能转为税务发票:请先编辑这张报价,填写汇率及其日期,再开具税务发票。在此之前不会开具任何单据,也不会占用编号。',
  p_doc_act_to_invoice: '开具发票',
  p_doc_err_period_closed: '日期 {{date}} 落在已结账的会计期间({{from}} – {{to}})内 — 不能在该期间开具此单据。已结账期间不得再记账。',
  p_doc_err_period_closed_edit: '{{docNo}} 的日期为 {{date}},落在已结账的会计期间({{from}} – {{to}})内 — 不能修改:那会改动已经结账的账簿。',
  p_doc_err_period_closed_void: '{{docNo}} 的日期为 {{date}},落在已结账的会计期间({{from}} – {{to}})内 — 不能作废或删除:那会改动已经结账的账簿。',
  p_doc_err_void_receipts_first: '{{docNo}} 还有有效的收据 — 请先作废其收据,再作废本单据。',
  p_doc_link_self: '本单即收据',
  p_doc_th_property: '房产',
  p_doc_th_receipt_no: '对应收据',
  p_doc_th_received: '已收({{cur}})',
  p_doc_th_sent: '已发送',
  p_doc_th_status: '状态',
  p_doc_th_total: '合计',
  p_doc_th_wht: '预扣税',
  p_doc_void_prompt: '作废 {{docNo}}?\n\n该编号将以 VOID 状态保留在序列中,且不会重复使用。\n\n作废原因?(将被记录)',
  p_doc_wht_saved: '预扣税凭证已保存 ✓',
  // empty
  p_empty_no_match: '未找到与“{{query}}”匹配的结果。',
  // erec
  p_erec_bank: '单据上显示的银行账户',
  p_erec_bank_none: '— 无 —',
  p_erec_received: '已收 {{amount}}',
  p_erec_remark: '备注',
  p_erec_saving: '保存中…',
  p_erec_title: '编辑收据 {{docNo}}',
  p_erec_warning: '本收据同时是税务发票。客户、金额、VAT 和日期是买方申报进项税的依据,因此不能在此修改 — 如需更正其中任何一项,请作废本收据并重新登记该笔收款。',
  p_erec_wht: '· 预扣税 {{amount}}',
  // err
  p_err_create_failed: '创建失败',
  p_err_save_failed: '保存失败',
  // exp
  p_exp_act_remove: '移除',
  p_exp_act_rescan: '重新识别金额',
  p_exp_act_rescan_title: '重新识别该扫描件的金额',
  p_exp_act_view: '查看附件',
  p_exp_confirm_remove: '删除 {{filename}}?',
  p_exp_empty: '{{year}} 年 {{month}}尚未上传任何文件。',
  p_exp_err_open: '无法打开该文件',
  p_exp_file_fallback: '文件',
  p_exp_file_hint: '仅支持照片和 PDF — Word 和 Excel 无法读取,请先导出为 PDF。多页发票请合并为一个 PDF,不要分开拍照。',
  p_exp_filed_warn: '{{year}} 年 {{month}} 的 VAT 申报已提交。本月之后再发送的资料将按增补申报处理,不计入已提交的那份申报表。',
  p_exp_filed_warn_bold: '增补申报',
  p_exp_filter_all: '全部状态',
  p_exp_filter_approved: '已入账',
  p_exp_filter_pending: '等待 {{brand}} 处理',
  p_exp_filter_rejected: '已退回',
  p_exp_list_title: '已上传 — {{year}} 年 {{month}}',
  p_exp_month: '月份',
  p_exp_msg_appended: '· {{filename}} — 已作为发票 {{docNo}} 的第 {{page}} 页添加',
  p_exp_msg_ocr_error: '部分文件无法自动识别 — {{brand}} 将手工录入金额。',
  p_exp_msg_saved: '已收到 {{n}} 张发票,等待 {{brand}} 处理。',
  p_exp_msg_skipped: '{{n}} 份因重复被跳过:',
  p_exp_msg_skipped_line: '· {{filename}}{{docNo}} —— {{why}}',
  p_exp_no_vat: '无 VAT',
  p_exp_ocr_error_row: '无法自动识别',
  p_exp_page_chip: 'p{{n}}',
  p_exp_page_title: '第 {{n}} 页',
  p_exp_quota_unlimited: '{{year}} 年 {{month}}:已上传 {{used}} 份 · 不限数量',
  p_exp_quota_full: '{{year}} 年 {{month}} 已达每月 {{cap}} 份上限 — 需要更多请邮件联系 {{contact}}',
  p_exp_quota_left: '{{month}} {{year}}:已用 {{used}}/{{cap}} · 剩余 {{left}}',
  p_exp_reading: '识别中…',
  p_exp_search_ph: '搜索供应商、发票号、文件…',
  p_exp_status_approved: '已入账',
  p_exp_status_pending: '待 {{brand}} 处理',
  p_exp_status_rejected: '已退回',
  p_exp_status_cancelled: '已被事务所作废',
  p_exp_subtitle: '您已支付账单的照片或 PDF。{{brand}} 会逐份与扫描件核对后再入账 — 这里上传的内容本身不会改变您的报税。',
  p_exp_th_amount: '金额',
  p_exp_th_attachment: '附件',
  p_exp_th_bucket: '分类',
  p_exp_th_date: '日期',
  p_exp_th_docno: '发票编号',
  p_exp_th_status: '状态',
  p_exp_th_supplier: '供应商',
  p_exp_th_vat: 'VAT',
  p_exp_title: '请上传您的采购发票',
  p_exp_upload: '＋ 上传发票',
  p_exp_upload_failed: '上传失败',
  p_exp_with_vat: '含 VAT',
  p_exp_year: '年份',
  // fil
  p_fil_chip_additional: 'A{{n}}',
  p_fil_chip_attachment: '附件',
  p_fil_chip_form: '申报表',
  p_fil_chip_receipt: '缴税收据',
  p_fil_legend: '绿色 = 已申报,下方可查看文件 · 申报表 = 已提交的申报表 · 收据 = 税务局缴款收据 · ✓ = 已申报,文件尚未上传。缺少内容?联系 {{brand}},我们会补上。',
  p_fil_loading: '加载中…',
  p_fil_subtitle: '{{brand}} 为您逐月申报的税表。点击月份可查看已申报表单或税务局回执。',
  p_fil_th_filing: '申报表',
  p_fil_title: '税务与申报',
  // filing
  p_filing_doc_gone: '该单据已不可用,请联系 {{brand}}。',
  // img
  p_img_err_generic: '无法读取该图片',
  p_img_err_not_image: '该文件不是图片',
  p_img_err_read: '无法读取该文件',
  p_img_none: '无',
  p_img_remove: '移除',
  p_img_replace: '替换',
  p_img_upload: '上传',
  // imp
  p_imp_already_bold: '{{n}} 个单据编号已存在',
  p_imp_already_rest: ',导入将中止:{{list}}',
  p_imp_btn: '导入',
  p_imp_btn_n: '导入 {{n}} 条',
  p_imp_confirm: '按表格中的单据编号创建 {{n}} 份{{docTypeWord}}?\n\n将新建 {{m}} 条客户记录。\n\n此操作无法批量撤销 — 只能逐份作废单据。',
  p_imp_create_as: '创建为',
  p_imp_done: '✓ 已创建 {{created}} 张发票 · 新增 {{customers}} 位客户',
  p_imp_done_btn: '完成',
  p_imp_err_failed: '导入失败',
  p_imp_err_period_closed: '未导入任何内容 — 表中 {{rows}} 行里有 {{n}} 行的日期落在已结账的会计期间({{periods}})内。已结账期间不得再记账:请把这些行从表中删去后重新导入。',
  p_imp_err_read: '无法读取该文件。',
  p_imp_open: '从表格导入',
  p_imp_map_title: '把表格的列对应到系统字段',
  p_imp_map_saved: '· 已套用你上次为这种表保存的对应关系',
  p_imp_map_edit: '修改对应',
  p_imp_map_hide: '收起对应',
  p_imp_map_th_column: '表格列',
  p_imp_map_th_sample: '示例内容',
  p_imp_map_th_field: '对应到',
  p_imp_map_th_vat: 'VAT 口径',
  p_imp_map_th_label: '发票行名称',
  p_imp_map_no_header: '第 {{n}} 列(无表头)',
  p_imp_map_required: '没有任何列对应到「{{field}}」—— 每张单据都必须有。',
  p_imp_map_need_amount: '至少要指定一列金额 —— 它才是发票的行项。',
  p_imp_field_ignore: '不导入',
  p_imp_field_customer: '客户名称',
  p_imp_field_docNo: '发票编号',
  p_imp_field_issueDate: '开票日期',
  p_imp_field_dueDate: '到期日',
  p_imp_field_address: '单据上的地址',
  p_imp_field_description: '说明',
  p_imp_field_remark: '备注',
  p_imp_field_fee: '金额(生成一行)',
  p_imp_field_total: '表格自带合计(仅用于核对)',
  p_imp_fee_q: '各费用列如何计税?',
  p_imp_fee_warn: '—— 此处填错,每位买方的付款金额都会差 7%',
  p_imp_file_label: '电子表格 (.xlsx)',
  p_imp_header_incl: '· 表头标注“含 VAT”',
  p_imp_header_unknown: '· 表头未说明 — 请确认',
  p_imp_mismatch_body: '发票将按上方的费用列开具;表中合计的口径可能不同。导入前请抽查几笔:',
  p_imp_mismatch_bold: '有 {{n}} 行与表格自身的合计列对不上。',
  p_imp_mismatch_line: '第 {{n}} 行 · {{docNo}} · 发票 {{a}},表格 {{b}}',
  p_imp_more: '… 另有 {{n}} 条',
  p_imp_prev_th_customer: '客户',
  p_imp_prev_th_date: '日期',
  p_imp_prev_th_lines: '明细行',
  p_imp_prev_th_total: '合计',
  p_imp_sheet: '工作表',
  p_imp_skipped_head: '已跳过 — 以下不会导入',
  p_imp_skipped_row: '第 {{n}} 行',
  p_imp_skipped_title: '已跳过的行 — 未为其创建任何单据:',
  p_imp_subtitle: '每张发票一行。单据编号、日期和金额完全按您的文件原样导入,不作生成或调整。',
  p_imp_summary: '将创建 {{rows}} 张发票 · 新增客户 {{customers}} 家 · 合计 {{amount}} {{cur}}',
  p_imp_summary_skipped: '跳过 {{skipped}} 行',
  p_imp_th_docno: '发票号',
  p_imp_th_owner: '所属人',
  p_imp_th_property: '房产',
  p_imp_th_row: '行',
  p_imp_th_why: '原因',
  p_imp_title: '从表格导入发票',
  p_imp_type_inv: '发票 — 仅用于收款,税务发票于付款时开具',
  p_imp_type_ti: '税务发票 / 送货单 — 交付时产生 VAT',
  p_imp_type_tir: '税务发票 / 收据 —— 现销,已结清',
  p_imp_word_invoices: '发票',
  p_imp_word_tax_invoices: '税务发票',
  p_imp_working: '处理中…',
  // lang
  p_lang_en: 'English',
  p_lang_en_short: 'EN',
  p_lang_th_short: 'ไทย',
  p_lang_zh_short: '中',
  // loading
  p_loading: '加载中…',
  // login
  p_login_email: '邮箱',
  p_login_err_failed: '登录失败',
  p_login_err_staff: '这里是客户门户,员工请使用内部系统。',
  p_cdoc_line_account: '该行入账科目（在「服务与科目」里按服务设置）',
  p_cdoc_line_account_default: '按服务默认（{{code}}）',
  p_cdoc_line_account_none: '科目 — 未选',
  // Cross-installation sign-in (owner 2026-10-15)
  p_login_err_blank: '请输入邮箱和密码。',
  p_login_hop_text: '该账号属于事务所的另一处安装({{host}}),要在那里登录吗?',
  p_login_hop_go: '是,去那里登录',
  p_login_hop_cancel: '取消',
  p_login_password: '密码',
  p_login_signin: '登录',
  p_login_signing_in: '登录中…',
  p_login_subtitle: '面向企业的开票与账单',
  p_login_title: '{{brand}} 客户门户',
  // logo
  p_logo_alt: '{{brand}}',
  // menu
  p_menu_actions_tooltip: '操作',
  p_menu_bills: '{{brand}} 的账单',
  p_menu_bills_hint: '{{brand}} 向您收取的费用',
  p_menu_customers: '客户',
  p_menu_customers_hint: '您开票的买方',
  p_menu_filings: '税务与申报',
  p_menu_filings_hint: '{{brand}} 为您申报的税表',
  p_menu_group_fromslf: '来自 {{brand}}',
  p_menu_group_mydata: '我的资料',
  p_menu_group_company: '公司',
  p_menu_products: '商品与服务',
  p_menu_products_hint: '您销售的商品/服务',
  p_menu_profile: '公司资料',
  p_menu_profile_hint: '地址、标志、单据编号',
  p_menu_tooltip: '菜单',
  p_menu_working: '处理中…',
  // month
  p_month_apr: '4月',
  p_month_aug: '8月',
  p_month_dec: '12月',
  p_month_feb: '2月',
  p_month_jan: '1月',
  p_month_jul: '7月',
  p_month_jun: '6月',
  p_month_mar: '3月',
  p_month_may: '5月',
  p_month_nov: '11月',
  p_month_oct: '10月',
  p_month_sep: '9月',
  // nav
  p_nav_bankstmt: '银行流水',
  p_nav_expenses: '采购发票',
  p_office_label: '您的 {{brand}} 办公室',
  p_office_hint: '为您记账并出具单据的 {{brand}} 办公室',
  p_nav_owner: '老板控制台',
  p_nav_backoffice: '后台账簿',
  p_nav_backoffice_hint: '在会计工作台中打开本公司的账簿。新标签页打开，需要员工登录。',
  p_nav_mybooks: '我的账簿',
  p_nav_mybooks_hint: '我们为你记的账本、试算表和财务报表。只读。',
  p_own_title: '老板控制台',
  p_own_lede: '一眼看清:谁欠你钱、你欠多少、接下来要交什么。',
  p_own_empty: '这里暂时还没有内容。等你开始开票、或我们开始为你申报,这一页就会有数据。',
  p_own_owed_you: '别人欠你',
  p_own_overdue_suffix: '已逾期',
  p_own_chase: '最久未收',
  p_own_days_late: '逾期 {{days}} 天',
  p_own_owed_slf: '你欠 {{brand}}',
  p_own_next_due: '最近一笔 {{date}} 到期',
  p_own_waiting: '等你处理',
  p_own_and_more: '另有 {{n}} 项',
  p_own_task_upload: '把本月单据发给我们',
  p_own_task_tax_pay: '缴税',
  p_own_task_svc_pay: '支付我们的服务费',
  p_own_task_info: '补充我们需要的资料',
  p_own_upcoming: '接下来要交',
  p_own_today: '今天',
  p_own_in_days: '{{days}} 天后',
  p_own_amount_tbc: '金额待定',
  p_own_amount_credit: '留抵 {{amount}}',
  p_own_efiling_note: '以上是税务局电子申报的截止日,比柜台申报日要晚。',
  p_own_booked: '{{year}} 年已入账',
  p_own_revenue: '销售额',
  p_own_purchases: '采购额',
  p_own_booked_note: '来自已记录的 {{months}} 个月。这不是利润——工资要等当月结账后才计入。',
  p_own_filed: '{{year}} 年已为你申报',
  p_own_filed_note: '我们已提交的申报表',
  p_nav_books: '我的账簿',
  p_menu_company_find: '查找公司…',
  p_menu_books_locked: '开账后可见',
  p_books_period: '会计期间',
  p_menu_company_none: '没有匹配的公司。',
  p_menu_company_n_of: '{{n}} / {{total}}',
  p_menu_policy: '我们怎么记你的账',
  p_menu_policy_hint: '我们对你账务实际采用的处理',
  p_menu_chart: '科目表',
  p_menu_chart_hint: '你的账记在哪些科目里',
  p_menu_ledgers: '账本与试算表',
  p_menu_ledgers_hint: '五本日记账、分类账与试算表',
  p_books_no_entries: '本期无分录',
  p_menu_statements: '财务报表',
  p_menu_statements_hint: '资产负债表、损益表、权益变动表',
  p_pol_title: '我们怎么记你的账',
  p_pol_lede: '这一页说明我们系统对你账务实际的处理方式。它描述的是软件正在执行的规则,不是法律条文的转述;涉及法律的部分,以你的会计师出具的书面意见为准。',
  p_pol_readonly: '本页为只读。',
  p_pol_s1_t: '什么时候算你的收入',
  p_pol_s1_b: '开税务发票(含税务发票/收据):在单据开立日当月计入销项。开普通发票:要等收到钱、并登记了收据之后才计入。这意味着一张普通发票如果本月没收到钱,这笔收入不会出现在本月的申报基数里;如果一直没收到,就一直不会出现。',
  p_pol_s2_t: '预扣税什么时候记',
  p_pol_s2_b: '客户代扣的预扣税,我们在收到款时才记,不在开票当期。所以你看到的应收挂的是全额,不是你最终到账的金额。',
  p_pol_s3_t: '折让单与增额单',
  p_pol_s3_b: '发票开出去之后要改金额,我们另开折让单或增额单,在它自己开立的月份单独入账。原来那张发票的记录不会被改动——这是审计要看的轨迹。',
  p_pol_s4_t: '外币',
  p_pol_s4_b: '我们系统里不保存任何汇率。开外币单据时,汇率由开单的人逐张录入;年末换算所需的汇率也必须由人提供给我们。没有汇率我们就拒绝换算,不会估一个数字出来。外币应收因此按币种分别显示,不折算成$t(cur_word)合计。',
  p_pol_s5_t: '不能抵扣的进项税',
  p_pol_s5_b: '不能抵扣的进项税,我们计入专门记录不可抵扣进项税的费用科目,不留在可退税的进项里。',
  p_pol_s6_t: '存货计价',
  p_pol_s6_b: '我们支持两种成本法:先进先出与加权平均。一旦某一期用某个方法估过值,就不能再换成另一种。当存货记录对不上时——有出库没有对应的入库,或者出得比进得多——我们拒绝出计价结果,不会给一个没有依据的数字。',
  p_pol_s7_t: '折旧',
  p_pol_s7_b: '折旧按直线法计算,并按你在该期间内实际持有资产的天数按比例分摊(一年按 365 天)。折旧不会把资产计到残值以下,也不会自己过账:每一笔都先生成草稿,经人工复核后才进入你的账。我们采用的上限如下。',
  p_pol_s7_note: '表中为上限税率。某项资产若未单独设定税率,则按其类别的上限计提。',
  p_pol_s8_t: '亏损结转',
  p_pol_s8_b: '我们逐年登记你的可结转亏损。到期年度一栏印的是「未确定」,因为我们没有可引用的依据来源。要填这个年限,必须同时记录它的法律出处。',
  p_pol_s9_t: '单据到了我们这里会怎样',
  p_pol_s9_b: '你发来的每一份单据归为三类之一。它先变成一张草稿分录,由人工复核后才过账。过账之后的分录永不删除——有错就用一张可见的冲回分录更正。每一笔分录落在五本日记账之一;银行类的分录必须指明是哪个账户。',
  p_pol_th_class: '资产类别',
  p_pol_th_rate: '上限税率',
  p_pol_not_dep: '不折旧',
  p_books_none_t: '你的账还没有建起来',
  p_books_none_b: '等我们为你开好会计期间、过了第一批分录,你的科目表、账本与财务报表就会出现在这里。',
  p_nav_invoices: '发票',
  p_nav_notes: '折让单 / 增额单',
  p_nav_quotations: '报价单',
  p_nav_receipts: '收据',
  p_nav_sign_out: '退出登录',
  // note
  p_note_amount_ph: '金额',
  p_note_difference_label: '差额',
  p_note_err: '无法开具此单据',
  p_note_info: '只填写本单调整的差额,而非原单据全额。该差额对应的 VAT 计入本单开具当月的销项税。',
  p_note_issue_btn: '开具单据',
  p_note_issue_date: '开具日期',
  p_note_issuing: '开具中…',
  p_note_item_ph: '调整的内容',
  p_note_kind_credit: '折让单(ใบลดหนี้)',
  p_note_kind_debit: '增额单 (ใบเพิ่มหนี้)',
  p_note_no_sources: '尚无税务发票 —— 折让/增额单只能调整含 VAT 的单据。',
  p_note_prefilled: '已按 {{docNo}} 开具时的行项填入,每行保留其原有的 VAT 处理。请把每行的数量或金额改为本次冲减的部分 —— 只有全额冲减的行才保持不变。',
  p_note_prefilled_left: '已按 {{docNo}} 尚可冲减的部分填入:{{total}} 中还剩 {{left}} —— 已冲减 {{credited}}({{nos}})。每行保留其原有的 VAT 处理;如只冲减其中一部分,请改小该行。',
  p_note_nothing_left: '{{docNo}} 已没有可冲减的金额:{{total}} 已全部冲减({{nos}})。',
  p_note_cap_line: '{{docNo}} 的「{{line}}」这一行还剩 {{left}} 可冲减(原开 {{charged}},已冲减 {{credited}});本单要冲减 {{takes}}。',
  p_note_cap_doc: '{{docNo}} 还剩 {{left}} 可冲减(原开 {{total}},已冲减 {{credited}});本折让单要冲减 {{takes}}。',
  p_note_cap_doc_by: '{{docNo}} 还剩 {{left}} 可冲减(原开 {{total}},已被 {{nos}} 冲减 {{credited}});本折让单要冲减 {{takes}}。',
  p_note_unlinked: '{{docNo}} 之前的折让单({{nos}})录入时没有注明冲减的是哪一行,因此不预填。{{total}} 中还剩 {{left}} 可冲减 —— 请录入本次冲减的行项。',
  p_note_rcpt_settles: '{{rcptNo}} 是税务发票 {{invNo}} 的收据,本身不列示 VAT —— 折让单/增额单调整的是税务发票。',
  p_note_rcpt_use: '改为调整 {{invNo}}',
  p_note_opt_credit: '折让单 —— 冲减金额(ใบลดหนี้)',
  p_note_opt_debit: '增额单 — 增加金额 (ใบเพิ่มหนี้)',
  p_note_reason_discount: '开票后给予折扣 (ลดราคาภายหลัง)',
  p_note_reason_extra_goods: '追加交付货物或服务(ส่งมอบเพิ่มเติม)',
  p_note_reason_goods_defective: '货物瑕疵 / 与订单不符(สินค้าชำรุดบกพร่อง)',
  p_note_reason_goods_returned: '货物退回(สินค้าถูกส่งคืน)',
  p_note_reason_label: '原因 *(打印在单据上)',
  p_note_reason_other: '其他(อื่น ๆ)',
  p_note_reason_other_ph: '请说明原因',
  p_note_reason_overcharge: '原单据计价高于实际(คำนวณราคาสูงกว่าที่เป็นจริง)',
  p_note_reason_service_not_provided: '服务部分或全部未提供(บอกเลิกการให้บริการ)',
  p_note_reason_undercharge: '原单据金额低于实际 (คำนวณราคาต่ำกว่าที่เป็นจริง)',
  p_note_ref: '被调整的税务发票 *',
  p_note_title_new: '新建{{noteKind}}',
  p_note_total_difference: '差额',
  p_note_wht_title: '本单在这一行印出的预扣税率。从原单据带出的行沿用原行的税率;如本单应印出其他税率,请在此修改。',
  p_note_wht_printed: '本单将印出:预扣税额(WHT Amount){{wht}} · 付款金额(Payment Amount){{net}} —— 按上面各行的预扣税率计算。',
  // 第四轮(2026-10-20):增额单抬高可冲额度;销项税也设上限;带出的行保留原 VAT 类别;旧页面编辑含加价行的单据被拒;
  // 外币税务发票缺汇率先校验再占号;已被折让单冲回的增额单不能作废。
  p_note_cap_doc_dn: '{{docNo}} 还剩 {{left}} 可冲减(原开 {{issued}} + 增额单 {{dnNos}} 增加 {{debited}},已冲减 {{credited}});本折让单要冲减 {{takes}}。',
  p_note_cap_doc_dn_by: '{{docNo}} 还剩 {{left}} 可冲减(原开 {{issued}} + 增额单 {{dnNos}} 增加 {{debited}},已被 {{nos}} 冲减 {{credited}});本折让单要冲减 {{takes}}。',
  p_note_vatcap_line: '{{docNo}} 的「{{line}}」这一行还剩 VAT {{left}} 可冲回(原开 VAT {{charged}},已冲回 {{credited}});本单要冲回 VAT {{takes}}。',
  p_note_vatcap_doc: '{{docNo}} 还剩销项税 {{left}} 可冲回(原开 VAT {{total}},已冲回 {{credited}});本折让单要冲回 VAT {{takes}}。',
  p_note_cat_locked: '第 {{n}} 行冲减的是 {{docNo}} 的「{{line}}」,该行原按「{{category}}」开具。折让单须按原开具的 VAT 类别冲回 —— 请在这一行保留「{{category}}」。',
  p_note_cat_locked_title: '从被冲减单据带出的行保留其原开具的 VAT 类别 —— 折让单按原样冲回。',
  p_note_debited: '{{docNo}} 可冲减的金额中有 {{debited}} 是增额单 {{dnNos}} 增加的;其行项排在原单据行项之后一并填入。',
  p_note_vatcap_doc_dn: '{{docNo}} 还剩销项税 {{left}} 可冲回(原开 VAT {{issued}} + 增额单 {{dnNos}} 增加 VAT {{debited}},已冲回 {{credited}});本折让单要冲回 VAT {{takes}}。',
  p_note_debited_in: '{{docNo}} 的 {{total}} 中有 {{debited}} 是增额单 {{dnNos}} 增加的。',
  p_cdoc_edit_stale: '此页面已过时:{{docNo}} 含有加价行,而此页面没有注明每一行来自哪一条已保存的行,保存会丢失加价行的标记。未保存任何内容 —— 请刷新页面后重新修改。',
  p_cdoc_fx_rate_missing: '这张税务发票以 {{currency}} 开具:开具前请填写汇率及其日期。未开具任何单据,也未占用编号。',
  // ── COA-D6(老板 10/28):为已收款的普通发票开税务发票 —— 不再记账 ──
  p_cdoc_taxfor_label: '对应哪张已收款的发票',
  p_cdoc_taxfor_optional: '(仅当这笔销售已用普通发票开票并已收款时选择)',
  p_cdoc_taxfor_none: '— 不选:一笔新销售(本税务发票入账)—',
  p_cdoc_taxfor_none_avail: '— 不选:该客户没有尚未开税务发票的已收款发票 —',
  p_cdoc_taxfor_pick_customer: '— 请先选择客户 —',
  p_cdoc_taxfor_opt: '{{docNo}} · 已收 {{paid}} · {{receiptNos}}',
  p_cdoc_taxfor_hint: '如果这笔销售已用普通发票向该客户开票且客户已付款，请选择那张发票：税务发票即为它而开，不会再记一次账，并在单据上注明「对应 INV-… / RE-…」。',
  p_cdoc_taxfor_note: '本税务发票对应 {{docNo}} / {{receiptNos}}。这笔销售已通过 {{receiptNos}} 入账，因此本税务发票不再记账 —— 不再确认收入、销项 VAT 或应收款 —— 且金额必须与已收款完全一致:{{paid}},VAT {{paidVat}}。',
  p_cdoc_taxfor_edit_note: '本税务发票对应 {{docNo}} / {{receiptNos}},不在账簿中记任何账 —— 金额须保持与已收款完全一致:{{paid}},VAT {{paidVat}}。',
  p_cdoc_taxfor_fill: '使用 {{docNo}} 的明细行',
  p_taxfor_err_type: '只有税务发票才能为已收款的发票开具 —— 请把单据类型选为「税务发票」。',
  p_taxfor_err_not_vat: '只有已登记增值税、开具泰国税务单据的公司才开税务发票 —— 没有可为已收款发票开具的税务发票。',
  p_taxfor_err_not_found: '在本公司的单据中找不到这张税务发票所对应的已收款发票。',
  p_taxfor_err_void: '{{docNo}} 已作废 —— 它背后没有已收款的销售可供开具税务发票。',
  p_taxfor_err_not_invoice: '{{docNo}} 不是普通发票 —— 不再记账的税务发票只能为其收据已入账的普通发票开具。',
  p_taxfor_err_plain: '{{docNo}} 是普通(非泰国)单据，不是泰国发票 —— 它的收据没有记录可供税务发票对应的泰国销售。',
  p_taxfor_err_customer: '{{docNo}} 开给的是 {{customer}} —— 为它开的税务发票必须开给同一客户。',
  p_taxfor_err_unpaid_part: '{{docNo}} 尚未全额收款 —— 已收 {{received}}/{{total}}({{receiptNos}}),只有部分销售已入账。请先在 {{docNo}} 上登记剩余收款，再开它的税务发票。',
  p_taxfor_err_unpaid_none: '{{docNo}} 尚未收款 —— 没有任何收据记录它的销售，账簿中没有可供这张税务发票对应的内容。',
  p_taxfor_err_adjusted: '{{docNo}} 或其收据已被 {{nos}} 调整 —— 账簿中记录的销售已不等于当初的收款，税务发票不能再对应它。',
  p_taxfor_err_taken: '已为 {{docNo}} 开过税务发票 {{tivNo}} —— 一张已收款发票只对应一张这样的税务发票。如需重开，请先作废 {{tivNo}}。',
  p_taxfor_err_currency: '{{docNo}} 以 {{currency}} 收款 —— 它的税务发票也须以 {{currency}} 开具。',
  p_taxfor_err_amount: '{{docNo}} 已收 {{paid}},VAT {{paidVat}}({{receiptNos}});本税务发票为 {{total}},VAT {{vat}}。为已收款发票开的税务发票不再记账，所以金额必须与已收款完全一致 —— 请把明细调整为 {{paid}},VAT {{paidVat}}。',
  p_taxfor_err_not_chain: '所选发票不是由报价单 {{quoteNo}} 开出的 —— 转换只能对应本报价单下已收款的发票。',
  p_taxfor_err_choose: '由报价单 {{quoteNo}} 开出的发票 {{docNos}} 都已全额收款 —— 请选择本税务发票对应哪一张。',
  p_taxfor_err_before: '这张税务发票的日期是 {{date}}，早于 {{earliest}} —— {{docNo}} 的日期是 {{invoiceDate}}，其款项于 {{paidDate}} 到账（{{receiptNos}}）。为已收款发票开的税务发票，日期不得早于这两者：请把日期定在 {{earliest}} 或之后。',
  p_taxfor_err_closed: '日期 {{date}} 落在已结账的会计期间（{{from}} – {{to}}）内 —— 为 {{docNo}} 开的税务发票不能定在该期间。它虽然不在账簿中记任何内容，但已结账的期间不开具任何税务发票。',
  p_taxfor_err_filed: '{{ym}} 的增值税已在申报中心标记为已申报 —— 为 {{docNo}} 开、日期为 {{date}} 的税务发票，会成为申报表已提交月份里的一张新税务发票。它虽然不在账簿中记任何内容，但已申报的月份不再开具税务发票：请先在申报中心取消 {{ym}} 的已申报标记，再开具。',
  p_crec_err_tax_for: '税务发票 {{invoiceNo}} 是为发票 {{forNo}} 开的，该发票已由 {{receiptNos}} 收款 —— 它没有未收金额，在此开收据会把同一笔收款记两次。',
  p_note_err_debit_rebilled: '{{docNo}} 已全额冲销，现由 {{nos}} 为 {{quoteNo}} 开票。{{docNo}} 已不再代表这笔销售，在它上面开增额单会与 {{nos}} 并存。请改在 {{nos}} 上开增额单。',
  p_note_err_debit_rebilled_invoice: '{{docNo}} 已全额冲销，现由 {{nos}} 为 {{quoteNo}} 开票。{{docNo}} 已不再代表这笔销售，在它上面开增额单会与 {{nos}} 并存。请改动 {{nos}}：直接编辑，或待其收款后在其收据上开增额单。',
  p_note_err_tax_for: '税务发票 {{docNo}} 是为发票 {{forNo}} 开的（已由 {{receiptNos}} 收款），本身不在账簿中记账 —— 这笔销售是通过 {{receiptNos}} 入账的。这笔销售的折让单或增额单针对该收据开具，且须在 {{docNo}} 作废之后：请先作废 {{docNo}}。',
  p_doc_void_tax_for_first: '{{docNo}} 支付了发票 {{invoiceNo}},而已为该发票开了税务发票 {{tivNo}} —— {{tivNo}} 不记账，正是因为这笔收款已入账。请先作废 {{tivNo}}。',
  p_note_err_tax_for_first_receipt: '{{docNo}} 支付了发票 {{invoiceNo}}，而已为该发票开了税务发票 {{tivNo}} —— {{tivNo}} 对应这笔销售并载明其增值税，所以在 {{tivNo}} 有效期间，不能用折让单或增额单改动这笔销售。请先作废 {{tivNo}}，再针对 {{docNo}} 开具。',
  p_note_err_tax_for_first_invoice: '发票 {{docNo}} 已由 {{receiptNos}} 收款，并已为其开了税务发票 {{tivNo}} —— 在 {{tivNo}} 有效期间，不能用折让单或增额单改动这笔销售。请先作废 {{tivNo}}，再针对其收据（{{receiptNos}}）开具 —— 普通发票本身不用折让单或增额单调整。',
  p_conv_taxfor_for: '本税务发票将对应 {{docNo}} / {{receiptNos}}:由本报价单开出的 {{docNo}} 已收款({{paid}},VAT {{paidVat}}),其销售已通过 {{receiptNos}} 入账。因此本税务发票不再记账，并注明对应 {{docNo}} / {{receiptNos}}。',
  p_conv_taxfor_choose: '由本报价单开出、并已全额收款的发票不止一张。本税务发票对应哪一张?它不再记账，并会注明那张发票及其收据。',
  p_conv_taxfor_choose_ph: '— 请选择已收款的发票 —',
  p_doc_void_dn_credited: '针对 {{docNo}} 的折让单({{nos}})已冲回 {{dnNo}} 增加的部分金额 —— 作废它会使已冲减金额超过原开金额。请先作废这些折让单。',
  p_doc_void_credit_rebilled: '{{noteNo}} 已冲销 {{docNo}}，现由 {{nos}} 为 {{quoteNo}} 开票。作废它会让 {{docNo}} 恢复，与 {{nos}} 并存，同一笔销售记两次。请先作废 {{nos}}。',
  p_doc_void_credit_rejected: '{{noteNo}} 已冲销 {{docNo}}，而 {{quoteNo}} 已标记为已拒绝。作废它会让已拒绝的报价单上出现一张有效单据。请先更改其状态。',
  p_doc_void_receipt_credited: '作废 {{docNo}} 会使{{docWord}} {{invoiceNo}} 重新欠款 {{outstanding}},而其收据 {{receiptNos}} 上的折让单 {{nos}} 仍然有效 —— 请先作废该折让单。',
  p_note_err_receipt_bill_owes: '{{docWord}} {{invoiceNo}} 仍有 {{outstanding}} 未收,所以不能对其收据 {{receiptNo}} 开折让单 —— 只有收据所结算的单据全部收齐后,才能对该收据开折让单。如需在收齐前调整 {{invoiceNo}} 的价格,请作废其收据后编辑 {{invoiceNo}}。',
  p_note_err_receipt_debit_owes: '增额单 {{dnNo}}(对收据 {{receiptNo}} 开具)仍有 {{outstanding}} 未收,所以不能对 {{receiptNo}} 开折让单 —— 只有对该收据开具的增额单全部收齐后,才能对该收据开折让单。',
  // 加价第五轮(2026-10-27):折让单日期早于其所冲减的单据(第 1 项);手工行超出非 VAT 申报栏的余额(第 2 项)。
  p_note_err_before_source: '本折让单日期为 {{noteDate}},早于其所冲减的{{docWord}} {{docNo}}(日期 {{date}})—— 折让单的日期不能早于它所冲减的任何单据。请将日期定为 {{date}} 或之后。',
  p_note_err_cat_left: '{{docNo}} 按「{{category}}」还剩 {{left}} 可冲减;本折让单手工填写的行要按「{{category}}」冲减 {{takes}}。折让单只能按原开具的 VAT 类别冲回,所以不能再按「{{category}}」冲减更多。',
  p_note_err_cat_left_dn: '{{docNo}} 及其增额单({{dnNos}})按「{{category}}」还剩 {{left}} 可冲减;本折让单手工填写的行要按「{{category}}」冲减 {{takes}}。折让单只能按原开具的 VAT 类别冲回,所以不能再按「{{category}}」冲减更多。',
  p_note_box_zero: '零税率 VAT 0%', p_note_box_exempt: '免税', p_note_box_outscope: '不计税(按成本代垫)', p_note_box_none: '无 VAT',
  p_note_err_none_flat_vat: '本折让单要冲回 {{docNos}} 记在 7% 税基栏且 VAT 为 0 的无 VAT 金额 {{amount}},同时又包含带 VAT 的行 —— 一张折让单无法把两者都准确冲回。请将 7% 的行单独开一张折让单,其余各行另开一张(该单不含 VAT)。',
  p_note_err_fill_unlinked: '已有不注明冲减哪一行的折让单({{nos}})冲减了 {{docNo}},因此无法得知其各行还剩多少,也不能再通过指向某一行来冲减。请用 × 删除从 {{docNo}} 填入的各行,再手工添加本折让单的各行。',
  p_note_err_box_locked: '第 {{n}} 行冲减的是 {{docNo}} 的「{{line}}」,该行原按「{{box}}」开具。折让单须按原开具的方式冲回,因此该行的收费性质须保持带出时的选择 —— VAT 申报表各栏之间不能挪移金额。',
  p_note_charge_locked_title: '从被冲减单据带出的行保留其原开具时的收费性质 —— 折让单按原样冲回。',
  p_note_err_cat_left_out: '{{docs}}:按「无 VAT」还剩 {{left}} 可冲减;本折让单手工填写的行要按「无 VAT」冲减 {{takes}}。另有 {{sibLeft}} 尚可冲减的无 VAT 金额,原开具时是替客户按原价垫付的款项(不计税)—— 冲减这部分时,请在手工填写的行上把收费性质选为「{{charge}}」。',
  p_note_err_cat_left_own: '{{docs}}:按「不计税(按成本代垫)」还剩 {{left}} 可冲减;本折让单手工填写的行要按此冲减 {{takes}}。另有 {{sibLeft}} 尚可冲减的无 VAT 金额,原开具时并非替客户垫付的款项 —— 冲减这部分时,请在手工填写的行上把收费性质选为「{{charge}}」。',
  p_doc_void_dn_drawn: '针对 {{docNo}} 的折让单({{nos}})冲回了 {{dnNo}} 按「{{box}}」开具的金额 —— 作废它会使按「{{box}}」冲减的金额超过原开具的金额。请先作废这些折让单。',
  p_doc_void_dn_drawn_later: '针对 {{docNo}} 的折让单({{nos}})有部分金额落在 {{dnNo}} 上 —— 作废它会使这部分转到增额单 {{nextNo}}(日期 {{date}}),晚于折让单 {{cnNo}}(日期 {{cnDate}}),而折让单不能冲减日期在其之后的单据。请先作废这些折让单。',
  p_doc_void_receipt_debit_credited: '作废 {{docNo}} 会使增额单 {{invoiceNo}} 重新欠款 {{outstanding}},而该增额单所调整的收据 {{receiptNos}} 上的折让单 {{nos}} 仍然有效 —— 请先作废该折让单。',
  p_doc_void_note_held: '{{docNo}} 须待{{noteWord}} {{noteNo}} 作废后才能作废,而 {{noteNo}} 目前无法作废:{{reason}}',
  p_note_type: '类型 *',
  // pager
  p_pager_first: '首页',
  p_pager_last: '末页',
  p_pager_next: '下一页',
  p_pager_no_records: '无记录',
  p_pager_page_of: '第 {{page}} 页 / 共 {{pages}}',
  p_pager_per_page: '每页',
  p_pager_prev: '上一页',
  p_pager_showing: '显示 {{from}}–{{to}} / 共 {{total}}',
  // pdf
  p_pdf_failed: 'PDF 生成失败',
  // prod
  p_prod_add: '+ 添加项目',
  p_prod_confirm_remove: '确定删除 {{name}}?',
  p_prod_empty: '暂无项目,请添加您销售的商品或服务。',
  p_prod_f_name: '名称 *',
  p_prod_f_price: '单价({{cur}})',
  p_prod_f_unit: '单位(如 个、小时)',
  p_prod_f_vat_checkbox: '适用 7% VAT',
  p_prod_f_wht: '默认预扣税(WHT)',
  p_prod_f_wht_hint: '商品通常 0%;服务 3%;租金 5%。买方代扣后会向您出具 50 ทวิ 凭证。',
  p_prod_modal_edit: '编辑项目',
  p_prod_modal_new: '新增项目',
  p_prod_photo_hint: '显示在商品目录中',
  p_prod_photo_label: '图片(选填)',
  p_prod_search_ph: '搜索名称或单位…',
  p_prod_th_name: '名称',
  p_prod_th_price: '价格({{cur}})',
  p_prod_th_unit: '单位',
  p_prod_th_vat: 'VAT',
  p_prod_title: '我的商品与服务',
  p_prod_vat_yes: '{{rate}}%',
  // prof
  p_prof_address: '单据上的地址',
  p_prof_biz_goods: '货物 — 交付时即产生 VAT 纳税义务,税务发票随货开具 (s.78)',
  p_prof_biz_hint: '此项仅决定新建单据时的默认类型,每份单据仍可单独更改。',
  p_prof_biz_mixed: '商品与服务兼营',
  p_prof_biz_service: '服务业 —— 客户付款时产生 VAT 纳税义务(s.78/1)',
  p_prof_biz_type: '经营类型',
  p_prof_company_name: '公司名称(锁定)',
  p_prof_default_wht: '新明细行默认预扣税',
  p_prof_default_wht_hint: '当明细行未选目录项目时适用。',
  p_prof_doc_cn: '折让单',
  p_prof_doc_dn: '增额单',
  p_prof_doc_inv: '发票',
  p_prof_doc_pro: '形式发票',
  p_prof_doc_quo: '报价单',
  p_prof_doc_rec: '收据',
  p_prof_doc_tax: '税务发票',
  p_prof_email: '邮箱',
  p_prof_failed: '失败',
  p_prof_identity_note: '身份信息依据贵公司文件核验,仅可由我方团队修改。',
  p_prof_logo_hint: '打印在发票、报价单和收据左上角',
  p_prof_logo_label: '公司 Logo',
  p_prof_month_seg: '单据编号中包含年月',
  p_prof_month_seg_hint: '— 开启:INV-{{yyyymm}}-001 · 关闭:INV-001。如果您自己的编号已包含年月,请关闭此项。',
  p_prof_numbering: '单据编号',
  p_prof_numbering_note: '— 编号连续性由您负责(已于 {{date}} 确认)',
  p_prof_phone: '电话',
  p_prof_prefix_next: '{{docType}} 前缀 / 下一号码',
  p_prof_pw_changed: '密码已修改 ✓',
  p_prof_saved: '已保存 ✓',
  p_prof_branch: '分支机构(不可修改)',
  p_prof_branch_head: '总公司',
  p_prof_branch_hint: '开具税务发票、折让单、增额单时，打在税号旁边。',
  p_prof_branch_n: '分支机构',
  p_prof_tax_id: '税号(不可修改)',
  p_prof_title: '公司资料',
  p_prof_vat_hint: '已登记 ⇒ 发票加收 7% VAT,收据即为税务发票。',
  p_prof_vat_not_registered: '未登记',
  p_prof_vat_reg: 'VAT 登记',
  p_prof_vat_registered: '已登记(7% VAT)',
  p_prof_website: '网站',
  // pw
  p_pw_confirm: '确认新密码',
  p_pw_current: '当前密码',
  p_pw_err_current_required: '请输入当前密码。',
  p_pw_err_failed: '失败',
  p_pw_err_min_length: '至少 8 位字符。',
  p_pw_err_mismatch: '两次输入的密码不一致。',
  p_pw_hint_current: '需填写当前密码。',
  p_pw_hint_first: '为安全起见,请更换临时密码。',
  p_pw_new: '新密码',
  p_pw_title_change: '修改密码',
  p_pw_title_first: '设置您的密码',
  // row
  p_row_delete: '删除',
  p_row_edit: '编辑',
  // search
  p_search_clear: '清除',
  // select
  p_select_placeholder: '— 请选择 —',
  p_pick_search: '输入以搜索…',
  p_pick_none: '没有匹配 — 换个关键词试试。',
  p_pick_more: '显示 {{shown}} / 共 {{total}} — 输入关键词可搜索其余。',
  p_cust_add_title: '新增客户',
  p_cust_add_save: '新增并选中',
  p_cust_name_req: '客户名称必填。',
  p_cust_add_failed: '新增客户失败。',
  p_prof_f_prefix: '前缀',
  p_prof_f_next: '下一个号',
  p_prof_f_date: '编号中的日期',
  p_prof_f_reset: '重新开始',
  p_prof_f_pad: '位数',
  p_prof_date_none: '不含日期',
  p_prof_reset_none: '不重置',
  p_prof_reset_year: '每年',
  p_prof_reset_month: '每月',
  p_prof_reset_day: '每日',
  p_prof_scheme_bad: '编号里必须含 {{need}},否则下个周期会出现重复编号。',
  p_prof_numbering_hint: '编号 = 前缀 · 日期 · 流水号。要让流水号重新开始,编号里的日期必须能区分周期:每年需含 YYYY,每月需含 YYYYMM,每日需含 YYYYMMDD。改动只影响此后新开的单据,已开出的单据编号一律不变。',
  // 公司自己的费用记录号 / 付款号格式(老板 2026-10-28,EXP-8):单据系列下面的两行。
  p_regno_head: '费用记录与供应商付款',
  p_regno_exp: '费用记录',
  p_regno_pmt: '供应商付款',
  p_regno_po: '采购订单',
  p_regno_claim: '报销单',
  p_regno_f_sep: '分隔符',
  p_regno_sep_dash: '用“-”',
  p_regno_sep_none: '不分隔',
  p_regno_hint: '每条费用记录、每笔供应商付款在建立时取号；号码的样式由您在这里决定。已发出的号码永远不变。下一个号只能往上调。改了格式后，新格式从为它显示的下一个号开始——除非您选更大的号，否则从 1 开始；以前用过的格式接着它已发出的号码往下编——已用过的号码（包括作废、删除的记录）绝不会再发出。已发出号码所用的前缀仍归原系列。您选定的下一个号一直有效到流水号下一次重新起号为止。',
  p_regno_err_prefix_empty: '前缀必须是 1–8 位字母或数字。',
  p_regno_err_prefix_digit_end: '不用分隔符时，前缀必须以字母结尾——“{{prefix}}”会和日期或流水号连在一起分不开。',
  p_regno_err_prefix_taken: '{{prefix}} 已经是{{other}}的前缀——每个系列要有自己的前缀，号码才能看出是什么单据。',
  p_regno_err_prefix_held: '已发出的{{other}}号码以 {{prefix}} 开头——每个系列要有自己的前缀，号码才能看出是什么单据。',
  p_regno_err_scheme_bad: '这个格式无效。',
  p_regno_err_start_bad: '下一个号必须是 1 到 {{max}} 之间的整数。',
  p_regno_err_start_down: '下一个号已经是 {{next}}——只能往上调。',
  p_regno_checking: '正在检查…',
  p_regno_failed: '无法检查这个格式，请重试。',
  p_flt_all: '全部',
  p_flt_custom: '自定义',
  p_flt_clear: '清除日期筛选',
  p_flt_period: '期间',
  p_flt_show_voided: '显示已作废',
  p_imp_open_short: '导入',
  p_menu_notes_hint: '更正已开出的单据',
  p_flt_this_month: '本月',
  p_flt_last_month: '上月',
  p_flt_from: '起始日期',
  p_flt_to: '结束日期',
  // send
  p_send_attachment: '附件',
  p_send_bcc: '抄送给您',
  p_send_btn: '发送邮件',
  p_send_done: '完成',
  p_send_err_prepare: '无法准备邮件。',
  p_send_err_send: '邮件发送失败。',
  p_send_from: '发件人',
  p_send_history: '发送记录',
  p_send_message: '正文',
  p_send_no_replyto: '— 请先在「公司资料」中填写公司邮箱 —',
  p_send_preparing: '准备中…',
  p_send_preview: '预览',
  p_send_preview_hint: '打开客户实际将收到的 PDF — 正本与副本两联。',
  p_send_replyto: '回复至',
  p_send_save_addr: '将此邮箱保存到 {{customerName}},下次自动填入',
  p_send_sending: '发送中…',
  p_send_sent_ok: '✓ 已发送至 {{email}},副本已存入您的收件箱。',
  p_send_subject: '主题',
  p_send_subtitle: 'PDF 会自动作为附件发送。客户的回复将直接回到您这里。',
  p_send_title: '发送 {{docNo}}',
  p_send_to_label: '客户邮箱 *',
  p_send_to_ph: 'name@example.com',
  // sort
  p_sort_th_tooltip: '按 {{label}} 排序',
  // vat
  p_vat_hint_exempt: '免征 VAT 业务(s.81)。',
  p_vat_hint_none: '不属 VAT 范围 / 非 VAT 登记人。',
  p_vat_hint_standard: 'VAT 在您填写的价格之外另加。',
  p_vat_hint_standard_incl: '所填价格已含 VAT。',
  p_vat_hint_zero: '零税率(s.80/1)—— 按 0% 计税,与免税不同。',
  p_vat_opt_exempt: 'VAT 免征',
  p_vat_opt_none: '无 VAT',
  p_vat_opt_standard: 'VAT {{rate}}% — 价外',
  p_vat_opt_standard_incl: 'VAT {{rate}}% — 价内含税',
  p_vat_opt_zero: 'VAT 0%',
  // wht
  p_wht_none: '无预扣税',
  p_wht_rate: '预扣税 {{rate}}%',
  p_wht_select_title: '买方代扣的预扣税',
};

export const portalTh: Record<string, string> = {
  // admin
  p_admin_title: '{{brand}} Client Portal — ผู้ดูแลระบบ',
  // bank
  p_bank_acct_name: 'ชื่อบัญชี',
  p_bank_acct_no: 'เลขที่บัญชี',
  p_bank_add: '+ เพิ่มบัญชีธนาคาร',
  p_bank_address: 'ที่อยู่ธนาคาร',
  p_bank_address_hint: '— จำเป็นสำหรับการโอนเงินจากต่างประเทศ',
  p_bank_address_ph: '1 Bangkok Blvd, Pathum Wan, Bangkok 10330, Thailand',
  p_bank_confirm_remove: 'ลบบัญชีธนาคารนี้หรือไม่?',
  p_bank_currency: 'สกุลเงิน',
  p_bank_empty: 'ยังไม่มีบัญชีธนาคาร — เพิ่มบัญชีเพื่อให้พิมพ์ลงบนเอกสารของท่าน',
  p_bank_name: 'ธนาคารและสาขา',
  p_bank_name_ph: 'Kasikornbank (KBank), One Bangkok Branch',
  p_bank_no_swift: '⚠ ไม่มี SWIFT / ที่อยู่ธนาคาร — ผู้ชำระเงินจากต่างประเทศจะโอนเข้าบัญชีนี้ไม่ได้',
  p_bank_note: 'หมายเหตุ (ไม่บังคับ)',
  p_bank_note_ph: 'เช่น ใช้กับใบแจ้งหนี้สกุล USD เท่านั้น',
  p_bank_qr_alt: 'QR',
  p_bank_qr_hint: 'PromptPay / QR ธนาคาร — พิมพ์ในช่องการชำระเงินบนใบแจ้งหนี้และใบเสนอราคา',
  p_bank_qr_label: 'QR รับชำระเงิน',
  p_bank_qr_title: 'QR รับชำระเงินที่พิมพ์บนเอกสาร',
  p_bank_swift: 'SWIFT / BIC',
  p_bank_swift_ph: 'KASITHBK',
  p_bank_title: 'บัญชีธนาคารที่แสดงบนเอกสาร',
  // bills
  p_bills_loading: 'กำลังโหลด…',
  p_bills_open_pdf: 'เปิด PDF',
  p_bills_subtitle: 'ใบเสนอราคา ใบแจ้งหนี้ และใบเสร็จรับเงินที่ {{brand}} ออกให้ท่าน · เอกสารที่ {{brand}} ออกให้ท่าน',
  p_bills_tab_invoices: 'ใบแจ้งหนี้',
  p_bills_tab_quotations: 'ใบเสนอราคา',
  p_bills_tab_receipts: 'ใบเสร็จรับเงิน',
  p_bills_th_date: 'วันที่',
  p_bills_th_due: 'ครบกำหนด',
  p_bills_th_issued_by: 'ออกโดย',
  p_bills_th_net: 'ยอดสุทธิ',
  p_bills_th_no: 'เลขที่',
  p_bills_th_paid_on: 'วันที่ชำระ',
  p_bills_th_status: 'สถานะ',
  p_bills_th_total: 'รวม ($t(cur_word))',
  p_bills_th_valid_until: 'ยืนราคาถึง',
  p_bills_title: 'ใบวางบิลจาก {{brand}}',
  p_menu_group_settings: 'ตั้งค่า',
  p_menu_files: 'เอกสาร', p_menu_files_hint: 'เอกสารทั้งหมดที่เราเก็บไว้ให้ท่าน รวมอยู่ที่เดียว',
  p_files_title: 'เอกสารของท่าน',
  p_files_subtitle: 'เอกสารทั้งหมดที่ {{brand}} เก็บไว้ให้ท่าน — แบบที่ยื่น ใบเสร็จกรมสรรพากร เอกสารบริษัท และเอกสารที่ท่านส่งมา',
  p_files_loading: 'กำลังโหลด…', p_files_none: 'ยังไม่มีเอกสาร', p_files_all: 'ทั้งหมด',
  p_files_search: 'ค้นหาเอกสาร…',
  p_files_name: 'เอกสาร', p_files_folder: 'ประเภท', p_files_size: 'ขนาด', p_files_added: 'เพิ่มเมื่อ',
  p_files_view: 'ดู', p_files_download: 'ดาวน์โหลด',
  p_files_f_tax: 'ภาษีและการยื่น', p_files_f_company: 'เอกสารบริษัท', p_files_f_uploads: 'ท่านส่งให้เรา',
  p_files_f_deliverables: 'ส่งมอบให้ท่าน', p_files_f_workflow: 'เอกสารระหว่างบริการ', p_files_f_library: 'อื่น ๆ',
  p_files_elsewhere: 'ใบแจ้งหนี้และใบเสร็จจาก {{brand}} อยู่ในเมนูใบวางบิลจาก {{brand}} ส่วนแบบที่ยื่นดูได้ที่การยื่นภาษีเช่นกัน',
  p_nav_finance: 'กำไรขาดทุน',
  p_fin_title: 'งบกำไรขาดทุน {{year}}',
  p_fin_subtitle: 'จากข้อมูลที่บันทึกจริงในบัญชีของท่าน ไม่มีการประมาณการและไม่มีการคาดการณ์ล่วงหน้า',
  p_fin_loading: 'กำลังโหลด…',
  p_fin_no_data: 'ยังไม่มีข้อมูลบันทึกสำหรับปีนี้',
  p_fin_revenue: 'รายได้',
  p_fin_expenses: 'ค่าใช้จ่าย',
  p_fin_purchases: 'ซื้อ',
  p_fin_salaries: 'ค่าจ้าง',
  p_fin_sso: 'ประกันสังคม (ส่วนนายจ้าง)',
  p_fin_pnd3: 'ค่าบริการจ่ายให้บุคคลธรรมดา',
  p_fin_rent53: 'ค่าเช่า',
  p_fin_expenses_total: 'รวมค่าใช้จ่าย',
  p_fin_net: 'สุทธิ',
  p_fin_by_month: 'แยกรายเดือน',
  p_fin_month: 'เดือน',
  p_fin_no_payroll: 'ยังไม่ได้จัดทำเงินเดือน',
  p_fin_incomplete: 'ยังไม่ได้จัดทำเงินเดือนของเดือน {{months}} ค่าจ้างและประกันสังคมจึงแสดงเป็นศูนย์ ตัวเลขเดือนเหล่านี้ยังไม่ใช่ตัวเลขสุดท้าย',
  p_fin_note: 'รายได้และยอดซื้อเป็นจำนวนก่อน VAT ประกันสังคมแสดงเฉพาะส่วนของนายจ้าง ส่วนของพนักงานหักจากเงินเดือนและไม่ใช่ต้นทุนเพิ่มของบริษัท ค่าบริการที่จ่ายให้นิติบุคคลรวมอยู่ในยอดซื้อแล้ว จึงแยกแสดงเฉพาะที่จ่ายให้บุคคลธรรมดา',
  p_fin_unposted: 'เอกสารบางรายการของเดือน {{months}} ยังไม่ได้บันทึกเข้าสมุดบัญชี ตัวเลขเดือนเหล่านี้จึงยังไม่ใช่ตัวเลขสุดท้าย',
  p_fin_not_posted: 'ยังไม่ได้บันทึกบัญชี',
  p_fin_note_books: 'จากรายการที่บันทึกในสมุดบัญชีของท่าน แยกเดือนตามวันที่ของรายการ เป็นรายการชุดเดียวกับที่ใช้จัดทำงบกำไรขาดทุนในเมนูงบการเงิน',
  p_fin_outside: 'มีเอกสารที่บันทึกไว้ของเดือน {{months}} ซึ่งไม่ได้ลงวันที่ หรือลงวันที่ไม่อยู่ในรอบระยะเวลาบัญชีใดของท่าน จึงไม่ได้รวมอยู่ในตัวเลขเหล่านี้',
  p_fin_outside_short: 'อยู่นอกรอบระยะเวลาบัญชี',
  p_fin_no_voucher: 'บันทึก ภ.ง.ด.1 ประกันสังคม ภ.ง.ด.3 หรือค่าเช่าที่จ่ายให้ผู้ให้เช่าที่เป็นนิติบุคคล (ภ.ง.ด.53) ของเดือน {{months}} มีค่าจ้าง ค่าบริการ หรือค่าเช่า แต่สมุดบัญชีของท่านไม่มีรายการที่ลงวันที่ในเดือนเหล่านั้นซึ่งบันทึกจำนวนเงินดังกล่าวเป็นค่าใช้จ่าย หากบันทึกไว้ในเดือนอื่น จำนวนเงินดังกล่าวจะรวมอยู่ในตัวเลขของเดือนนั้น หากไม่ได้บันทึกไว้ ก็ไม่ได้รวมอยู่ในตัวเลขเหล่านี้',
  p_fin_no_voucher_short: 'ไม่มีรายการค่าจ้าง ค่าบริการ หรือค่าเช่าที่ลงวันที่ในเดือนนี้',
  p_fin_rental_no_voucher: 'ท่านมีสัญญาเช่ากับผู้ให้เช่าที่เป็นนิติบุคคลที่มีผลอยู่ แต่เดือน {{months}} ไม่มีบันทึกค่าเช่า (ภ.ง.ด.53) ของสัญญานี้ และสมุดบัญชีของท่านไม่มีรายการที่ลงวันที่ในเดือนเหล่านั้นซึ่งบันทึกค่าเช่าเป็นค่าใช้จ่าย หากค่าเช่าของเดือนเหล่านั้นบันทึกไว้ในเดือนอื่น จะรวมอยู่ในตัวเลขของเดือนนั้น หากไม่ได้บันทึกไว้ ก็ไม่ได้รวมอยู่ในตัวเลขเหล่านี้',
  p_fin_rental_no_voucher_short: 'สัญญาเช่ามีผล: ไม่มีบันทึกหรือรายการค่าเช่าในเดือนนี้',
  p_fin_closed_unposted: 'เอกสารบางรายการที่บันทึกไว้ของเดือน {{months}} ไม่อยู่ในสมุดบัญชีที่ปิดบัญชีแล้วของรอบระยะเวลาบัญชีนั้น จึงไม่ได้รวมอยู่ในตัวเลขเหล่านี้',
  p_fin_closed_unposted_short: 'บันทึกไว้แล้ว แต่ไม่อยู่ในสมุดบัญชีที่ปิดแล้ว',
  p_nav_calendar: 'ปฏิทิน',
  p_cal_title: 'สิ่งที่กำลังจะถึงกำหนด',
  p_cal_subtitle: 'กำหนดยื่นภาษีของท่าน และงานของทั้งสองฝ่าย รายการที่ระบุว่า "ท่าน" คือรอท่านดำเนินการ',
  p_cal_loading: 'กำลังโหลด…', p_cal_nothing: 'ไม่มีรายการในช่วงนี้',
  p_cal_all_year: 'ทั้งปี', p_cal_today: 'วันนี้', p_cal_you: 'ท่าน',
  p_cal_only_mine: 'เฉพาะของท่าน ({{count}})', p_cal_yours_count: 'รอท่าน {{count}} รายการ',
  p_cal_is_filing: 'กำหนดยื่น', p_cal_done: 'เสร็จแล้ว', p_cal_blocked: 'ติดขัด',
  p_cal_k_pp30: 'ภาษีมูลค่าเพิ่ม (ภ.พ.30)', p_cal_k_pnd1: 'ภ.ง.ด.1', p_cal_k_pnd3: 'ภ.ง.ด.3', p_cal_k_pnd53: 'ภ.ง.ด.53',
  p_cal_k_sso: 'ประกันสังคม', p_cal_k_pnd51: 'ภ.ง.ด.51 (ครึ่งปี)', p_cal_k_pnd50: 'ภ.ง.ด.50 (ประจำปี)',
  p_cal_k_pnd90: 'ภาษีเงินได้บุคคลธรรมดา', p_cal_k_audit: 'ตรวจสอบบัญชี', p_cal_k_annual_fs: 'งบการเงินประจำปี',
  p_cal_k_bookkeeping: 'ทำบัญชี', p_cal_k_property_tax: 'ภาษีที่ดินและสิ่งปลูกสร้าง',
  p_cal_k_invoice_data_upload: 'ส่งใบกำกับภาษีให้เรา', p_cal_k_tax_payment: 'ชำระภาษี',
  p_cal_k_service_payment: 'ชำระค่าบริการ', p_cal_k_service_invoice: 'ใบแจ้งหนี้จากเรา',
  p_cal_k_tax_remit: 'เรานำส่งภาษีให้ท่าน', p_cal_k_lead_quotation: 'ใบเสนอราคา',
  p_cal_k_lead_contract: 'สัญญา', p_cal_k_lead_collect_info: 'ส่งข้อมูลให้เรา',
  p_doc_got_bill: 'ได้รับใบแจ้งหนี้จากผู้ขายหรือไม่',
  p_doc_got_receipt: 'มีใบเสร็จจากที่ท่านชำระไปหรือไม่',
  p_doc_send_to_slf: 'ส่งให้เราอ่านและบันทึกบัญชี →',
  p_nav_hr: 'พนักงาน',
  p_hr_title: 'พนักงานของท่าน',
  p_hr_subtitle: 'รายชื่อพนักงานและเงินสมทบประกันสังคม หากต้องการแก้ไข โปรดส่งคำขอมา เราจะดำเนินการให้',
  p_hr_loading: 'กำลังโหลด…',
  p_hr_new_request: 'ส่งคำขอแก้ไข',
  p_hr_pending_note: 'มีคำขอ {{count}} รายการรอ {{brand}} ตรวจสอบ',
  p_hr_name: 'ชื่อ', p_hr_position: 'ตำแหน่ง', p_hr_salary: 'เงินเดือน',
  p_hr_joined: 'เริ่มงาน', p_hr_status: 'สถานะ', p_hr_nationality: 'สัญชาติ',
  p_hr_no_staff: 'ยังไม่มีข้อมูลพนักงาน',
  p_hr_active: 'ทำงานอยู่', p_hr_left: 'ลาออกแล้ว', p_hr_not_in_sso: 'ไม่อยู่ในประกันสังคม',
  p_hr_joined_this_month: 'เริ่มงานเดือนนี้',
  p_hr_left_this_month: 'ลาออกเดือนนี้',
  p_hr_change_salary: 'ปรับเงินเดือน', p_hr_leaving: 'ลาออก',
  p_hr_sso_title: 'ประกันสังคมเดือนนี้',
  p_hr_sso_how: 'ทั้งสองฝ่ายจ่ายฝ่ายละ {{rate}}% ของเงินเดือน เงินเดือนต่ำกว่า {{floor}} คิดที่ {{floor}} และแต่ละฝ่ายสูงสุด {{cap}} เกินจากนั้นเงินสมทบจะไม่เพิ่มขึ้น ส่วนของพนักงานหักจากเงินเดือน นายจ้างจ่ายสมทบเท่ากัน เดือนใดที่มีการจ้างงานแม้เพียงวันเดียวจะนับทั้งเดือน ทั้งเดือนที่เริ่มงานและเดือนที่ลาออก',
  p_hr_check_these: 'ควรตรวจสอบเดือนนี้:',
  p_hr_mover_note: 'ผู้ที่เริ่มงานหรือลาออกระหว่างเดือนคือกรณีที่ตัวเลขอัตโนมัติมักต้องให้คนตรวจ หากจำนวนควรต่างจากนี้ โปรดแจ้ง เราจะตั้งค่าด้วยมือ',
  p_hr_employee_side: 'พนักงาน', p_hr_employer_side: 'นายจ้าง', p_hr_total: 'รวม',
  p_hr_month_total: 'รวม · {{count}} คน', p_hr_manual: 'ตั้งด้วยมือ',
  p_hr_no_sso: 'เดือนนี้ไม่มีพนักงานที่ต้องส่งประกันสังคม',
  p_hr_snapshot: 'เดือนนี้คงที่แล้ว เป็นข้อมูลที่ยื่นไป',
  p_hr_preview: 'เดือนนี้ยังไม่ได้ยื่น คำนวณจากรายชื่อพนักงานปัจจุบันและอาจเปลี่ยนแปลงได้',
  p_hr_year_title: 'ประกันสังคมตลอดปี {{year}}',
  p_hr_month: 'เดือน', p_hr_headcount: 'จำนวนคน', p_hr_wages: 'ค่าจ้างรวม',
  p_hr_filed_snap: 'คงที่', p_hr_preview_short: 'ประมาณการ',
  p_hr_requests: 'คำขอของท่าน',
  p_hr_k_salary_change: 'ปรับเงินเดือน', p_hr_k_employee_add: 'พนักงานใหม่', p_hr_k_employee_leave: 'พนักงานลาออก',
  p_hr_s_pending: 'รอ {{brand}} ดำเนินการ', p_hr_s_applied: 'ดำเนินการแล้ว', p_hr_s_rejected: 'ไม่ได้ดำเนินการ',
  p_hr_request_title: 'ส่งคำขอแก้ไข',
  p_hr_request_note: 'การส่งคำขอยังไม่เปลี่ยนแปลงข้อมูลใด {{brand}} จะตรวจสอบก่อน เพราะประกันสังคมและ ภ.ง.ด.1 จะเปลี่ยนตาม',
  p_hr_kind: 'ประเภทการแก้ไข', p_hr_employee: 'พนักงาน', p_hr_pick_employee: 'เลือก…',
  p_hr_start_date: 'วันเริ่มงาน', p_hr_last_day: 'วันทำงานวันสุดท้าย',
  p_hr_new_salary: 'เงินเดือนใหม่', p_hr_effective_from: 'มีผลตั้งแต่',
  p_hr_note: 'ข้อมูลเพิ่มเติม', p_hr_cancel: 'ยกเลิก', p_hr_send: 'ส่งคำขอ', p_hr_sending: 'กำลังส่ง…',
  p_hr_sso_preview: 'ที่เงินเดือนนี้ ประกันสังคมฝ่ายพนักงาน {{employee}} และนายจ้างเท่ากัน รวมเดือนละ {{total}}',
  p_hr_err: 'ส่งไม่สำเร็จ โปรดลองอีกครั้ง',
  // ── PandaAI Tax (อ่านอย่างเดียว) ──
  p_nav_tax: 'ภาษี',
  p_tax_title: 'การยื่นภาษี',
  p_tax_subtitle: 'ภาษีทุกประเภทที่เรายื่นให้ท่าน: ที่มาของตัวเลข จำนวนเงิน ใครชำระอะไรแล้วบ้าง และเอกสารที่ยื่น',
  p_tax_loading: 'กำลังโหลด…',
  p_tax_pick: 'เลือกประเภทภาษีทางด้านซ้าย',
  p_tax_not_configured: '{{brand}} ไม่ได้ดำเนินการรายการนี้',
  p_tax_nothing_filed: 'ยังไม่มีการยื่น',
  p_tax_half_year: 'ยื่นปีละสองครั้ง',
  p_tax_annual: 'ยื่นปีละครั้ง',
  p_tax_calc: 'วิธีคำนวณ',
  p_tax_no_calc: 'ไม่มีรายละเอียดการคำนวณสำหรับงวดนี้',
  p_tax_amount: 'จำนวนที่ต้องชำระ',
  p_tax_you_paid_slf: 'ท่านชำระให้ {{brand}}',
  p_tax_slf_paid_rd: '{{brand}} นำส่งกรมสรรพากร',
  p_tax_settled: 'ชำระครบแล้ว',
  p_tax_outstanding: 'คงค้าง',
  p_tax_nothing_billed: 'ไม่มีการเรียกเก็บสำหรับงวดนี้',
  p_tax_remitted: 'นำส่งแล้ว',
  p_tax_not_remitted: 'ยังไม่ได้นำส่ง',
  p_tax_filed: 'ยื่นแบบแล้ว',
  p_tax_method: 'วิธีชำระเงิน',
  p_tax_method_na: 'ระบบไม่ได้บันทึกไว้',
  p_tax_docs: 'เอกสารที่ยื่น',
  p_tax_no_docs: 'ยังไม่มีเอกสารสำหรับงวดนี้',
  p_tax_l_out_base: 'ยอดขาย (ไม่รวม VAT)',
  p_tax_l_out_vat: 'ภาษีขาย',
  p_tax_l_in_base: 'ยอดซื้อ (ไม่รวม VAT)',
  p_tax_l_in_vat: 'ภาษีซื้อ',
  p_tax_l_net: 'ภาษีขาย − ภาษีซื้อ',
  p_tax_l_brought: 'เครดิตยกมา',
  p_tax_l_carry: 'เครดิตยกไปงวดหน้า',
  p_tax_l_surcharge: 'เงินเพิ่ม',
  p_tax_l_fines: 'ค่าปรับ',
  p_tax_l_headcount: 'จำนวนพนักงาน',
  p_tax_l_gross_wages: 'ค่าจ้างรวม',
  // ── บริการของ SLF (อ่านอย่างเดียว) ──
  p_menu_services: 'บริการของเรา',
  p_menu_services_hint: 'เราทำอะไร ค่าบริการเท่าไร และขั้นตอนเป็นอย่างไร',
  p_svc_title: 'บริการของ {{brand}}',
  p_svc_subtitle: 'รายการบริการทั้งหมด พร้อมราคาของท่านหากได้ตกลงไว้ กดที่บริการเพื่อดูขั้นตอน',
  p_svc_search: 'ค้นหาบริการ…',
  p_svc_loading: 'กำลังโหลด…',
  p_svc_none: 'ไม่พบบริการที่ตรงกัน',
  p_svc_yours: 'ของท่าน',
  p_svc_your_price: 'ราคาของท่าน',
  p_svc_list_price: 'ราคาตามรายการ',
  p_svc_how: 'ขั้นตอนของบริการนี้:',
  p_svc_steps_loading: 'กำลังโหลดขั้นตอน…',
  p_svc_no_steps: 'ยังไม่ได้บันทึกขั้นตอนของบริการนี้',
  p_svc_step_you: 'ท่าน',
  p_svc_step_slf: '{{brand}}',
  // ── งานที่ SLF ดำเนินการ (อ่านอย่างเดียว) ──
  p_nav_jobs: 'งาน',
  p_jobs_title: 'งานที่ {{brand}} ดำเนินการ',
  p_jobs_subtitle: 'งานที่เรากำลังดำเนินการให้ท่าน และสถานะของแต่ละงาน ดูได้อย่างเดียว',
  p_jobs_loading: 'กำลังโหลด…',
  p_jobs_all: 'ทั้งหมด',
  p_jobs_no: 'เลขที่',
  p_jobs_what: 'รายการ',
  p_jobs_status: 'สถานะ',
  p_jobs_due_date: 'กำหนด / เสร็จ',
  p_jobs_amount: 'จำนวนเงิน',
  p_jobs_paid: 'ชำระแล้ว',
  p_jobs_outstanding: 'คงค้าง',
  p_jobs_date_tbc: 'รอยืนยัน',
  p_jobs_waiting_on: 'รอ',
  p_jobs_opened: 'เปิดงาน',
  p_jobs_updated: 'อัปเดต',
  p_jobs_st_not_started: 'ยังไม่เริ่ม',
  p_jobs_st_in_progress: 'กำลังดำเนินการ',
  p_jobs_st_with_you: 'รอท่านดำเนินการ',
  p_jobs_st_with_authority: 'รอหน่วยงานราชการ',
  p_jobs_st_with_third_party: 'รอบุคคลภายนอก',
  p_jobs_st_done: 'เสร็จสิ้น',
  p_jobs_st_cancelled: 'ยกเลิก',
  // bs — bank statements the client sends in (owner 2026-10-13)
  p_bs_title: 'ส่งรายการเดินบัญชีธนาคารของท่านมาให้เรา',
  p_bs_subtitle: 'รายการเดินบัญชี (statement) ของบัญชีธนาคารของท่านเอง จากธนาคารโดยตรง {{brand}} จะจับคู่แต่ละรายการกับใบแจ้งหนี้และใบเสร็จของท่านด้วยมือ — การอัปโหลดที่นี่ไม่ได้เปลี่ยนแปลงบัญชีหรือแบบแสดงรายการภาษีของท่านโดยตัวมันเอง',
  p_bs_account: 'บัญชีธนาคาร',
  p_bs_account_auto: 'ตรวจหาจากไฟล์อัตโนมัติ',
  p_bs_no_accounts: 'ยังไม่มีบัญชีธนาคารในข้อมูลบริษัทของท่าน — กรุณาเพิ่มบัญชีที่หน้าข้อมูลบริษัทก่อน เพื่อให้ระบบจดจำรายการเดินบัญชีได้',
  p_bs_pw_label: 'รหัสผ่านไฟล์ PDF',
  p_bs_pw_ph: 'กรอกเฉพาะเมื่อธนาคารตั้งรหัสผ่านไฟล์',
  p_bs_pw_hint: 'ใช้เพื่อเปิดไฟล์นี้เท่านั้น ไม่มีการจัดเก็บ',
  p_bs_upload: '＋ อัปโหลดรายการเดินบัญชี',
  p_bs_uploading: 'กำลังอ่านไฟล์…',
  p_bs_file_hint: 'ไฟล์ PDF จากธนาคารหรือไฟล์ CSV ที่ส่งออกมา — ครั้งละไม่เกิน {{max}} ไฟล์ ไฟล์ละไม่เกิน {{mb}} MB รูปถ่ายและ Excel อ่านไม่ได้',
  p_bs_too_many: 'กรุณาส่งครั้งละไม่เกิน {{max}} ไฟล์',
  p_bs_upload_failed: 'อัปโหลดไม่สำเร็จ',
  p_bs_res_saved: '✓ {{filename}} — บัญชี {{account}} · อ่านได้ {{rows}} รายการ ใหม่ {{added}} รายการ มีอยู่แล้ว {{skipped}} รายการ',
  p_bs_res_failed: '✗ {{filename}} — {{error}}',
  p_bs_err_account_not_registered: 'บัญชีในรายการเดินบัญชีนี้ยังไม่ได้ลงทะเบียนในข้อมูลบริษัทของท่าน — กรุณาเพิ่มบัญชีก่อน',
  p_bs_err_statement_account_mismatch: 'รายการเดินบัญชีนี้เป็นของบัญชีอื่น ไม่ใช่บัญชีที่ท่านเลือก',
  p_bs_list_title: 'รายการเดินบัญชีที่ได้รับ',
  p_bs_th_file: 'ไฟล์',
  p_bs_th_account: 'บัญชี',
  p_bs_th_period: 'ช่วงเวลา',
  p_bs_th_rows: 'รายการ',
  p_bs_rows_cell: 'ใหม่ {{added}} จาก {{rows}}',
  p_bs_closing: 'ยอดคงเหลือปลายงวด {{amount}}',
  p_bs_th_uploaded: 'อัปโหลดเมื่อ',
  p_bs_via_portal: 'โดยท่าน',
  p_bs_via_workspace: 'โดย {{brand}}',
  p_bs_act_view: 'ดู',
  p_bs_act_withdraw: 'ถอนออก',
  p_bs_confirm_withdraw: 'ถอน {{filename}} ออกหรือไม่? รายการที่ยังไม่ได้จับคู่จะถูกลบไปด้วย',
  p_bs_withdraw_locked: 'ถอนรายการเดินบัญชีนี้ไม่ได้ — {{brand}} ได้จับคู่บางรายการไปแล้ว',
  p_bs_empty: 'ยังไม่มีรายการเดินบัญชี',
  p_bs_err_open: 'ไม่สามารถเปิดไฟล์ได้',
  p_bs_file_fallback: 'รายการเดินบัญชี',
  // btn
  p_btn_cancel: 'ยกเลิก',
  p_btn_save: 'บันทึก',
  p_btn_save_bilingual: 'บันทึก',
  // dlg
  p_dlg_close: 'ปิด',
  p_dlg_discard: 'ทิ้งการแก้ไขที่ยังไม่ได้บันทึกหรือไม่?',
  // cdoc
  p_cdoc_add_line: '+ เพิ่มรายการ',
  p_cdoc_bank_cash: 'ชำระเป็นเงินสด — ไม่มีข้อมูลบัญชีธนาคาร',
  p_cdoc_bank_label: 'บัญชีธนาคารที่แสดงบน {{docKind}} นี้',
  p_cdoc_bank_none: '— ยังไม่มีบัญชี (เพิ่มได้ที่ ข้อมูลบริษัท) —', p_cdoc_bank_unset: '— ไม่มีบัญชีธนาคารแสดงบนเอกสารนี้ —',
  p_cdoc_charge_hint_cust: 'ท่านจ่ายเงินให้บุคคลภายนอกแทนลูกค้า และเรียกเก็บคืนตามจำนวนเดิมโดยไม่บวกเพิ่ม และใบเสร็จหรือใบกำกับภาษีของผู้ขายออกในชื่อลูกค้า ไม่ใช่ชื่อของท่าน',
  p_cdoc_charge_hint_own: 'ท่านเรียกเก็บค่าบริการหรือสินค้าที่บริษัทของท่านเป็นผู้ทำหรือจัดหาเอง เป็นตัวเลือกที่ใช้โดยทั่วไป',
  p_cdoc_charge_hint_self: 'ท่านจ่ายเงินให้บุคคลภายนอกแทนลูกค้า แต่ใบเสร็จหรือใบกำกับภาษีของผู้ขายออกในชื่อบริษัทของท่านเอง',
  p_cdoc_charge_opt_cust: 'จ่ายแทนลูกค้า — ใบกำกับของผู้ขายออกในชื่อลูกค้า',
  p_cdoc_charge_opt_own: 'บริการหรือสินค้าของบริษัทเราเอง',
  p_cdoc_charge_opt_self: 'จ่ายแทนลูกค้า — ใบกำกับของผู้ขายออกในชื่อบริษัทเรา',
  p_cdoc_charge_title: 'รายการนี้เป็นค่าบริการหรือสินค้าของบริษัทท่านเอง หรือเป็นเงินที่ท่านจ่ายแทนลูกค้าแล้วเรียกเก็บคืน',
  p_cdoc_charge_unset: '— ยังไม่ได้ระบุ —',
  p_cdoc_charge_w503: 'ใบกำกับของผู้ขายออกในชื่อบริษัทของท่านเอง รายการนี้จึงยังถือเป็นค่าบริการของท่านเอง ไม่ใช่เงินที่จ่ายแทนแล้วเรียกเก็บคืน หากผู้ขายออกใบกำกับให้ลูกค้าโดยตรง โปรดเปลี่ยนตัวเลือก ทั้งนี้ท่านยังออกเอกสารนี้ได้',
  p_cdoc_charge_w505: 'รายการนี้ยังระบุการคิดภาษีมูลค่าเพิ่มของตัวเองไว้ด้วย จึงยังถือเป็นค่าบริการของท่านเอง ไม่ใช่เงินที่จ่ายแทนแล้วเรียกเก็บคืน เฉพาะรายการที่ไม่ได้ระบุการคิดภาษีของตัวเองเท่านั้น ที่ระบบจะถือเป็นเงินที่ท่านจ่ายแทนและเรียกเก็บคืน',
  p_cdoc_charge_w506: 'ท่านระบุว่าผู้ขายเรียกเก็บรวมทั้งสิ้น {{cost}} แต่รายการนี้เรียกเก็บจากลูกค้า {{charged}} มากกว่า {{diff}} (ไม่นับภาษีมูลค่าเพิ่มที่รายการนี้บวกเพิ่ม เพราะท่านเก็บแล้วนำส่ง จึงไม่ใช่ค่าบริการของท่าน) เงินที่จ่ายแทนลูกค้าต้องเรียกเก็บคืนตามจำนวนที่จ่ายไป ส่วนที่บวกเพิ่มถือเป็นค่าบริการของท่านเอง ทั้งสองจำนวนจะคงอยู่ในรายการนี้และถูกทำเครื่องหมายไว้เพื่อตรวจทาน ทั้งนี้ท่านยังออกเอกสารนี้ได้',
  p_cdoc_choose_service: '— เลือกบริการ —',
  p_cdoc_correct_prompt: 'เหตุใดจึงแก้ไข {{docNo}}?\n\nระบบจะบันทึกเหตุผลนี้ไว้กับเอกสารพร้อมกับตัวเลขเดิม',
  p_cdoc_cost_ph: 'ผู้ขายเรียกเก็บ',
  p_cdoc_cost_title: 'จำนวนรวมที่ผู้ขายเรียกเก็บจากท่านสำหรับรายการนี้ ตามที่ปรากฏในใบเรียกเก็บของผู้ขาย รวมภาษีมูลค่าเพิ่มที่ปรากฏในใบเรียกเก็บนั้น เงินที่จ่ายแทนลูกค้าต้องเรียกเก็บคืนตามจำนวนที่จ่ายไป การระบุจำนวนนี้จะทำให้ระบบตรวจทานกับจำนวนที่รายการนี้เรียกเก็บจากลูกค้าได้ (ไม่นับภาษีมูลค่าเพิ่มที่รายการนี้บวกเพิ่ม) เมื่อออกเอกสารใหม่เป็นสกุลเงินบาท หากรายการนี้ไม่ได้ระบุการคิดภาษีของตัวเองและเรียกเก็บมากกว่าจำนวนนี้ ส่วนที่บวกเพิ่มจะถูกบันทึกแยกเป็นอีกรายการหนึ่งซึ่งเป็นค่าบริการของท่านเองพร้อมภาษีมูลค่าเพิ่ม โดยจะแสดงทั้งสองรายการให้เห็นก่อนบันทึก ไม่บังคับกรอก หากเว้นว่างจะไม่ถือเป็นการระบุสิ่งใด ไม่เปลี่ยนจำนวนเงินใดในเอกสารนี้ และไม่ขัดขวางการออกเอกสาร',
  p_cdoc_split_title: 'บันทึกเป็นสองรายการ',
  p_cdoc_split_why: 'ผู้ขายเรียกเก็บ {{cost}} และรายการนี้เรียกเก็บจากลูกค้า {{charged}} ส่วนที่บวกเพิ่ม {{diff}} เป็นค่าบริการของท่านเอง จึงบันทึกแยกเป็นอีกรายการหนึ่งพร้อมภาษีมูลค่าเพิ่ม ส่วน {{cost}} ยังคงเป็นเงินที่จ่ายแทนลูกค้าและเรียกเก็บคืนตามจำนวนที่จ่ายไป',
  p_cdoc_split_cost_line: 'จ่ายแทนลูกค้า เรียกเก็บคืนตามจำนวนที่จ่ายไป — ไม่มี VAT',
  p_cdoc_split_markup_line: 'ค่าบริการของท่านเอง — {{vatLabel}} · VAT {{vat}}',
  p_cdoc_split_name: 'ชื่อรายการที่เพิ่มขึ้น (พิมพ์บนเอกสาร)',
  p_cdoc_currency: 'สกุลเงิน',
  p_cdoc_remark_ph: 'หมายเหตุที่พิมพ์ลงในเอกสาร — การจัดรูปแบบ การขึ้นบรรทัดใหม่ และรูปภาพที่วางจะถูกเก็บไว้',
  p_cdoc_pay_method: 'เงื่อนไขการชำระเงิน',
  p_cdoc_pay_method_none: '— ไม่ระบุ —',
  p_cdoc_pm_tt: 'T/T — การโอนเงินผ่านธนาคาร',
  p_cdoc_pm_lc: 'L/C — เลตเตอร์ออฟเครดิต',
  p_cdoc_pm_sblc: 'SBLC — สแตนด์บายเลตเตอร์ออฟเครดิต',
  p_cdoc_pm_bg: 'BG — หนังสือค้ำประกันธนาคาร',
  p_cdoc_pay_method_note: 'จะพิมพ์ไว้ในหัวเอกสาร และแสดงชื่อเต็มข้างข้อมูลธนาคาร',
  p_cdoc_pay_method_blank: 'เอกสารภายในประเทศโดยทั่วไปเว้นว่างไว้ เลือกเมื่อชำระผ่านเครื่องมือทางธนาคาร',
  p_cdoc_customer: 'ลูกค้า *',
  p_cdoc_doc_type: 'ประเภทเอกสาร',
  p_cdoc_doc_type_locked: '— กำหนดตายตัวเมื่อออกเอกสารแล้ว',
  p_cdoc_due_date: 'วันครบกำหนดชำระ *',
  p_cdoc_due_hint: '— วันที่ออก + {{days}} วัน',
  p_cdoc_edit_note: 'เลขที่เอกสารยังคงเป็น {{docNo}} หากเอกสารฉบับนี้ส่งให้ลูกค้าไปแล้ว กรุณาส่งฉบับที่แก้ไขแล้วให้ลูกค้าอีกครั้ง',
  p_cdoc_entered_incl: 'ยอดที่กรอก (รวม VAT) {{amount}} — แยกภาษีด้านล่าง',
  p_cdoc_err_due: 'ต้องระบุวันครบกำหนดชำระ — ใบแจ้งหนี้นี้ครบกำหนดชำระเมื่อใด',
  p_cdoc_err_due_before: 'วันครบกำหนดชำระต้องไม่ก่อนวันที่ออกเอกสาร',
  p_cdoc_err_line: 'ทุกรายการต้องระบุบริการ — กรุณาเลือกบริการ หรือลบรายการที่ว่างออก',
  p_cdoc_err_reason: 'ต้องระบุเหตุผลในการแก้ไขเอกสารทางภาษี',
  p_cdoc_fx_date: 'วันที่อัตราแลกเปลี่ยน',
  p_cdoc_fx_note: 'โดยปกติใบกำกับภาษีต้องออกเป็นเงินบาท การใช้สกุลเงินต่างประเทศต้องได้รับอนุมัติจากกรมสรรพากร (มาตรา 86/4) โดยเอกสารจะแสดงจำนวนเงินเทียบเท่าเป็นเงินบาทกำกับไว้',
  p_cdoc_fx_published: 'อัตราที่ประกาศเมื่อ {{date}}: {{rate}}',
  p_cdoc_fx_published_near: 'ไม่มีอัตราประกาศสำหรับวันที่ {{asked}} อัตราก่อนหน้าที่ใกล้ที่สุดคือ {{rate}} ของวันที่ {{date}}',
  p_cdoc_fx_far: 'อัตราที่กรอกต่างจากอัตราที่ประกาศเกิน 2% — กรุณาตรวจสอบตัวเลขและสกุลเงิน',
  p_cdoc_fx_ph: 'เช่น 33.50',
  p_cdoc_fx_rate: 'อัตราแลกเปลี่ยนเป็น$t(cur_word) *',
  p_cdoc_issue_btn: 'ออก{{docKind}}',
  p_cdoc_issue_date: 'วันที่ออกเอกสาร',
  p_cdoc_issuing: 'กำลังออกเอกสาร…',
  p_cdoc_items: 'รายการ',
  p_cdoc_less_wht: 'หักภาษี ณ ที่จ่าย {{amount}}',
  p_cdoc_net: 'ยอดสุทธิที่ได้รับ',
  p_cdoc_qty_title: 'จำนวน',
  p_cdoc_remark: 'หมายเหตุ (ไม่บังคับ)',
  p_cdoc_save_changes: 'บันทึกการแก้ไข',
  p_cdoc_saving: 'กำลังบันทึก…',
  p_cdoc_subtotal: 'รวมเป็นเงิน',
  p_cdoc_title_edit: 'แก้ไข {{docKind}} {{docNo}}',
  p_cdoc_title_new: '{{docKind}}ใหม่',
  p_cdoc_total: 'รวมทั้งสิ้น',
  p_cdoc_type_invoice: 'ใบแจ้งหนี้ — งานบริการ ออกใบกำกับภาษีเมื่อได้รับชำระเงิน',
  p_cdoc_type_proforma: 'ใบแจ้งหนี้ชั่วคราว (Proforma Invoice) — ไม่ใช่ใบกำกับภาษี ยังไม่มีความรับผิดในการเสีย VAT',
  p_cdoc_type_tax_invoice: 'ใบกำกับภาษี/ใบส่งของ — ความรับผิดในการเสียภาษีมูลค่าเพิ่มเกิดขึ้นเมื่อส่งมอบสินค้า',
  p_cdoc_type_tir: 'ใบกำกับภาษี/ใบเสร็จรับเงิน — ขายเงินสด',
  p_cdoc_unitprice_title: 'ราคาต่อหน่วย',
  p_cdoc_valid_until: 'ยืนราคาถึงวันที่',
  p_cdoc_vat: 'VAT',
  p_cdoc_vat_title: 'การคิดภาษีมูลค่าเพิ่ม',
  // crec
  p_crec_already_paid: 'ชำระแล้ว',
  p_crec_attach_hint: 'แนบหนังสือรับรอง 50 ทวิ ของผู้ซื้อได้ภายหลังจากหน้ารายการใบเสร็จรับเงิน',
  p_crec_cash: 'เงินที่ได้รับจริง ($t(cur_word))',
  p_crec_date_ok: 'ใบเสร็จรับเงินจะลงวันที่และออกเลขที่ในเดือน {{yyyymm}}',
  p_crec_date_required: 'จำเป็นต้องระบุ — ให้ดูวันที่จากรายการเดินบัญชีธนาคาร ไม่ใช่วันที่ท่านกรอกข้อมูล',
  p_crec_differs: '· ไม่ตรงกับยอดค้างชำระ {{amount}} — ระบบจะบันทึกเป็นการชำระบางส่วน',
  p_crec_err: 'ไม่สำเร็จ',
  p_crec_err_exceeds: 'ยอดที่รับ {{received}} มากกว่ายอดค้างชำระ {{outstanding}} ของ{{docWord}} {{invoiceNo}} — ใบเสร็จรับเงินต้องไม่เกินยอดที่ค้างชำระ โปรดระบุไม่เกิน {{outstanding}} (เงินสด + ภาษีหัก ณ ที่จ่าย)',
  p_crec_err_wht_exceeds: 'ภาษีหัก ณ ที่จ่าย {{wht}} มากกว่าภาษีหัก ณ ที่จ่ายที่ยังรอได้รับ {{left}} ของ{{docWord}} {{invoiceNo}} — ใบเสร็จรับเงินบันทึกภาษีหัก ณ ที่จ่ายเกินกว่าที่ผู้ซื้อยังต้องหักไม่ได้ โปรดระบุไม่เกิน {{left}}',
  p_crec_err_zero: 'จำนวนเงินที่ได้รับต้องมากกว่าศูนย์',
  p_crec_err_settled_doc: '{{docWord}} {{invoiceNo}} ออกใบเสร็จรับเงินครบเต็มจำนวนแล้ว ({{receiptNos}}) — ไม่มียอดค้างชำระ การออกใบเสร็จอีกฉบับจะบันทึกยอดขายเดียวกันซ้ำสองครั้ง',
  p_crec_err_nothing_owed_doc: '{{docWord}} {{invoiceNo}} ไม่มียอดค้างชำระ — ไม่มีเงินที่ต้องออกใบเสร็จรับเงินอีก',
  p_crec_err_void: '{{docWord}} {{invoiceNo}} ถูกยกเลิกแล้ว — ไม่มียอดค้างชำระ ไม่มีเงินที่ต้องออกใบเสร็จรับเงิน',
  p_crec_err_credited: '{{docWord}} {{invoiceNo}} ไม่มียอดค้างชำระ (ออกใบเสร็จรับเงินแล้ว {{receipted}} ลดหนี้แล้ว {{credited}}) — ไม่มีเงินที่ต้องออกใบเสร็จรับเงินอีก',
  p_crec_err_negative: 'จำนวนเงินต้องไม่ติดลบ — โปรดระบุเงินที่ได้รับและภาษีหัก ณ ที่จ่ายที่ผู้ซื้อหักไว้เป็น 0 หรือมากกว่า',
  p_crec_err_nothing_owed: 'ใบแจ้งหนี้ {{invoiceNo}} ไม่มียอดค้างชำระ — ไม่มีเงินที่ต้องออกใบเสร็จรับเงินอีก',
  p_crec_err_period_closed: 'วันที่รับชำระ {{date}} อยู่ในรอบระยะเวลาบัญชีที่ปิดแล้ว ({{from}} – {{to}}) — ไม่สามารถบันทึกรายการใดในรอบนั้นได้ โปรดตรวจสอบวันที่เงินเข้าบัญชีจริง',
  p_crec_err_filed: 'ภาษีมูลค่าเพิ่มของ {{ym}} ถูกทำเครื่องหมายว่ายื่นแล้วในศูนย์ยื่นแบบ ใบเสร็จลงวันที่ {{date}} จะเพิ่มรายการภาษีขายในแบบที่ยื่นไปแล้ว โปรดตรวจสอบวันที่ได้รับเงิน หรือยกเลิกเครื่องหมายของ {{ym}} ที่นั่นก่อน',
  p_crec_err_fx_rate: '{{docNo}} เป็นสกุล {{currency}} และไม่มีอัตราแลกเปลี่ยนให้ใบเสร็จนี้อ้างอิง จึงบันทึกบัญชีไม่ได้ กรุณาระบุอัตราบน {{docNo}} ก่อน',
  p_crec_err_settled: 'ใบแจ้งหนี้ {{invoiceNo}} ออกใบเสร็จรับเงินครบเต็มจำนวนแล้ว ({{receiptNos}}) — ไม่มียอดค้างชำระ การออกใบเสร็จอีกฉบับจะบันทึกยอดขายเดียวกันซ้ำสองครั้ง',
  p_crec_exceeds: '· มากกว่ายอดค้างชำระ {{amount}} — ใบเสร็จรับเงินต้องไม่เกินยอดที่ค้างชำระ จึงออกไม่ได้',
  p_crec_expected_wht: 'ภาษีหัก ณ ที่จ่าย (ประมาณ)',
  p_crec_invoice: 'เอกสาร *',
  p_crec_invoice_total: 'ยอดรวมตามเอกสาร',
  p_crec_issue: 'ออกใบเสร็จรับเงิน',
  p_crec_negative: '· จำนวนเงินติดลบ — ใบเสร็จรับเงินบันทึกเงินที่ได้รับจริง จึงออกไม่ได้',
  p_crec_of_which: '— ในจำนวนนี้เป็นภาษีหัก ณ ที่จ่ายที่จะได้รับ {{wht}} และเงินสด {{cash}}',
  p_crec_wht_over: '· ภาษีหัก ณ ที่จ่ายมากกว่ายอดที่ยังรอได้รับ {{wht}} — ใบเสร็จรับเงินบันทึกเกินกว่านั้นไม่ได้ จึงออกไม่ได้',
  p_crec_zero: '· โปรดระบุเงินสดที่ได้รับหรือภาษีหัก ณ ที่จ่ายที่ถูกหัก — ออกใบเสร็จรับเงินยอดศูนย์ไม่ได้',
  p_crec_wht_none_edit: '{{docWord}} {{invoiceNo}} ออกโดยไม่มีภาษีหัก ณ ที่จ่าย — หากผู้ซื้อหักภาษี ณ ที่จ่ายไว้จริง โปรดแก้ไข {{invoiceNo}} ให้รายการมีอัตราภาษีหัก ณ ที่จ่ายก่อน แล้วจึงบันทึกการรับชำระนี้',
  p_crec_wht_none_cash: '{{docWord}} {{invoiceNo}} ออกโดยไม่มีภาษีหัก ณ ที่จ่ายและแก้ไขไม่ได้แล้ว — โปรดบันทึกเฉพาะเงินสดที่ได้รับ และส่งหนังสือรับรองการหักภาษี ณ ที่จ่ายของผู้ซื้อให้นักบัญชี',
  p_crec_wht_none_void: '{{docWord}} {{invoiceNo}} ออกโดยไม่มีภาษีหัก ณ ที่จ่าย — หากผู้ซื้อหักภาษี ณ ที่จ่ายไว้จริง โปรดยกเลิก {{receiptNos}} ก่อน แก้ไข {{invoiceNo}} ให้รายการมีอัตราภาษีหัก ณ ที่จ่าย แล้วจึงบันทึกเงินสดและภาษีหัก ณ ที่จ่ายรวมในใบเสร็จรับเงินฉบับเดียว',
  p_crec_wht_none_void_staff: '{{docWord}} {{invoiceNo}} ออกโดยไม่มีภาษีหัก ณ ที่จ่าย — หากผู้ซื้อหักภาษี ณ ที่จ่ายไว้จริง โปรดให้ลูกค้ายกเลิก {{receiptNos}} ในพอร์ทัลของลูกค้าก่อน (ใบเสร็จรับเงินยกเลิกได้ในพอร์ทัลเท่านั้น ไม่ใช่ในพื้นที่ทำงานนี้) แล้วแก้ไข {{invoiceNo}} ให้รายการมีอัตราภาษีหัก ณ ที่จ่าย และบันทึกเงินสดและภาษีหัก ณ ที่จ่ายรวมในใบเสร็จรับเงินฉบับเดียว',
  p_owed_received_credited: 'รับชำระแล้ว {{received}} · ลดหนี้แล้ว {{credited}}',
  p_crec_option_outstanding: 'คงค้าง {{amount}} $t(cur_word)',
  p_crec_option_outstanding_wht: 'คงค้าง {{amount}} $t(cur_word) — ในจำนวนนี้เป็นภาษีหัก ณ ที่จ่ายที่จะได้รับ {{wht}} และเงินสด {{cash}}',
  p_crec_outstanding: 'ยอดค้างชำระ',
  p_crec_payment_date: 'วันที่รับชำระ (วันที่เงินเข้าบัญชี) *',
  p_crec_saving: 'กำลังบันทึก…',
  p_crec_select_invoice: '— เลือกเอกสารที่ยังไม่ได้ชำระ —',
  p_crec_settling: 'ตัดชำระ {{total}} = เงินสด {{cash}} + หัก ณ ที่จ่าย {{wht}}',
  p_crec_subtitle: 'เมื่อเลือกเอกสาร ระบบจะเติมยอดคงค้างและภาษีหัก ณ ที่จ่ายที่คาดว่าผู้ซื้อจะหักไว้ให้อัตโนมัติ หากเงินที่ได้รับจริงไม่ตรงกัน ท่านสามารถแก้ไขตัวเลขทั้งสองได้',
  p_crec_title: 'บันทึกรับชำระเงิน',
  p_crec_wht: 'ภาษีหัก ณ ที่จ่ายที่ผู้ซื้อหักไว้',
  // cust
  p_cust_add: '+ เพิ่มลูกค้า',
  p_cust_confirm_remove: 'ลบ {{name}} หรือไม่?',
  p_cust_empty: 'ยังไม่มีลูกค้า — เพิ่มผู้ซื้อรายแรกของท่าน',
  p_cust_f_address: 'ที่อยู่',
  p_cust_f_branch: 'สาขา',
  p_cust_f_email: 'อีเมล',
  p_cust_f_name: 'ชื่อบริษัท / ชื่อบุคคล *',
  p_cust_f_name_th: 'ชื่อภาษาไทย (ไม่บังคับ)',
  p_cust_f_notes: 'หมายเหตุ',
  p_cust_f_notes_hint: '— ใช้ภายในเท่านั้น ไม่แสดงบนเอกสาร',
  p_cust_f_phone: 'โทรศัพท์',
  p_cust_f_taxid: 'เลขประจำตัวผู้เสียภาษี',
  p_cust_modal_edit: 'แก้ไขข้อมูลลูกค้า',
  p_cust_modal_new: 'ลูกค้าใหม่',
  p_cust_search_ph: 'ค้นหาชื่อ รหัส เลขประจำตัวผู้เสียภาษี อีเมล…',
  p_cust_th_branch: 'สาขา',
  p_cust_th_email: 'อีเมล',
  p_cust_th_name: 'ชื่อ',
  p_cust_th_no: 'เลขที่',
  p_cust_th_taxid: 'เลขประจำตัวผู้เสียภาษี',
  p_cust_title: 'ลูกค้าของฉัน',
  // disc
  p_disc_accept: 'ข้าพเจ้าได้อ่านและยอมรับข้อความข้างต้น',
  p_disc_body_bold: 'ท่านเป็นผู้รับผิดชอบแต่เพียงผู้เดียวต่อความถูกต้องและความต่อเนื่องของเลขที่เอกสารของท่าน',
  p_disc_body_p1: 'เอกสารขายของท่านทั้งหมด (ใบเสนอราคา ใบแจ้งหนี้ ใบเสร็จรับเงิน) ต้องออกผ่านระบบนี้ เพื่อให้เลขที่เอกสารเรียงต่อเนื่องกัน เลขที่เอกสารจะไม่ถูกนำกลับมาใช้ซ้ำ และเมื่อยกเลิกเอกสาร เลขที่นั้นจะยังคงอยู่ในลำดับโดยมีสถานะ VOID',
  p_disc_body_p2: ' รวมถึงเอกสารที่ท่านออกนอกระบบนี้ด้วย',
  p_disc_continue: 'ดำเนินการต่อ',
  p_disc_title: 'การออกเลขที่เอกสาร — เป็นความรับผิดชอบของท่าน',
  // doc
  p_doc_act_attach_wht: 'แนบ 50 ทวิ',
  p_doc_act_attach_wht_title: 'แนบหนังสือรับรองการหักภาษี ณ ที่จ่าย (50 ทวิ) จากผู้ซื้อ',
  p_doc_act_delete: 'ลบและคืนเลขที่เอกสาร',
  p_doc_act_edit: 'แก้ไข',
  p_doc_act_pdf: 'PDF',
  p_doc_act_send: 'ส่งไปยัง…',
  p_doc_act_send_title: 'ส่งอีเมลเอกสารนี้ให้ลูกค้า พร้อมแนบไฟล์ PDF',
  p_doc_act_void: 'ยกเลิกเอกสาร',
  p_doc_add_new: '+ สร้าง{{docKind}}ใหม่',
  p_doc_add_note: '+ สร้างใบลดหนี้ / ใบเพิ่มหนี้',
  p_doc_add_receipt: '+ บันทึกรับชำระเงิน',
  p_doc_confirm_handed_over: '{{docNo}} ถูกดาวน์โหลดเป็น PDF ไปแล้ว ผู้ซื้ออาจถือสำเนาฉบับนั้นอยู่\n\nการแก้ไขจะทำให้เอกสารไม่ตรงกับสำเนาที่ผู้ซื้อถืออยู่ หากผู้ซื้อได้รับเอกสารไปแล้ว ควรออกใบลดหนี้/ใบเพิ่มหนี้แทน หรือยกเลิกแล้วออกใหม่\n\nยืนยันที่จะแก้ไขหรือไม่?',
  p_doc_delete_prompt: 'ลบ {{docNo}} และคืนเลขที่เอกสารกลับเข้าระบบหรือไม่?\n\nคืนได้เฉพาะเลขล่าสุดของชุดเอกสารนั้นเท่านั้น เอกสารฉบับถัดไปที่ท่านออกจะใช้เลข {{docNo}} ซ้ำ\n\nเหตุผล? (ระบบจะบันทึกไว้)',
  p_doc_empty: 'ยังไม่มีข้อมูล',
  p_doc_empty_notes: 'ยังไม่มีใบลดหนี้หรือใบเพิ่มหนี้ ออกเอกสารได้จากใบกำกับภาษีที่ต้องแก้ไข',
  p_doc_err_open: 'ไม่สามารถเปิดเอกสารนี้ได้',
  p_doc_freed: '{{docNo}} กลับมาว่างแล้ว — เอกสารฉบับถัดไปจะใช้เลขที่นี้',
  p_doc_head_invoices: 'ใบแจ้งหนี้',
  p_doc_head_notes: 'ใบลดหนี้ / ใบเพิ่มหนี้',
  p_doc_head_quotations: 'ใบเสนอราคา',
  p_doc_head_receipts: 'ใบเสร็จรับเงิน',
  p_doc_kind_invoice: 'ใบแจ้งหนี้',
  p_doc_kind_quotation: 'ใบเสนอราคา',
  p_doc_not_editable: 'เอกสารนี้ไม่สามารถแก้ไขได้',
  // Batch 11 R1 (COA-D9): the edit window's read-only note on a document already in the accounting books.
  p_ib_title: 'บันทึกในสมุดบัญชีแล้ว',
  p_ib_note: 'เอกสารนี้บันทึกในสมุดบัญชีแล้ว ({{docNos}}) หากต้องการแก้ไข กรุณาสอบถามนักบัญชีของคุณก่อน',
  // Batch 11 R3 (COA-D9): เอกสารที่บันทึกในสมุดบัญชีแล้ว — สิ่งที่ลูกค้ายังทำได้
  p_ll_edit_limited: 'แก้ไข (บางส่วน)',
  p_ll_void_locked: 'บันทึกในสมุดบัญชีแล้ว ({{docNos}}) — ยกเลิกไม่ได้อีกต่อไป หากต้องการแก้ไข กรุณาติดต่อนักบัญชีของคุณ',
  p_ll_void_filed: 'ยื่นแบบภาษีมูลค่าเพิ่มของ {{month}} แล้ว — ยกเลิกเอกสารนี้ไม่ได้อีกต่อไป หากต้องการแก้ไข กรุณาติดต่อนักบัญชีของคุณ (แก้ไขได้ด้วยใบลดหนี้หรือใบเพิ่มหนี้)',
  p_ll_delete_filed: 'ยื่นแบบภาษีมูลค่าเพิ่มของ {{month}} แล้ว — ลบเอกสารนี้ไม่ได้อีกต่อไป หากต้องการแก้ไข กรุณาติดต่อนักบัญชีของคุณ',
  p_ll_delete_locked: 'บันทึกในสมุดบัญชีแล้ว ({{docNos}}) — คืนเลขที่เอกสารไม่ได้อีกต่อไป',
  p_ll_limited_title: 'บันทึกในสมุดบัญชีแล้ว — แก้ไขได้บางส่วน',
  p_ll_tax_for_title: 'ออกให้ใบแจ้งหนี้ที่ชำระแล้ว — แก้ไขได้บางส่วน',
  p_ll_tax_for_adjusted: 'ใบกำกับภาษีนี้ออกให้ {{forNo}} ({{receiptNos}}) ซึ่งต่อมาถูกปรับด้วย {{nos}} ใบกำกับภาษีนี้ไม่บันทึกสิ่งใดในสมุดบัญชี และยอดเงิน รายการ วันที่ ผู้ซื้อ และสกุลเงินคงเดิมตามที่ออก: ที่นี่แก้ได้เฉพาะหมายเหตุ บัญชีธนาคาร วิธีชำระเงิน และวันครบกำหนด',
  p_ll_tax_for_changed: 'ใบกำกับภาษีนี้ออกให้ {{forNo}} ({{receiptNos}}) ซึ่งการขายที่ชำระแล้วนั้นมีการเปลี่ยนแปลงหลังจากออกใบกำกับภาษีนี้ ใบกำกับภาษีนี้ไม่บันทึกสิ่งใดในสมุดบัญชี และยอดเงิน รายการ วันที่ ผู้ซื้อ และสกุลเงินคงเดิมตามที่ออก: ที่นี่แก้ได้เฉพาะหมายเหตุ บัญชีธนาคาร วิธีชำระเงิน และวันครบกำหนด',
  p_ll_tax_for_filed: 'ใบกำกับภาษีนี้ออกให้ {{forNo}} ({{receiptNos}}) และลงวันที่ {{date}} ในเดือน {{ym}} ซึ่งภาษีมูลค่าเพิ่มถูกทำเครื่องหมายว่ายื่นแล้ว ใบกำกับภาษีนี้ไม่บันทึกสิ่งใดในสมุดบัญชี แต่จะไม่ออกใหม่ในเดือนนั้น: ยอดเงิน รายการ วันที่ ผู้ซื้อ และสกุลเงินคงเดิมตามที่ออก — ที่นี่แก้ได้เฉพาะหมายเหตุ บัญชีธนาคาร วิธีชำระเงิน และวันครบกำหนด',
  p_ll_tax_for_closed: 'ใบกำกับภาษีนี้ออกให้ {{forNo}} ({{receiptNos}}) และลงวันที่ {{date}} ซึ่งอยู่ในรอบระยะเวลาบัญชีที่ปิดแล้ว ({{from}} – {{to}}) ใบกำกับภาษีนี้ไม่บันทึกสิ่งใดในสมุดบัญชี แต่จะไม่ออกใหม่ในรอบนั้น: ยอดเงิน รายการ วันที่ ผู้ซื้อ และสกุลเงินคงเดิมตามที่ออก — ที่นี่แก้ได้เฉพาะหมายเหตุ บัญชีธนาคาร วิธีชำระเงิน และวันครบกำหนด',
  p_ll_limited_note: 'ยอดเงิน รายการ วันที่ ผู้ซื้อ และสกุลเงินบันทึกในสมุดบัญชีแล้ว ({{docNos}}) ที่นี่แก้ได้เฉพาะหมายเหตุ บัญชีธนาคาร วิธีชำระเงิน และวันครบกำหนด ส่วนอื่นกรุณาติดต่อนักบัญชีของคุณ',
  p_ll_receipt_superseded: 'บรรทัดภาษีขายของใบแจ้งหนี้นี้ ({{lineNo}}) บันทึกในสมุดบัญชีแล้ว ({{docNos}}) จึงยังออกใบเสร็จรับเงินนี้ไม่ได้ กรุณาติดต่อนักบัญชีของคุณ',
  p_ib_hand_changed: '{{docNos}} ถูกบันทึกด้วยมือในสมุดบัญชีของคุณ และสิ่งที่บันทึกไว้มีการเปลี่ยนแปลงในภายหลัง — กรุณาให้นักบัญชีของคุณตรวจสอบ',
  p_doc_note_credit: 'ใบลดหนี้',
  p_doc_note_debit: 'ใบเพิ่มหนี้',
  p_doc_reason_required: 'ต้องระบุเหตุผล',
  p_doc_search_ph: 'ค้นหาเลขที่เอกสาร ลูกค้า ที่อยู่…',
  p_doc_send_tooltip: 'ส่งแล้ว {{count}} ครั้ง · ครั้งล่าสุดถึง {{email}}',
  p_doc_status_open: 'เปิดอยู่',
  p_doc_status_paid: 'ชำระแล้ว',
  p_doc_status_partial: 'ชำระบางส่วน',
  p_doc_status_unpaid: 'ยังไม่ชำระ',
  p_doc_status_rejected: 'ปฏิเสธแล้ว',
  p_doc_status_void: 'VOID',
  p_doc_th_adjusts: 'ปรับปรุงใบกำกับภาษี',
  p_doc_th_customer: 'ลูกค้า',
  p_doc_th_date: 'วันที่',
  p_doc_th_difference: 'ผลต่าง ($t(cur_word))',
  p_doc_th_no: 'เลขที่',
  p_doc_th_due: '\u0e04\u0e23\u0e1a\u0e01\u0e33\u0e2b\u0e19\u0e14',
  p_doc_th_owing: '\u0e04\u0e07\u0e04\u0e49\u0e32\u0e07',
  p_doc_th_paid: '\u0e0a\u0e33\u0e23\u0e30\u0e41\u0e25\u0e49\u0e27',
  p_sum_n_inv: '{{count}} \u0e43\u0e1a',
  p_sum_n_doc: '{{count}} \u0e09\u0e1a\u0e31\u0e1a',
  p_sum_oldest: '\u0e19\u0e32\u0e19\u0e2a\u0e38\u0e14 {{days}} \u0e27\u0e31\u0e19 \u2014 {{docNo}}',
  p_sum_outstanding: '\u0e23\u0e2d\u0e23\u0e31\u0e1a\u0e0a\u0e33\u0e23\u0e30',
  p_sum_overdue: '\u0e40\u0e01\u0e34\u0e19\u0e01\u0e33\u0e2b\u0e19\u0e14',
  p_sum_scope: '\u0e17\u0e38\u0e01\u0e0a\u0e48\u0e27\u0e07\u0e40\u0e27\u0e25\u0e32 \u00b7 \u0e13 \u0e27\u0e31\u0e19\u0e19\u0e35\u0e49',
  p_doc_th_invoice_no: 'ใบแจ้งหนี้',
  p_conv_already: 'ออกเอกสารเรียกเก็บจากใบเสนอราคานี้แล้ว: {{list}}',
  p_conv_already_stages: 'ออกใบแจ้งหนี้จากใบเสนอราคานี้แล้ว: {{list}} ออกเพิ่มได้ — ใบเสนอราคาหนึ่งใบออกใบแจ้งหนี้ได้หลายครั้ง — แต่ลูกค้าจะได้รับใบแจ้งหนี้อีกฉบับ',   // (owner 2026-11-03) main's sentence, where the workspace is off: billed in stages
  p_conv_cancel: 'ยกเลิก',
  p_conv_done: 'ออกใบแจ้งหนี้ {{docNo}} เรียบร้อยแล้ว',
  p_conv_go: 'ออกใบแจ้งหนี้',
  p_conv_loading: 'กำลังตรวจสอบใบเสนอราคา…',
  p_conv_receipts: 'เงินที่รับมาแล้วตามใบเสนอราคานี้ ({{list}}) จะอยู่ที่เดิม ใบแจ้งหนี้ใบใหม่จึงออกเต็มจำนวนและยังไม่ชำระ',
  p_conv_subtitle: 'ใบแจ้งหนี้จะคัดลอกทุกรายการจากใบเสนอราคา ลงวันที่วันนี้ และเริ่มต้นที่สถานะยังไม่ชำระ',
  p_conv_rejected: 'ใบเสนอราคา {{docNo}} ถูกทำเครื่องหมายว่าปฏิเสธแล้ว: ออกเอกสารเรียกเก็บ แก้ไข หรือส่งไม่ได้จนกว่าจะเปลี่ยนสถานะ',
  p_conv_err_already: 'มีการออก {{nos}} จาก {{docNo}} แล้ว และใบเสนอราคาหนึ่งใบเรียกเก็บได้ครั้งเดียว หากต้องการเรียกเก็บใหม่ กรุณายกเลิกเอกสารนั้นก่อน',
  p_conv_err_tax_unpaid: '{{docNo}} ยังไม่ได้รับชำระ หากออกใบกำกับภาษีตอนนี้จะบันทึกยอดขายนี้ และใบเสร็จของเอกสารนั้นจะบันทึกซ้ำอีกครั้ง กรุณาบันทึกการรับเงินด้วยใบเสร็จก่อนแล้วจึงออกใบกำกับภาษี หรือยกเลิก {{docNo}} แล้วออกใบกำกับภาษีแทน',
  p_conv_err_void: 'ใบเสนอราคานี้ถูกยกเลิกแล้ว กรุณาออกใบใหม่',
  p_conv_err_world_plain: 'ใบเสนอราคานี้ออกเป็นเอกสารทั่วไป แต่บริษัทนี้ออกเอกสารภาษีไทยแล้ว กรุณาออกใบเสนอราคาใหม่แล้วเรียกเก็บจากใบนั้น',
  p_conv_err_world_thai: 'ใบเสนอราคานี้ออกตามกฎภาษีไทย แต่บริษัทนี้ออกเอกสารทั่วไปแล้ว กรุณาออกใบเสนอราคาใหม่แล้วเรียกเก็บจากใบนั้น',
  p_conv_err_customer_gone: 'ลูกค้าใน {{docNo}} ไม่อยู่ในรายชื่อลูกค้าแล้ว กรุณาเพิ่มกลับ หรือออกใบเสนอราคาใหม่',
  p_conv_err_customer: '{{docNo}} เสนอราคาให้ {{customer}} เอกสารที่ออกจากใบนี้ต้องออกให้ลูกค้ารายเดียวกัน',
  p_conv_err_no_lines: 'ใบเสนอราคานี้ไม่มีรายการให้เรียกเก็บ',
  p_conv_err_type: 'บริษัทนี้ไม่ออกเอกสารประเภทนี้จากใบเสนอราคา',
  p_conv_err_not_found: 'ไม่พบใบเสนอราคาที่เอกสารนี้อ้างถึงในเอกสารของบริษัทนี้',
  p_conv_err_receipt_needs_bill: 'ใบเสร็จออกตามเอกสารเรียกเก็บ กรุณาออกใบแจ้งหนี้จาก {{docNo}} ก่อน',
  p_conv_err_nothing_owed: '{{nos}} ไม่มียอดค้างชำระ จึงไม่มีอะไรให้ออกใบเสร็จ',
  p_conv_err_cash_date: 'การขายเงินสดหมายถึงได้รับเงินแล้ว กรุณาระบุวันที่ได้รับเงิน',
  p_conv_err_cash_future: 'การขายเงินสดหมายถึงได้รับเงินแล้ว จึงลงวันที่หลังวันนี้ไม่ได้ กรุณาลงวันที่ที่ได้รับเงินจริง',
  p_conv_err_filed: 'ภาษีมูลค่าเพิ่มของ {{ym}} ถูกทำเครื่องหมายว่ายื่นแล้วในศูนย์ยื่นแบบ เอกสารลงวันที่ {{date}} จะเพิ่มรายการภาษีขายในแบบที่ยื่นไปแล้ว กรุณาเลือกวันที่อื่น หรือยกเลิกเครื่องหมาย {{ym}} ก่อน',
  p_conv_err_repriced_vat: 'ใบเสนอราคานี้มียอดรวม {{quoted}} แต่จะเรียกเก็บเป็น {{total}} เนื่องจากสถานะการจดทะเบียนภาษีมูลค่าเพิ่มของคุณเปลี่ยนไปหลังออกใบเสนอราคา กรุณาออกใบเสนอราคาใหม่เพื่อให้ลูกค้าเห็นยอดใหม่ แล้วจึงเรียกเก็บจากฉบับนั้น',
  p_conv_err_repriced_split: 'ใบเสนอราคานี้เรียกเก็บตามที่เสนอไม่ได้: มีรายการที่เรียกเก็บค่าใช้จ่ายสูงกว่าที่ผู้ขายคิด และส่วนที่บวกเพิ่มจะถูกเรียกเก็บเป็นค่าบริการของคุณพร้อมภาษีมูลค่าเพิ่ม กรุณาออกใบเสนอราคาใหม่ด้วยรายการเหล่านี้เพื่อให้ลูกค้าเห็นยอดใหม่ แล้วจึงเรียกเก็บจากฉบับนั้น การแก้ไขใบเสนอราคานี้จะไม่เปลี่ยนรายการ จึงยังเรียกเก็บไม่ได้',
  p_conv_proformas: 'ออกใบแจ้งหนี้เสนอราคา (Proforma) จากใบเสนอราคานี้แล้ว: {{list}} ใบนี้ไม่ได้เรียกเก็บเงิน จึงไม่ขัดขวางการออกเอกสารครั้งนี้ แต่ลูกค้าอาจได้รับแล้ว',
  p_conv_err_fx_approval: 'ใบกำกับภาษีต้องออกเป็นเงินบาท เว้นแต่กรมสรรพากรอนุมัติสกุลอื่น และไม่มีบันทึกการอนุมัติสำหรับ {{currency}}',
  p_copy_err_customer_gone: 'ลูกค้ารายนี้ไม่อยู่ในรายชื่อลูกค้าแล้ว กรุณาเลือกรายอื่น',
  p_doc_edit_billed: 'มีการออก {{nos}} จากใบเสนอราคานี้แล้ว จึงแก้ไขไม่ได้ กรุณายกเลิกเอกสารนั้นก่อน',
  p_conv_title: 'ออกใบแจ้งหนี้จาก {{docNo}}',
  p_conv_type_invoice: 'ใบแจ้งหนี้ (Invoice)',
  p_conv_type_invoice_hint: 'ใบวางบิล สำหรับบริการ ภาษีมูลค่าเพิ่มถึงกำหนดเมื่อได้รับชำระ (ม.78/1) ใบกำกับภาษีจึงออกพร้อมใบเสร็จรับเงิน',
  p_conv_type_label: 'เอกสารนี้เป็นประเภทใด?',
  p_conv_type_locked: 'เมื่อออกแล้วแก้ไม่ได้ — กำหนดจุดที่ความรับผิดภาษีเกิดขึ้น หากต้องแก้ต้องยกเลิกและออกใหม่',
  p_conv_type_proforma: 'ใบแจ้งหนี้ชั่วคราว (Proforma)',
  p_conv_type_proforma_hint: 'ไม่ใช่เอกสารทางภาษี ไม่ทำให้เกิดจุดที่ความรับผิดภาษี และไม่บันทึกบัญชี',
  p_conv_type_tax_invoice: 'ใบกำกับภาษี (Tax invoice)',
  p_conv_type_tax_invoice_hint: 'สำหรับขายสินค้า ภาษีมูลค่าเพิ่มถึงกำหนดเมื่อส่งมอบสินค้า (ม.78) ภาษีขายจึงบันทึกที่เอกสารฉบับนี้ — ในเดือนนี้',
  p_conv_working: 'กำลังออก…',
  p_conv_fx_rate_missing: '{{docNo}} ออกเป็นสกุล {{currency}} และยังไม่มีอัตราแลกเปลี่ยน จึงยังออกเป็นใบกำกับภาษีไม่ได้ — กรุณาแก้ไขใบเสนอราคา กรอกอัตราแลกเปลี่ยนและวันที่ของอัตรา แล้วจึงออกใบกำกับภาษี ระหว่างนี้จะไม่มีการออกเอกสารและไม่ใช้เลขที่เอกสาร',
  p_doc_act_to_invoice: 'ออกใบแจ้งหนี้',
  p_doc_err_period_closed: 'วันที่ {{date}} อยู่ในรอบระยะเวลาบัญชีที่ปิดแล้ว ({{from}} – {{to}}) — ไม่สามารถออกเอกสารนี้ในรอบนั้นได้ ไม่สามารถบันทึกรายการใดในรอบบัญชีที่ปิดแล้ว',
  p_doc_err_period_closed_edit: '{{docNo}} ลงวันที่ {{date}} ซึ่งอยู่ในรอบระยะเวลาบัญชีที่ปิดแล้ว ({{from}} – {{to}}) — ไม่สามารถแก้ไขได้ เพราะจะทำให้บัญชีที่ปิดแล้วเปลี่ยนแปลง',
  p_doc_err_period_closed_void: '{{docNo}} ลงวันที่ {{date}} ซึ่งอยู่ในรอบระยะเวลาบัญชีที่ปิดแล้ว ({{from}} – {{to}}) — ไม่สามารถยกเลิกหรือลบได้ เพราะจะทำให้บัญชีที่ปิดแล้วเปลี่ยนแปลง',
  p_doc_err_void_receipts_first: '{{docNo}} ยังมีใบเสร็จรับเงินที่ใช้งานอยู่ — โปรดยกเลิกใบเสร็จรับเงินก่อน แล้วจึงยกเลิกเอกสารนี้',
  p_doc_link_self: 'เอกสารฉบับนี้',
  p_doc_th_property: 'ทรัพย์สิน',
  p_doc_th_receipt_no: 'ใบเสร็จรับเงิน',
  p_doc_th_received: 'รับชำระ ($t(cur_word))',
  p_doc_th_sent: 'ส่งแล้ว',
  p_doc_th_status: 'สถานะ',
  p_doc_th_total: 'รวมทั้งสิ้น',
  p_doc_th_wht: 'WHT',
  p_doc_void_prompt: 'ยกเลิก {{docNo}} หรือไม่?\n\nเลขที่เอกสารจะยังคงอยู่ในลำดับโดยมีสถานะ VOID และจะไม่ถูกนำกลับมาใช้ซ้ำ\n\nเหตุผลในการยกเลิกคืออะไร (ระบบจะบันทึกไว้)',
  p_doc_wht_saved: 'บันทึกหนังสือรับรองการหักภาษี ณ ที่จ่ายแล้ว ✓',
  // empty
  p_empty_no_match: 'ไม่พบข้อมูลที่ตรงกับ “{{query}}”',
  // erec
  p_erec_bank: 'บัญชีธนาคารที่แสดงบนเอกสาร',
  p_erec_bank_none: '— ไม่ระบุ —',
  p_erec_received: 'รับชำระ {{amount}}',
  p_erec_remark: 'หมายเหตุ',
  p_erec_saving: 'กำลังบันทึก…',
  p_erec_title: 'แก้ไขใบเสร็จรับเงิน {{docNo}}',
  p_erec_warning: 'ใบเสร็จรับเงินฉบับนี้เป็นใบกำกับภาษี ชื่อลูกค้า จำนวนเงิน ภาษีมูลค่าเพิ่ม และวันที่ เป็นข้อมูลที่ผู้ซื้อใช้เป็นภาษีซื้อ จึงแก้ไขที่นี่ไม่ได้ — หากต้องการแก้ไขข้อมูลเหล่านี้ ให้ยกเลิกใบเสร็จรับเงินฉบับนี้แล้วบันทึกการรับชำระเงินใหม่',
  p_erec_wht: '· หัก ณ ที่จ่าย {{amount}}',
  // err
  p_err_create_failed: 'สร้างไม่สำเร็จ',
  p_err_save_failed: 'บันทึกไม่สำเร็จ',
  // exp
  p_exp_act_remove: 'ลบ',
  p_exp_act_rescan: 'อ่านตัวเลขใหม่',
  p_exp_act_rescan_title: 'อ่านตัวเลขจากไฟล์สแกนนี้ใหม่อีกครั้ง',
  p_exp_act_view: 'ดูไฟล์แนบ',
  p_exp_confirm_remove: 'ลบ {{filename}} หรือไม่?',
  p_exp_empty: 'ยังไม่มีการอัปโหลดสำหรับเดือน {{month}} ปี {{year}}',
  p_exp_err_open: 'ไม่สามารถเปิดไฟล์ได้',
  p_exp_file_fallback: 'ไฟล์',
  p_exp_file_hint: 'รองรับเฉพาะรูปภาพและไฟล์ PDF — ไม่สามารถอ่านไฟล์ Word และ Excel ได้ กรุณาบันทึกเป็น PDF ก่อน เอกสารที่มีหลายหน้าให้ส่งเป็นไฟล์ PDF เดียว ไม่ใช่แยกเป็นรูปหลายรูป',
  p_exp_filed_warn: 'แบบ ภ.พ.30 ของเดือน {{month}} {{year}} ได้ยื่นไปแล้ว เอกสารที่ท่านส่งเข้ามาสำหรับเดือนนี้จะถูกบันทึกเป็นการยื่นเพิ่มเติม ไม่ได้รวมอยู่ในแบบที่ยื่นไปแล้ว',
  p_exp_filed_warn_bold: 'การยื่นเพิ่มเติม',
  p_exp_filter_all: 'ทุกสถานะ',
  p_exp_filter_approved: 'บันทึกบัญชีแล้ว',
  p_exp_filter_pending: 'รอ {{brand}} ดำเนินการ',
  p_exp_filter_rejected: 'ส่งคืนให้ท่าน',
  p_exp_list_title: 'อัปโหลดแล้ว — {{month}} {{year}}',
  p_exp_month: 'เดือน',
  p_exp_msg_appended: '· {{filename}} — เพิ่มเป็นหน้า {{page}} ของบิลเลขที่ {{docNo}}',
  p_exp_msg_ocr_error: 'มีบางไฟล์ที่ระบบอ่านอัตโนมัติไม่ได้ — {{brand}} จะกรอกตัวเลขให้ด้วยตนเอง',
  p_exp_msg_saved: 'ได้รับเอกสาร {{n}} ฉบับแล้ว อยู่ระหว่างรอ {{brand}} ดำเนินการ',
  p_exp_msg_skipped: 'ข้าม {{n}} รายการเนื่องจากซ้ำ:',
  p_exp_msg_skipped_line: '· {{filename}}{{docNo}} — {{why}}',
  p_exp_no_vat: 'ไม่มี VAT',
  p_exp_ocr_error_row: 'ไม่สามารถอ่านข้อมูลอัตโนมัติได้',
  p_exp_page_chip: 'น.{{n}}',
  p_exp_page_title: 'หน้า {{n}}',
  p_exp_quota_unlimited: 'อัปโหลดแล้ว {{used}} รายการในเดือน{{month}} {{year}} · ไม่จำกัดจำนวน',
  p_exp_quota_full: 'ถึงขีดจำกัด {{cap}} รายการต่อเดือนของเดือน{{month}} {{year}} แล้ว — หากต้องการส่งจำนวนมากกว่านี้ กรุณาอีเมลถึง {{contact}}',
  p_exp_quota_left: 'ใช้ไปแล้ว {{used}} จาก {{cap}} สำหรับเดือน {{month}} {{year}} · เหลือ {{left}}',
  p_exp_reading: 'กำลังอ่านเอกสาร…',
  p_exp_search_ph: 'ค้นหาผู้ขาย เลขที่ใบแจ้งหนี้ ชื่อไฟล์…',
  p_exp_status_approved: 'บันทึกบัญชีแล้ว',
  p_exp_status_pending: 'รอ {{brand}} ตรวจสอบ',
  p_exp_status_rejected: 'ส่งกลับให้ท่าน',
  p_exp_status_cancelled: 'ยกเลิกโดยสำนักงาน',
  p_exp_subtitle: 'รูปถ่ายหรือไฟล์ PDF ของบิลค่าใช้จ่ายที่ท่านชำระแล้ว {{brand}} จะตรวจสอบเอกสารแต่ละฉบับกับภาพสแกนก่อนบันทึกเข้าบัญชีของท่าน — การอัปโหลดที่นี่ไม่ได้เปลี่ยนแปลงแบบแสดงรายการภาษีของท่านโดยตัวมันเอง · ส่งใบกำกับภาษีซื้อของท่าน',
  p_exp_th_amount: 'จำนวนเงิน',
  p_exp_th_attachment: 'ไฟล์แนบ',
  p_exp_th_bucket: 'กลุ่มภาษี',
  p_exp_th_date: 'วันที่',
  p_exp_th_docno: 'เลขที่ใบกำกับภาษี',
  p_exp_th_status: 'สถานะ',
  p_exp_th_supplier: 'ผู้ขาย',
  p_exp_th_vat: 'VAT',
  p_exp_title: 'ส่งบิลซื้อ/ใบกำกับภาษีซื้อของท่านมาให้เรา',
  p_exp_upload: '＋ อัปโหลดใบกำกับภาษีซื้อ',
  p_exp_upload_failed: 'อัปโหลดไม่สำเร็จ',
  p_exp_with_vat: 'มี VAT',
  p_exp_year: 'ปี',
  // fil
  p_fil_chip_additional: 'A{{n}}',
  p_fil_chip_attachment: 'เอกสารแนบ',
  p_fil_chip_form: 'แบบยื่น',
  p_fil_chip_receipt: 'ใบเสร็จรับเงิน',
  p_fil_legend: 'เขียว = ยื่นแล้ว มีเอกสารให้ดูด้านล่าง · แบบยื่น = แบบแสดงรายการภาษีที่ยื่นแล้ว · ใบเสร็จรับเงิน = ใบเสร็จรับเงินค่าภาษีจากกรมสรรพากร · ✓ = ยื่นแล้ว แต่ยังไม่ได้อัปโหลดเอกสาร หากไม่พบเอกสารที่ต้องการ กรุณาติดต่อ {{brand}} แล้วเราจะเพิ่มให้',
  p_fil_loading: 'กำลังโหลด…',
  p_fil_subtitle: 'แบบภาษีที่ {{brand}} ยื่นให้ท่าน แยกตามเดือน คลิกที่เดือนเพื่อเปิดแบบที่ยื่นแล้วหรือใบเสร็จรับเงินของกรมสรรพากร',
  p_fil_th_filing: 'แบบแสดงรายการภาษี',
  p_fil_title: 'ภาษีและการยื่นแบบ',
  // filing
  p_filing_doc_gone: 'เอกสารนี้ไม่พร้อมใช้งานแล้ว กรุณาติดต่อ {{brand}}',
  // img
  p_img_err_generic: 'ไม่สามารถอ่านรูปภาพนี้ได้',
  p_img_err_not_image: 'ไฟล์นี้ไม่ใช่รูปภาพ',
  p_img_err_read: 'ไม่สามารถอ่านไฟล์ได้',
  p_img_none: 'ไม่มี',
  p_img_remove: 'ลบออก',
  p_img_replace: 'เปลี่ยนรูป',
  p_img_upload: 'อัปโหลด',
  // imp
  p_imp_already_bold: 'มีเลขที่เอกสารซ้ำอยู่แล้ว {{n}} รายการ',
  p_imp_already_rest: 'และจะทำให้การนำเข้าหยุดลง: {{list}}',
  p_imp_btn: 'นำเข้า',
  p_imp_btn_n: 'นำเข้า {{n}} รายการ',
  p_imp_confirm: 'สร้าง{{docTypeWord}}จำนวน {{n}} ฉบับ โดยใช้เลขที่เอกสารในไฟล์หรือไม่?\n\nระบบจะสร้างข้อมูลลูกค้าใหม่ {{m}} ราย\n\nไม่สามารถย้อนกลับทั้งชุดได้ — ต้องยกเลิกเอกสารทีละฉบับ',
  p_imp_create_as: 'สร้างเป็น',
  p_imp_done: '✓ สร้างใบแจ้งหนี้ {{created}} ฉบับ · ลูกค้าใหม่ {{customers}} ราย',
  p_imp_done_btn: 'เสร็จสิ้น',
  p_imp_err_failed: 'นำเข้าไม่สำเร็จ',
  p_imp_err_period_closed: 'ไม่ได้นำเข้าข้อมูลใดเลย — {{n}} จาก {{rows}} แถวในชีตนี้ลงวันที่อยู่ในรอบระยะเวลาบัญชีที่ปิดแล้ว ({{periods}}) ไม่สามารถบันทึกรายการใดในรอบบัญชีที่ปิดแล้ว โปรดนำแถวเหล่านั้นออกจากชีตแล้วนำเข้าใหม่',
  p_imp_err_read: 'ไม่สามารถอ่านไฟล์นั้นได้',
  p_imp_open: 'นำเข้าจากสเปรดชีต',
  p_imp_map_title: 'จับคู่คอลัมน์ในสเปรดชีตกับฟิลด์ของระบบ',
  p_imp_map_saved: '· ใช้การจับคู่ที่คุณบันทึกไว้สำหรับชีตนี้',
  p_imp_map_edit: 'แก้ไขการจับคู่',
  p_imp_map_hide: 'ซ่อนการจับคู่',
  p_imp_map_th_column: 'คอลัมน์ในสเปรดชีต',
  p_imp_map_th_sample: 'ตัวอย่างข้อมูล',
  p_imp_map_th_field: 'จับคู่เป็น',
  p_imp_map_th_vat: 'การคิด VAT',
  p_imp_map_th_label: 'ชื่อรายการในใบแจ้งหนี้',
  p_imp_map_no_header: 'คอลัมน์ที่ {{n}} (ไม่มีหัวตาราง)',
  p_imp_map_required: 'ยังไม่ได้จับคู่คอลัมน์ใดกับ "{{field}}" ซึ่งทุกเอกสารต้องมี',
  p_imp_map_need_amount: 'ต้องจับคู่อย่างน้อยหนึ่งคอลัมน์ที่เป็นจำนวนเงิน เพราะเป็นรายการในใบแจ้งหนี้',
  p_imp_field_ignore: 'ไม่นำเข้า',
  p_imp_field_customer: 'ชื่อลูกค้า',
  p_imp_field_docNo: 'เลขที่ใบแจ้งหนี้',
  p_imp_field_issueDate: 'วันที่ออก',
  p_imp_field_dueDate: 'วันครบกำหนด',
  p_imp_field_address: 'ที่อยู่บนเอกสาร',
  p_imp_field_description: 'รายละเอียด',
  p_imp_field_remark: 'หมายเหตุ',
  p_imp_field_fee: 'จำนวนเงิน (เป็นหนึ่งรายการ)',
  p_imp_field_total: 'ยอดรวมในไฟล์ (ใช้ตรวจทานเท่านั้น)',
  p_imp_fee_q: 'ค่าธรรมเนียมแต่ละคอลัมน์เสียภาษีอย่างไร?',
  p_imp_fee_warn: '— หากกรอกผิด จะทำให้ยอดที่ผู้ซื้อทุกรายต้องชำระคลาดเคลื่อน 7%',
  p_imp_file_label: 'ไฟล์สเปรดชีต (.xlsx)',
  p_imp_header_incl: '· หัวคอลัมน์ระบุว่า “รวม VAT”',
  p_imp_header_unknown: '· หัวคอลัมน์ไม่ได้ระบุ — โปรดยืนยัน',
  p_imp_mismatch_body: 'ใบแจ้งหนี้จะออกตามคอลัมน์ค่าบริการด้านบน ส่วนยอดรวมในไฟล์อาจเป็นคนละตัวเลข กรุณาตรวจสอบบางรายการก่อนนำเข้า:',
  p_imp_mismatch_bold: '{{n}} แถวไม่ตรงกับคอลัมน์ยอดรวมในไฟล์',
  p_imp_mismatch_line: 'แถว {{n}} · {{docNo}} · ใบแจ้งหนี้ {{a}} เทียบกับไฟล์ {{b}}',
  p_imp_more: '… และอีก {{n}} รายการ',
  p_imp_prev_th_customer: 'ลูกค้า',
  p_imp_prev_th_date: 'วันที่',
  p_imp_prev_th_lines: 'รายการ',
  p_imp_prev_th_total: 'รวม',
  p_imp_sheet: 'ชีต',
  p_imp_skipped_head: 'ข้าม — รายการเหล่านี้จะไม่ถูกนำเข้า',
  p_imp_skipped_row: 'แถว {{n}}',
  p_imp_skipped_title: 'แถวที่ข้าม — ไม่ได้สร้างเอกสารสำหรับรายการเหล่านี้:',
  p_imp_subtitle: 'หนึ่งแถวต่อหนึ่งใบแจ้งหนี้ เลขที่เอกสาร วันที่ และจำนวนเงิน จะถูกนำมาจากไฟล์ของท่านตามเดิมทุกประการ ไม่มีการสร้างขึ้นใหม่หรือปรับแก้',
  p_imp_summary: 'จะสร้างใบแจ้งหนี้ {{rows}} ใบ · ลูกค้าใหม่ {{customers}} ราย · รวม {{amount}} $t(cur_word)',
  p_imp_summary_skipped: 'ข้าม {{skipped}} แถว',
  p_imp_th_docno: 'เลขที่ใบแจ้งหนี้',
  p_imp_th_owner: 'เจ้าของ',
  p_imp_th_property: 'ทรัพย์สิน',
  p_imp_th_row: 'แถว',
  p_imp_th_why: 'เหตุผล',
  p_imp_title: 'นำเข้าใบแจ้งหนี้จากไฟล์สเปรดชีต',
  p_imp_type_inv: 'ใบแจ้งหนี้ — วางบิลเท่านั้น ใบกำกับภาษีจะออกเมื่อได้รับชำระเงิน',
  p_imp_type_ti: 'ใบกำกับภาษี / ใบส่งของ — ความรับผิดในการเสียภาษีมูลค่าเพิ่มเกิดขึ้นเมื่อส่งมอบสินค้า',
  p_imp_type_tir: 'ใบกำกับภาษี/ใบเสร็จรับเงิน — ขายเงินสด ชำระเงินแล้ว',
  p_imp_word_invoices: 'ใบแจ้งหนี้',
  p_imp_word_tax_invoices: 'ใบกำกับภาษี',
  p_imp_working: 'กำลังดำเนินการ…',
  // lang
  p_lang_en: 'English',
  p_lang_en_short: 'EN',
  p_lang_th_short: 'ไทย',
  p_lang_zh_short: '中',
  // loading
  p_loading: 'กำลังโหลด…',
  // login
  p_login_email: 'อีเมล',
  p_login_err_failed: 'เข้าสู่ระบบไม่สำเร็จ',
  p_login_err_staff: 'นี่คือพอร์ทัลสำหรับลูกค้า — พนักงานกรุณาใช้ระบบภายใน',
  p_cdoc_line_account: 'รายการนี้ลงบัญชีรายได้นี้ (ตั้งค่าต่อบริการที่หน้า Products & accounts)',
  p_cdoc_line_account_default: 'ค่าเริ่มต้นจากบริการ ({{code}})',
  p_cdoc_line_account_none: 'บัญชี — ยังไม่เลือก',
  // Cross-installation sign-in (owner 2026-10-15)
  p_login_err_blank: 'กรุณากรอกอีเมลและรหัสผ่าน',
  p_login_hop_text: 'บัญชีนี้อยู่กับสำนักงานอีกแห่งของบริษัท ({{host}}) ต้องการเข้าสู่ระบบที่นั่นหรือไม่?',
  p_login_hop_go: 'ใช่ เข้าสู่ระบบที่นั่น',
  p_login_hop_cancel: 'ยกเลิก',
  p_login_password: 'รหัสผ่าน',
  p_login_signin: 'เข้าสู่ระบบ',
  p_login_signing_in: 'กำลังเข้าสู่ระบบ…',
  p_login_subtitle: 'ระบบออกเอกสารสำหรับธุรกิจของท่าน',
  p_login_title: 'พอร์ทัลลูกค้า {{brand}}',
  // logo
  p_logo_alt: '{{brand}}',
  // menu
  p_menu_actions_tooltip: 'การดำเนินการ',
  p_menu_bills: 'ใบวางบิลจาก {{brand}}',
  p_menu_bills_hint: 'ค่าบริการที่ {{brand}} เรียกเก็บจากท่าน',
  p_menu_customers: 'ลูกค้า',
  p_menu_customers_hint: 'ผู้ซื้อที่ท่านออกใบแจ้งหนี้ให้',
  p_menu_filings: 'ภาษีและการยื่นแบบ',
  p_menu_filings_hint: 'แบบแสดงรายการภาษีที่ {{brand}} ยื่นให้ท่าน',
  p_menu_group_fromslf: 'จาก {{brand}}',
  p_menu_group_mydata: 'ข้อมูลของฉัน',
  p_menu_group_company: 'บริษัท',
  p_menu_products: 'สินค้าและบริการ',
  p_menu_products_hint: 'สิ่งที่ท่านขาย',
  p_menu_profile: 'ข้อมูลบริษัท',
  p_menu_profile_hint: 'ที่อยู่ โลโก้ การออกเลขที่เอกสาร',
  p_menu_tooltip: 'เมนู',
  p_menu_working: 'กำลังดำเนินการ…',
  // month
  p_month_apr: 'เม.ย.',
  p_month_aug: 'ส.ค.',
  p_month_dec: 'ธ.ค.',
  p_month_feb: 'ก.พ.',
  p_month_jan: 'ม.ค.',
  p_month_jul: 'ก.ค.',
  p_month_jun: 'มิ.ย.',
  p_month_mar: 'มี.ค.',
  p_month_may: 'พ.ค.',
  p_month_nov: 'พ.ย.',
  p_month_oct: 'ต.ค.',
  p_month_sep: 'ก.ย.',
  // nav
  p_nav_bankstmt: 'รายการเดินบัญชี',
  p_nav_expenses: 'ใบกำกับภาษีซื้อ',
  p_office_label: 'สำนักงาน {{brand}} ของท่าน',
  p_office_hint: 'สำนักงาน {{brand}} ที่ดูแลบัญชีและออกเอกสารให้ท่าน',
  p_nav_owner: 'แดชบอร์ดผู้บริหาร',
  p_nav_backoffice: 'สมุดบัญชี',
  p_nav_backoffice_hint: 'เปิดสมุดบัญชีของบริษัทนี้ในพื้นที่ทำงานบัญชี จะเปิดแท็บใหม่และต้องเข้าสู่ระบบด้วยบัญชีพนักงาน',
  p_nav_mybooks: 'สมุดบัญชีของฉัน',
  p_nav_mybooks_hint: 'สมุดรายวัน งบทดลอง และงบการเงินของท่านตามที่เราจัดทำ อ่านอย่างเดียว',
  p_own_title: 'แดชบอร์ดผู้บริหาร',
  p_own_lede: 'ภาพรวมธุรกิจของคุณ — ใครค้างชำระคุณ คุณค้างเท่าไร และอะไรถึงกำหนดถัดไป',
  p_own_empty: 'ยังไม่มีข้อมูลแสดงที่นี่ หน้านี้จะเริ่มมีข้อมูลเมื่อคุณออกใบแจ้งหนี้ หรือเมื่อเราเริ่มยื่นแบบให้คุณ',
  p_own_owed_you: 'ลูกค้าค้างชำระคุณ',
  p_own_overdue_suffix: 'เกินกำหนด',
  p_own_chase: 'ค้างนานที่สุด',
  p_own_days_late: 'เกินกำหนด {{days}} วัน',
  p_own_owed_slf: 'คุณค้างชำระ {{brand}}',
  p_own_next_due: 'ครบกำหนดถัดไป {{date}}',
  p_own_waiting: 'รอคุณดำเนินการ',
  p_own_and_more: 'และอีก {{n}} รายการ',
  p_own_task_upload: 'ส่งเอกสารประจำเดือนให้เรา',
  p_own_task_tax_pay: 'ชำระภาษี',
  p_own_task_svc_pay: 'ชำระค่าบริการของเรา',
  p_own_task_info: 'ส่งข้อมูลที่เราขอ',
  p_own_upcoming: 'ถึงกำหนดถัดไป',
  p_own_today: 'วันนี้',
  p_own_in_days: 'อีก {{days}} วัน',
  p_own_amount_tbc: 'รอสรุปยอด',
  p_own_amount_credit: 'เครดิตยกไป {{amount}}',
  p_own_efiling_note: 'วันที่แสดงคือกำหนดยื่นแบบออนไลน์ของกรมสรรพากร ซึ่งช้ากว่ากำหนดยื่นที่สำนักงาน',
  p_own_booked: 'บันทึกบัญชีปี {{year}}',
  p_own_revenue: 'ยอดขาย',
  p_own_purchases: 'ยอดซื้อ',
  p_own_booked_note: 'จาก {{months}} เดือนที่บันทึกแล้ว ไม่ใช่ตัวเลขกำไร เงินเดือนจะรวมเมื่อปิดเดือนแล้วเท่านั้น',
  p_own_filed: 'ยื่นแบบให้คุณแล้วในปี {{year}}',
  p_own_filed_note: 'แบบที่เรายื่นแล้ว',
  p_nav_books: 'สมุดบัญชีของคุณ',
  p_menu_company_find: 'ค้นหาบริษัท…',
  p_menu_books_locked: 'จะแสดงเมื่อเปิดบัญชีของคุณแล้ว',
  p_books_period: 'งวดบัญชี',
  p_menu_company_none: 'ไม่พบบริษัทที่ตรงกัน',
  p_menu_company_n_of: '{{n}} จาก {{total}}',
  p_menu_policy: 'เราบันทึกบัญชีของคุณอย่างไร',
  p_menu_policy_hint: 'แนวปฏิบัติที่เราใช้กับเอกสารของคุณจริง',
  p_menu_chart: 'ผังบัญชี',
  p_menu_chart_hint: 'บัญชีที่ใช้บันทึกรายการของคุณ',
  p_menu_ledgers: 'บัญชีแยกประเภทและงบทดลอง',
  p_menu_ledgers_hint: 'สมุดรายวันทั้งห้าเล่ม บัญชีแยกประเภท และงบทดลอง',
  p_books_no_entries: 'ไม่มีรายการในงวดนี้',
  p_menu_statements: 'งบการเงิน',
  p_menu_statements_hint: 'งบแสดงฐานะการเงิน งบกำไรขาดทุน งบแสดงการเปลี่ยนแปลงส่วนของเจ้าของ',
  p_pol_title: 'เราบันทึกบัญชีของคุณอย่างไร',
  p_pol_lede: 'หน้านี้อธิบายสิ่งที่ระบบของเราทำกับเอกสารของคุณจริง เป็นการอธิบายการทำงานของระบบ ไม่ใช่การอ้างอิงตัวบทกฎหมาย ในส่วนที่เกี่ยวข้องกับกฎหมาย ให้ถือความเห็นเป็นลายลักษณ์อักษรจากผู้ทำบัญชีของคุณเป็นสำคัญ',
  p_pol_readonly: 'หน้านี้เป็นแบบอ่านอย่างเดียว',
  p_pol_s1_t: 'รายได้ของคุณเข้าบัญชีเมื่อใด',
  p_pol_s1_b: 'ใบกำกับภาษี (รวมใบกำกับภาษี/ใบเสร็จรับเงิน) บันทึกเป็นภาษีขายในเดือนที่ออกเอกสาร ส่วนใบแจ้งหนี้ทั่วไปจะบันทึกต่อเมื่อได้รับชำระเงินและบันทึกใบเสร็จรับเงินแล้วเท่านั้น ดังนั้นใบแจ้งหนี้ที่ยังไม่ได้รับชำระในเดือนนี้จะไม่ปรากฏในฐานภาษีขายของเดือนนี้ และหากไม่ได้รับชำระเลยก็จะไม่ปรากฏเลย',
  p_pol_s2_t: 'ภาษีหัก ณ ที่จ่าย บันทึกเมื่อใด',
  p_pol_s2_b: 'ภาษีที่ลูกค้าหักไว้ เราบันทึกเมื่อได้รับเงิน ไม่ใช่ในเดือนที่ออกใบแจ้งหนี้ ยอดลูกหนี้ของคุณจึงแสดงจำนวนเต็ม ไม่ใช่จำนวนที่จะเข้าบัญชีธนาคารจริง',
  p_pol_s3_t: 'ใบลดหนี้และใบเพิ่มหนี้',
  p_pol_s3_b: 'การแก้ไขมูลค่าของใบกำกับภาษีที่ออกไปแล้ว เราจะออกใบลดหนี้หรือใบเพิ่มหนี้แยกต่างหาก และบันทึกในเดือนของเอกสารนั้นเอง บันทึกของใบกำกับภาษีฉบับเดิมจะไม่ถูกแก้ไข ซึ่งเป็นร่องรอยที่ผู้สอบบัญชีต้องการเห็น',
  p_pol_s4_t: 'เงินตราต่างประเทศ',
  p_pol_s4_b: 'ระบบของเราไม่ได้เก็บอัตราแลกเปลี่ยนไว้เอง อัตราแลกเปลี่ยนบนเอกสารสกุลต่างประเทศกรอกโดยผู้ออกเอกสารเป็นรายฉบับ และอัตราที่ต้องใช้ ณ สิ้นปีต้องมีผู้ให้ข้อมูลแก่เรา หากไม่มีอัตรา เราจะไม่แปลงค่าและจะไม่ประมาณตัวเลขขึ้นมาเอง ยอดลูกหนี้สกุลต่างประเทศจึงแสดงแยกตามสกุลเงิน และไม่รวมเป็นยอดเงิน$t(cur_word)ยอดเดียว',
  p_pol_s5_t: 'ภาษีซื้อที่ขอคืนไม่ได้',
  p_pol_s5_b: 'ภาษีซื้อที่ขอคืนไม่ได้ จะบันทึกเป็นค่าใช้จ่ายในบัญชีค่าใช้จ่ายที่แยกไว้สำหรับภาษีซื้อที่ขอคืนไม่ได้ แทนที่จะคงไว้ปะปนกับภาษีซื้อที่ขอคืนได้',
  p_pol_s6_t: 'การตีราคาสินค้าคงเหลือ',
  p_pol_s6_b: 'รองรับวิธีคิดต้นทุนสองวิธี คือ เข้าก่อนออกก่อน (FIFO) และถัวเฉลี่ยถ่วงน้ำหนัก เมื่อตีราคางวดใดด้วยวิธีหนึ่งแล้ว เราไม่สามารถเปลี่ยนให้คุณเป็นอีกวิธีได้ และเมื่อบันทึกสินค้าคงเหลือไม่สอดคล้องกัน เช่น มีการจ่ายออกโดยไม่มีการรับเข้า หรือจ่ายออกมากกว่าที่รับเข้า เราจะไม่ออกผลการตีราคาให้ แทนที่จะให้ตัวเลขที่ไม่มีที่มา',
  p_pol_s7_t: 'ค่าเสื่อมราคา',
  p_pol_s7_b: 'ค่าเสื่อมราคาคำนวณด้วยวิธีเส้นตรง และเฉลี่ยตามจำนวนวันที่คุณถือครองทรัพย์สินจริงภายในงวด โดยคิดปีละ 365 วัน จะไม่ตัดค่าเสื่อมจนต่ำกว่ามูลค่าคงเหลือ และจะไม่ผ่านรายการเอง ทุกรายการจัดทำเป็นฉบับร่างและมีเจ้าหน้าที่ตรวจสอบก่อนเข้าสู่บัญชีของคุณ อัตราขั้นสูงที่เราใช้แสดงด้านล่าง',
  p_pol_s7_note: 'ตารางนี้เป็นอัตราขั้นสูง หากทรัพย์สินใดไม่ได้กำหนดอัตราเฉพาะ จะใช้อัตราขั้นสูงของประเภทนั้น',
  p_pol_s8_t: 'ผลขาดทุนยกไป',
  p_pol_s8_b: 'เราบันทึกผลขาดทุนยกไปของคุณเป็นรายปี ช่องปีที่สิ้นสุดสิทธิแสดงว่า "ยังไม่ได้กำหนด" เนื่องจากเรายังไม่มีแหล่งอ้างอิงที่ยืนยันได้ การบันทึกระยะเวลาดังกล่าวต้องบันทึกฐานทางกฎหมายของมันไปพร้อมกัน',
  p_pol_s9_t: 'เอกสารที่คุณส่งมาจะถูกดำเนินการอย่างไร',
  p_pol_s9_b: 'เอกสารทุกฉบับจะถูกจัดเป็นหนึ่งในสามประเภท จากนั้นจะกลายเป็นรายการฉบับร่างซึ่งมีเจ้าหน้าที่ตรวจสอบก่อนผ่านรายการ รายการที่ผ่านแล้วจะไม่ถูกลบ หากมีข้อผิดพลาดจะแก้ไขด้วยรายการกลับรายการที่มองเห็นได้ ทุกรายการจะลงในสมุดรายวันหนึ่งในห้าเล่ม และรายการธนาคารจะระบุบัญชีที่เกี่ยวข้อง',
  p_pol_th_class: 'ประเภททรัพย์สิน',
  p_pol_th_rate: 'อัตราขั้นสูง',
  p_pol_not_dep: 'ไม่คิดค่าเสื่อมราคา',
  p_books_none_t: 'ยังไม่ได้จัดทำสมุดบัญชีของคุณ',
  p_books_none_b: 'เมื่อเราเปิดงวดบัญชีให้คุณและผ่านรายการชุดแรกแล้ว ผังบัญชี สมุดบัญชี และงบการเงินของคุณจะปรากฏที่นี่',
  p_nav_invoices: 'ใบแจ้งหนี้',
  p_nav_notes: 'ใบลดหนี้ / ใบเพิ่มหนี้',
  p_nav_quotations: 'ใบเสนอราคา',
  p_nav_receipts: 'ใบเสร็จรับเงิน',
  p_nav_sign_out: 'ออกจากระบบ',
  // note
  p_note_amount_ph: 'จำนวนเงิน',
  p_note_difference_label: 'ผลต่างของมูลค่า',
  p_note_err: 'ออกใบลดหนี้/ใบเพิ่มหนี้ไม่สำเร็จ',
  p_note_info: 'กรอกเฉพาะส่วนต่างที่เอกสารนี้ปรับปรุง ไม่ใช่ยอดเต็มของเอกสารต้นฉบับ ภาษีมูลค่าเพิ่มของส่วนต่างนั้นจะไปปรับภาษีขายในเดือนที่ออกเอกสารนี้',
  p_note_issue_btn: 'ออกเอกสาร',
  p_note_issue_date: 'วันที่ออกเอกสาร',
  p_note_issuing: 'กำลังออกเอกสาร…',
  p_note_item_ph: 'รายการที่ต้องการปรับปรุง',
  p_note_kind_credit: 'ใบลดหนี้',
  p_note_kind_debit: 'ใบเพิ่มหนี้',
  p_note_no_sources: 'ยังไม่มีใบกำกับภาษีในระบบ — ใบลดหนี้/ใบเพิ่มหนี้ใช้ปรับปรุงได้เฉพาะเอกสารที่มีภาษีมูลค่าเพิ่มเท่านั้น',
  p_note_prefilled: 'ระบบได้กรอกรายการตาม {{docNo}} ที่ออกไว้ โดยแต่ละรายการคงการคิดภาษีมูลค่าเพิ่มเดิมไว้ กรุณาแก้จำนวนหรือยอดเงินของแต่ละรายการให้เป็นส่วนที่ลดหนี้ — คงรายการไว้ตามเดิมเฉพาะเมื่อลดหนี้ทั้งจำนวนเท่านั้น',
  p_note_prefilled_left: 'ระบบได้กรอกเฉพาะยอดที่ยังลดหนี้ได้ของ {{docNo}}: คงเหลือ {{left}} จาก {{total}} — ลดหนี้ไปแล้ว {{credited}} ({{nos}}) แต่ละรายการคงการคิดภาษีมูลค่าเพิ่มเดิมไว้ หากต้องการลดหนี้น้อยกว่านี้ให้แก้ยอดของรายการนั้นให้ลดลง',
  p_note_nothing_left: '{{docNo}} ไม่มียอดคงเหลือให้ลดหนี้แล้ว: ลดหนี้ครบทั้ง {{total}} แล้ว ({{nos}})',
  p_note_cap_line: 'รายการ "{{line}}" ของ {{docNo}} ยังลดหนี้ได้อีก {{left}} (เรียกเก็บ {{charged}} ลดหนี้ไปแล้ว {{credited}}) แต่เอกสารนี้จะลดหนี้รายการนี้ {{takes}}',
  p_note_cap_doc: '{{docNo}} ยังลดหนี้ได้อีก {{left}} (ออกไว้ {{total}} ลดหนี้ไปแล้ว {{credited}}) แต่ใบลดหนี้นี้จะลดหนี้ {{takes}}',
  p_note_cap_doc_by: '{{docNo}} ยังลดหนี้ได้อีก {{left}} (ออกไว้ {{total}} ลดหนี้ไปแล้ว {{credited}} โดย {{nos}}) แต่ใบลดหนี้นี้จะลดหนี้ {{takes}}',
  p_note_unlinked: 'ใบลดหนี้ก่อนหน้าของ {{docNo}} ({{nos}}) ไม่ได้ระบุว่าลดหนี้รายการใด ระบบจึงไม่กรอกรายการให้ ยอดคงเหลือที่ลดหนี้ได้คือ {{left}} จาก {{total}} — กรุณากรอกรายการที่ลดหนี้',
  p_note_rcpt_settles: '{{rcptNo}} เป็นใบเสร็จรับเงินของใบกำกับภาษี {{invNo}} และไม่ได้แสดงภาษีมูลค่าเพิ่มของตัวเอง — ใบลดหนี้/ใบเพิ่มหนี้ใช้ปรับปรุงใบกำกับภาษี',
  p_note_rcpt_use: 'ปรับปรุง {{invNo}} แทน',
  p_note_opt_credit: 'ใบลดหนี้ — ลดมูลค่าตามเอกสารเดิม',
  p_note_opt_debit: 'ใบเพิ่มหนี้ — เพิ่มมูลค่า',
  p_note_reason_discount: 'ลดราคาสินค้าหรือบริการหลังออกใบกำกับภาษี',
  p_note_reason_extra_goods: 'ส่งมอบสินค้าหรือให้บริการเพิ่มเติม',
  p_note_reason_goods_defective: 'สินค้าชำรุดบกพร่องหรือไม่ตรงตามที่ตกลง',
  p_note_reason_goods_returned: 'สินค้าถูกส่งคืน',
  p_note_reason_label: 'เหตุผล * (จะพิมพ์บนใบลดหนี้/ใบเพิ่มหนี้)',
  p_note_reason_other: 'อื่น ๆ',
  p_note_reason_other_ph: 'ระบุเหตุผล',
  p_note_reason_overcharge: 'คำนวณราคาสูงกว่าที่เป็นจริง',
  p_note_reason_service_not_provided: 'บอกเลิกการให้บริการบางส่วนหรือทั้งหมด',
  p_note_reason_undercharge: 'คำนวณราคาต่ำกว่าที่เป็นจริง',
  p_note_ref: 'ใบกำกับภาษีที่ต้องการปรับปรุง *',
  p_note_title_new: '{{noteKind}}ใหม่',
  p_note_total_difference: 'ส่วนต่าง',
  p_note_wht_title: 'อัตราภาษีหัก ณ ที่จ่ายที่เอกสารนี้จะพิมพ์สำหรับรายการนี้ รายการที่กรอกจากเอกสารต้นฉบับจะใช้อัตราเดิมของรายการนั้น หากต้องการให้พิมพ์อัตราอื่นให้แก้ที่นี่',
  p_note_wht_printed: 'เอกสารนี้จะพิมพ์: ภาษีหัก ณ ที่จ่าย (WHT Amount) {{wht}} · ยอดชำระ (Payment Amount) {{net}} — คำนวณจากอัตราหัก ณ ที่จ่ายของแต่ละรายการข้างต้น',
  // รอบที่สี่ (2026-10-20)
  p_note_cap_doc_dn: '{{docNo}} ยังลดหนี้ได้อีก {{left}} (ออกไว้ {{issued}} + เพิ่มหนี้ {{debited}} โดย {{dnNos}} ลดหนี้ไปแล้ว {{credited}}) แต่ใบลดหนี้นี้จะลดหนี้ {{takes}}',
  p_note_cap_doc_dn_by: '{{docNo}} ยังลดหนี้ได้อีก {{left}} (ออกไว้ {{issued}} + เพิ่มหนี้ {{debited}} โดย {{dnNos}} ลดหนี้ไปแล้ว {{credited}} โดย {{nos}}) แต่ใบลดหนี้นี้จะลดหนี้ {{takes}}',
  p_note_vatcap_line: 'รายการ "{{line}}" ของ {{docNo}} ยังลดภาษีมูลค่าเพิ่มได้อีก {{left}} (เรียกเก็บภาษี {{charged}} ลดไปแล้ว {{credited}}) แต่เอกสารนี้จะลดภาษีของรายการนี้ {{takes}}',
  p_note_vatcap_doc: '{{docNo}} ยังลดภาษีขายได้อีก {{left}} (เรียกเก็บภาษี {{total}} ลดไปแล้ว {{credited}}) แต่ใบลดหนี้นี้จะลดภาษี {{takes}}',
  p_note_cat_locked: 'รายการที่ {{n}} ลดหนี้ "{{line}}" ของ {{docNo}} ซึ่งเรียกเก็บแบบ {{category}} ใบลดหนี้ต้องลดหนี้รายการตามประเภทภาษีมูลค่าเพิ่มที่เรียกเก็บไว้ — กรุณาคง {{category}} ไว้ในรายการนี้',
  p_note_cat_locked_title: 'รายการที่กรอกจากเอกสารที่ลดหนี้จะคงประเภทภาษีมูลค่าเพิ่มที่เรียกเก็บไว้ — ใบลดหนี้ลดหนี้ตามที่เรียกเก็บ',
  p_note_debited: 'ยอดที่ลดหนี้ได้ของ {{docNo}} มี {{debited}} ที่เพิ่มโดยใบเพิ่มหนี้ {{dnNos}} — รายการของใบเพิ่มหนี้กรอกไว้ต่อจากรายการของเอกสาร',
  p_note_vatcap_doc_dn: '{{docNo}} ยังลดภาษีขายได้อีก {{left}} (เรียกเก็บภาษี {{issued}} + เพิ่ม {{debited}} โดย {{dnNos}} ลดไปแล้ว {{credited}}) แต่ใบลดหนี้นี้จะลดภาษี {{takes}}',
  p_note_debited_in: 'ยอด {{total}} ของ {{docNo}} รวม {{debited}} ที่เพิ่มโดยใบเพิ่มหนี้ {{dnNos}}',
  p_cdoc_edit_stale: 'หน้านี้ไม่เป็นปัจจุบัน: {{docNo}} มีรายการส่วนที่บวกเพิ่ม (mark-up) และหน้านี้ไม่ได้ระบุว่าแต่ละรายการมาจากรายการที่บันทึกไว้รายการใด การบันทึกจะทำให้เครื่องหมายของรายการส่วนที่บวกเพิ่มหายไป ยังไม่ได้บันทึกสิ่งใด — กรุณาโหลดหน้าใหม่แล้วแก้ไขอีกครั้ง',
  p_cdoc_fx_rate_missing: 'ใบกำกับภาษีนี้ออกเป็นสกุล {{currency}}: กรุณากรอกอัตราแลกเปลี่ยนและวันที่ของอัตราก่อนออกเอกสาร ยังไม่ได้ออกเอกสารและไม่ได้ใช้เลขที่เอกสาร',
  // ── COA-D6 (เจ้าของ 28/10): ใบกำกับภาษีที่ออกสำหรับใบแจ้งหนี้ที่ชำระแล้ว — ไม่บันทึกบัญชีซ้ำ ──
  p_cdoc_taxfor_label: 'สำหรับใบแจ้งหนี้ที่ชำระแล้วฉบับใด',
  p_cdoc_taxfor_optional: '(เลือกเฉพาะเมื่อการขายนี้ออกใบแจ้งหนี้ไปแล้วและได้รับชำระแล้ว)',
  p_cdoc_taxfor_none: '— ไม่เลือก: การขายใหม่ (ใบกำกับภาษีนี้บันทึกการขาย) —',
  p_cdoc_taxfor_none_avail: '— ไม่เลือก: ลูกค้ารายนี้ไม่มีใบแจ้งหนี้ที่ชำระแล้วซึ่งยังไม่ได้ออกใบกำกับภาษี —',
  p_cdoc_taxfor_pick_customer: '— กรุณาเลือกลูกค้าก่อน —',
  p_cdoc_taxfor_opt: '{{docNo}} · ชำระแล้ว {{paid}} · {{receiptNos}}',
  p_cdoc_taxfor_hint: 'หากการขายนี้ออกใบแจ้งหนี้ให้ลูกค้ารายนี้ไปแล้วและลูกค้าชำระแล้ว ให้เลือกใบแจ้งหนี้นั้น: ใบกำกับภาษีจะออกสำหรับใบแจ้งหนี้นั้น ไม่บันทึกบัญชีซ้ำ และระบุว่า “สำหรับ INV-… / RE-…”',
  p_cdoc_taxfor_note: 'ใบกำกับภาษีนี้ออกสำหรับ {{docNo}} / {{receiptNos}} การขายนี้บันทึกบัญชีแล้วผ่าน {{receiptNos}} ใบกำกับภาษีนี้จึงไม่บันทึกบัญชีซ้ำ — ไม่มีรายได้ ภาษีขาย หรือลูกหนี้เพิ่ม — และต้องมียอดตรงกับที่ได้รับชำระทุกประการ: {{paid}} ภาษีมูลค่าเพิ่ม {{paidVat}}',
  p_cdoc_taxfor_edit_note: 'ใบกำกับภาษีนี้ออกสำหรับ {{docNo}} / {{receiptNos}} และไม่บันทึกบัญชีใด ๆ — ยอดต้องคงตรงกับที่ได้รับชำระ: {{paid}} ภาษีมูลค่าเพิ่ม {{paidVat}}',
  p_cdoc_taxfor_fill: 'ใช้รายการของ {{docNo}}',
  p_taxfor_err_type: 'ใบกำกับภาษีเท่านั้นที่ออกสำหรับใบแจ้งหนี้ที่ชำระแล้ว — กรุณาเลือกประเภทเอกสารเป็น “ใบกำกับภาษี”',
  p_taxfor_err_not_vat: 'เฉพาะบริษัทที่จดทะเบียนภาษีมูลค่าเพิ่มและออกเอกสารภาษีไทยเท่านั้นที่ออกใบกำกับภาษี — จึงไม่มีใบกำกับภาษีที่จะออกสำหรับใบแจ้งหนี้ที่ชำระแล้ว',
  p_taxfor_err_not_found: 'ไม่พบใบแจ้งหนี้ที่ชำระแล้วซึ่งใบกำกับภาษีนี้ออกให้ ในเอกสารของบริษัทนี้',
  p_taxfor_err_void: '{{docNo}} ถูกยกเลิกแล้ว — ไม่มีการขายที่ชำระแล้วให้ออกใบกำกับภาษี',
  p_taxfor_err_not_invoice: '{{docNo}} ไม่ใช่ใบแจ้งหนี้ธรรมดา — ใบกำกับภาษีที่ไม่บันทึกบัญชีซ้ำออกได้เฉพาะสำหรับใบแจ้งหนี้ธรรมดาที่ใบเสร็จรับเงินบันทึกการขายไว้แล้ว',
  p_taxfor_err_plain: '{{docNo}} เป็นเอกสารทั่วไป ไม่ใช่ใบแจ้งหนี้ตามระบบภาษีไทย — ใบเสร็จรับเงินของเอกสารนี้ไม่ได้บันทึกการขายตามระบบภาษีไทยให้ใบกำกับภาษีอ้างอิง',
  p_taxfor_err_customer: '{{docNo}} ออกให้ {{customer}} — ใบกำกับภาษีสำหรับเอกสารนี้ต้องออกให้ลูกค้ารายเดียวกัน',
  p_taxfor_err_unpaid_part: '{{docNo}} ยังได้รับชำระไม่ครบ — ได้รับแล้ว {{received}} จาก {{total}} ({{receiptNos}}) การขายจึงบันทึกบัญชีเพียงบางส่วน กรุณาบันทึกการรับชำระส่วนที่เหลือของ {{docNo}} ก่อน แล้วจึงออกใบกำกับภาษี',
  p_taxfor_err_unpaid_none: '{{docNo}} ยังไม่ได้รับชำระ — ยังไม่มีใบเสร็จรับเงินบันทึกการขายนี้ จึงไม่มีรายการในสมุดบัญชีให้ใบกำกับภาษีนี้อ้างอิง',
  p_taxfor_err_adjusted: '{{docNo}} หรือใบเสร็จรับเงินของเอกสารนี้ถูกปรับปรุงด้วย {{nos}} แล้ว — ยอดที่บันทึกบัญชีไว้ไม่ใช่ยอดที่ได้รับชำระอีกต่อไป ใบกำกับภาษีจึงอ้างอิงไม่ได้',
  p_taxfor_err_taken: 'ได้ออกใบกำกับภาษี {{tivNo}} สำหรับ {{docNo}} ไปแล้ว — ใบแจ้งหนี้ที่ชำระแล้วหนึ่งฉบับมีใบกำกับภาษีเช่นนี้ได้หนึ่งฉบับ หากต้องการออกใหม่ กรุณายกเลิก {{tivNo}} ก่อน',
  p_taxfor_err_currency: '{{docNo}} ได้รับชำระเป็นสกุล {{currency}} — ใบกำกับภาษีต้องออกเป็นสกุล {{currency}} เช่นกัน',
  p_taxfor_err_amount: '{{docNo}} ได้รับชำระ {{paid}} ภาษีมูลค่าเพิ่ม {{paidVat}} ({{receiptNos}}) แต่ใบกำกับภาษีนี้เป็น {{total}} ภาษีมูลค่าเพิ่ม {{vat}} — ใบกำกับภาษีสำหรับใบแจ้งหนี้ที่ชำระแล้วไม่บันทึกบัญชีซ้ำ จึงต้องมียอดตรงกับที่ได้รับชำระทุกประการ กรุณาปรับรายการให้เป็น {{paid}} ภาษีมูลค่าเพิ่ม {{paidVat}}',
  p_taxfor_err_not_chain: 'ใบแจ้งหนี้ที่เลือกไม่ได้ออกจากใบเสนอราคา {{quoteNo}} — การแปลงต้องอ้างอิงใบแจ้งหนี้ที่ชำระแล้วของใบเสนอราคานี้เท่านั้น',
  p_taxfor_err_choose: 'ใบแจ้งหนี้ {{docNos}} ที่ออกจากใบเสนอราคา {{quoteNo}} ได้รับชำระเต็มจำนวนทุกฉบับ — กรุณาเลือกว่าใบกำกับภาษีนี้ออกสำหรับฉบับใด',
  p_taxfor_err_before: 'ใบกำกับภาษีนี้ลงวันที่ {{date}} ซึ่งก่อน {{earliest}} — {{docNo}} ลงวันที่ {{invoiceDate}} และได้รับชำระเงินเมื่อ {{paidDate}} ({{receiptNos}}) ใบกำกับภาษีสำหรับใบแจ้งหนี้ที่ชำระแล้วต้องลงวันที่ไม่ก่อนทั้งสองวันนี้: กรุณาลงวันที่ {{earliest}} หรือหลังจากนั้น',
  p_taxfor_err_closed: 'วันที่ {{date}} อยู่ในรอบระยะเวลาบัญชีที่ปิดแล้ว ({{from}} – {{to}}) — ใบกำกับภาษีสำหรับ {{docNo}} ลงวันที่ในรอบนั้นไม่ได้ แม้ใบกำกับภาษีนี้จะไม่บันทึกสิ่งใดในสมุดบัญชี แต่ไม่มีการออกใบกำกับภาษีในรอบบัญชีที่ปิดแล้ว',
  p_taxfor_err_filed: 'ภาษีมูลค่าเพิ่มเดือน {{ym}} ถูกทำเครื่องหมายว่ายื่นแล้วในศูนย์ยื่นแบบ — ใบกำกับภาษีสำหรับ {{docNo}} ลงวันที่ {{date}} จะเป็นใบกำกับภาษีฉบับใหม่ในเดือนที่ยื่นแบบไปแล้ว แม้ใบกำกับภาษีนี้จะไม่บันทึกสิ่งใดในสมุดบัญชี แต่ไม่มีการออกใบกำกับภาษีในเดือนที่ยื่นแล้ว: กรุณายกเลิกเครื่องหมายยื่นแล้วของ {{ym}} ที่ศูนย์ยื่นแบบก่อน แล้วจึงออก',
  p_crec_err_tax_for: 'ใบกำกับภาษี {{invoiceNo}} ออกสำหรับใบแจ้งหนี้ {{forNo}} ซึ่งชำระแล้วตามใบเสร็จรับเงิน {{receiptNos}} — ไม่มียอดค้างชำระ การออกใบเสร็จรับเงินอีกจะบันทึกการรับชำระเดียวกันซ้ำสองครั้ง',
  p_note_err_debit_rebilled: '{{docNo}} ลดหนี้เต็มจำนวนแล้ว และขณะนี้ {{nos}} เป็นเอกสารเรียกเก็บของ {{quoteNo}} {{docNo}} จึงไม่ใช่ยอดขายนี้อีกต่อไป หากออกใบเพิ่มหนี้บนเอกสารนี้จะซ้อนกับ {{nos}} กรุณาออกใบเพิ่มหนี้บน {{nos}} แทน',
  p_note_err_debit_rebilled_invoice: '{{docNo}} ลดหนี้เต็มจำนวนแล้ว และขณะนี้ {{nos}} เป็นเอกสารเรียกเก็บของ {{quoteNo}} {{docNo}} จึงไม่ใช่ยอดขายนี้อีกต่อไป หากออกใบเพิ่มหนี้บนเอกสารนี้จะซ้อนกับ {{nos}} กรุณาแก้ไข {{nos}} แทน หรือเมื่อชำระแล้วจึงออกใบเพิ่มหนี้บนใบเสร็จรับเงินของเอกสารนั้น',
  p_note_err_tax_for: 'ใบกำกับภาษี {{docNo}} ออกสำหรับใบแจ้งหนี้ {{forNo}} ซึ่งชำระแล้วตามใบเสร็จรับเงิน {{receiptNos}} และไม่ได้บันทึกบัญชีใด ๆ — การขายบันทึกบัญชีผ่าน {{receiptNos}} ใบลดหนี้หรือใบเพิ่มหนี้ของการขายนี้ออกอ้างอิงใบเสร็จรับเงินนั้น หลังจากยกเลิก {{docNo}} แล้วเท่านั้น: กรุณายกเลิก {{docNo}} ก่อน',
  p_doc_void_tax_for_first: '{{docNo}} เป็นการรับชำระใบแจ้งหนี้ {{invoiceNo}} ซึ่งได้ออกใบกำกับภาษี {{tivNo}} ให้แล้ว — {{tivNo}} ไม่บันทึกบัญชีเพราะการรับชำระนี้บันทึกไว้แล้ว กรุณายกเลิก {{tivNo}} ก่อน',
  p_note_err_tax_for_first_receipt: '{{docNo}} เป็นการรับชำระใบแจ้งหนี้ {{invoiceNo}} ซึ่งได้ออกใบกำกับภาษี {{tivNo}} ให้แล้ว — {{tivNo}} อ้างอิงการขายนี้และแสดงภาษีมูลค่าเพิ่มของการขายนี้ จึงออกใบลดหนี้หรือใบเพิ่มหนี้เพื่อเปลี่ยนแปลงการขายนี้ไม่ได้ตราบที่ {{tivNo}} ยังมีผลอยู่ กรุณายกเลิก {{tivNo}} ก่อน แล้วจึงออกอ้างอิง {{docNo}}',
  p_note_err_tax_for_first_invoice: 'ใบแจ้งหนี้ {{docNo}} ได้รับชำระตามใบเสร็จรับเงิน {{receiptNos}} และได้ออกใบกำกับภาษี {{tivNo}} ให้แล้ว — ออกใบลดหนี้หรือใบเพิ่มหนี้เพื่อเปลี่ยนแปลงการขายนี้ไม่ได้ตราบที่ {{tivNo}} ยังมีผลอยู่ กรุณายกเลิก {{tivNo}} ก่อน แล้วจึงออกอ้างอิงใบเสร็จรับเงินของการขายนี้ ({{receiptNos}}) — ใบแจ้งหนี้ธรรมดาไม่ได้ปรับปรุงด้วยใบลดหนี้หรือใบเพิ่มหนี้โดยตรง',
  p_conv_taxfor_for: 'ใบกำกับภาษีนี้จะออกสำหรับ {{docNo}} / {{receiptNos}}: {{docNo}} ที่ออกจากใบเสนอราคานี้ได้รับชำระแล้ว ({{paid}} ภาษีมูลค่าเพิ่ม {{paidVat}}) และบันทึกบัญชีแล้วผ่าน {{receiptNos}} ใบกำกับภาษีนี้จึงไม่บันทึกบัญชีซ้ำ และระบุว่าออกสำหรับ {{docNo}} / {{receiptNos}}',
  p_conv_taxfor_choose: 'มีใบแจ้งหนี้จากใบเสนอราคานี้มากกว่าหนึ่งฉบับที่ได้รับชำระเต็มจำนวน ใบกำกับภาษีนี้ออกสำหรับฉบับใด? ใบกำกับภาษีจะไม่บันทึกบัญชีซ้ำ และระบุใบแจ้งหนี้กับใบเสร็จรับเงินฉบับนั้น',
  p_conv_taxfor_choose_ph: '— เลือกใบแจ้งหนี้ที่ชำระแล้ว —',
  p_doc_void_dn_credited: 'ใบลดหนี้ของ {{docNo}} ({{nos}}) ได้ลดหนี้ส่วนที่ {{dnNo}} เพิ่มไว้แล้วบางส่วน — หากยกเลิก ยอดที่ลดหนี้จะเกินยอดที่เรียกเก็บ กรุณายกเลิกใบลดหนี้เหล่านั้นก่อน',
  p_doc_void_credit_rebilled: '{{noteNo}} กลับรายการ {{docNo}} แล้ว และขณะนี้ {{nos}} เป็นเอกสารเรียกเก็บของ {{quoteNo}} หากยกเลิก {{docNo}} จะกลับมาพร้อมกับ {{nos}} และบันทึกยอดขายเดียวกันสองครั้ง กรุณายกเลิก {{nos}} ก่อน',
  p_doc_void_credit_rejected: '{{noteNo}} กลับรายการ {{docNo}} แล้ว และ {{quoteNo}} ถูกทำเครื่องหมายว่าปฏิเสธแล้ว หากยกเลิก จะมีเอกสารที่ยังมีผลอยู่บนใบเสนอราคาที่ปฏิเสธแล้ว กรุณาเปลี่ยนสถานะก่อน',
  p_doc_void_receipt_credited: 'การยกเลิก {{docNo}} จะทำให้{{docWord}} {{invoiceNo}} กลับมามียอดค้างชำระ {{outstanding}} ขณะที่ใบลดหนี้ {{nos}} ยังอ้างอิงใบเสร็จรับเงิน {{receiptNos}} ของเอกสารนั้นอยู่ — โปรดยกเลิกใบลดหนี้ก่อน',
  p_note_err_receipt_bill_owes: '{{docWord}} {{invoiceNo}} ยังมียอดค้างชำระ {{outstanding}} จึงออกใบลดหนี้อ้างอิงใบเสร็จรับเงิน {{receiptNo}} ไม่ได้ — ออกใบลดหนี้อ้างอิงใบเสร็จรับเงินได้ต่อเมื่อเอกสารที่ใบเสร็จนั้นรับชำระได้รับชำระครบถ้วนแล้วเท่านั้น หากต้องการเปลี่ยนราคาของ {{invoiceNo}} ก่อนได้รับชำระครบ โปรดยกเลิกใบเสร็จรับเงินของเอกสารนั้นแล้วแก้ไข {{invoiceNo}}',
  p_note_err_receipt_debit_owes: 'ใบเพิ่มหนี้ {{dnNo}} ซึ่งออกอ้างอิงใบเสร็จรับเงิน {{receiptNo}} ยังมียอดค้างชำระ {{outstanding}} จึงออกใบลดหนี้อ้างอิง {{receiptNo}} ไม่ได้ — ออกใบลดหนี้อ้างอิงใบเสร็จรับเงินได้ต่อเมื่อใบเพิ่มหนี้ที่ออกอ้างอิงใบเสร็จนั้นได้รับชำระครบถ้วนแล้วเท่านั้น',
  // งานปรับส่วนเพิ่มรอบที่ 5 (2026-10-27): ใบลดหนี้ลงวันที่ก่อนเอกสารที่นำมาลดยอด (ข้อ 1); รายการที่พิมพ์เองเกินยอดคงเหลือของช่องที่ไม่มี VAT (ข้อ 2)
  p_note_err_before_source: 'ใบลดหนี้นี้ลงวันที่ {{noteDate}} ซึ่งก่อน{{docWord}} {{docNo}} (ลงวันที่ {{date}}) ที่ใบลดหนี้นี้นำมาลดยอด — ใบลดหนี้จะลงวันที่ก่อนเอกสารที่นำมาลดยอดไม่ได้ กรุณาลงวันที่ {{date}} หรือหลังจากนั้น',
  p_note_err_cat_left: '{{docNo}} เหลือยอดที่ลดได้ในประเภท "{{category}}" {{left}} แต่รายการที่พิมพ์เองในใบลดหนี้นี้ลดยอดในประเภท "{{category}}" {{takes}} — ใบลดหนี้ลดยอดได้ตามประเภท VAT ที่เรียกเก็บไว้เท่านั้น จึงลดยอดในประเภท "{{category}}" เพิ่มอีกไม่ได้',
  p_note_err_cat_left_dn: '{{docNo}} และใบเพิ่มหนี้ ({{dnNos}}) เหลือยอดที่ลดได้ในประเภท "{{category}}" {{left}} แต่รายการที่พิมพ์เองในใบลดหนี้นี้ลดยอดในประเภท "{{category}}" {{takes}} — ใบลดหนี้ลดยอดได้ตามประเภท VAT ที่เรียกเก็บไว้เท่านั้น จึงลดยอดในประเภท "{{category}}" เพิ่มอีกไม่ได้',
  p_note_box_zero: 'VAT 0% (อัตราศูนย์)', p_note_box_exempt: 'ยกเว้น VAT', p_note_box_outscope: 'นอกขอบข่ายภาษี (เงินทดรองจ่ายตามต้นทุน)', p_note_box_none: 'ไม่มี VAT',
  p_note_err_none_flat_vat: 'ใบลดหนี้นี้ลดยอดมูลค่าที่ไม่มี VAT {{amount}} ซึ่ง {{docNos}} บันทึกไว้ในช่องฐานภาษี 7% โดยไม่มี VAT พร้อมกับรายการที่มี VAT — ใบลดหนี้ฉบับเดียวกลับรายการทั้งสองอย่างให้ถูกต้องไม่ได้ กรุณาออกใบลดหนี้สำหรับรายการ 7% แยกเป็นฉบับต่างหาก และออกอีกฉบับสำหรับรายการอื่นทั้งหมด (ฉบับนั้นจะไม่มี VAT)',
  p_note_err_fill_unlinked: 'มีใบลดหนี้ที่ไม่ได้ระบุรายการ ({{nos}}) ลดยอดของ {{docNo}} ไปแล้ว จึงไม่ทราบยอดคงเหลือของแต่ละรายการ และไม่สามารถลดหนี้โดยอ้างอิงรายการได้อีก กรุณาลบรายการที่ระบบกรอกจาก {{docNo}} (×) แล้วเพิ่มรายการของใบลดหนี้นี้ใหม่ด้วยตนเอง',
  p_note_err_box_locked: 'รายการที่ {{n}} ลดหนี้ "{{line}}" ของ {{docNo}} ซึ่งเรียกเก็บแบบ {{box}} ใบลดหนี้ต้องลดหนี้รายการตามที่เรียกเก็บไว้ ลักษณะรายการจึงต้องคงไว้ตามที่กรอกมา — ห้ามย้ายมูลค่าระหว่างช่องของแบบแสดงรายการภาษีมูลค่าเพิ่ม',
  p_note_charge_locked_title: 'รายการที่กรอกจากเอกสารที่ลดหนี้จะคงลักษณะรายการที่เรียกเก็บไว้ — ใบลดหนี้ลดหนี้ตามที่เรียกเก็บ',
  p_note_err_cat_left_out: '{{docs}}: เหลือยอดที่ลดได้ในประเภท "ไม่มี VAT" {{left}} แต่รายการที่พิมพ์เองในใบลดหนี้นี้ลดยอดในประเภท "ไม่มี VAT" {{takes}} — มูลค่าที่ไม่มี VAT ที่ยังลดได้อีก {{sibLeft}} เรียกเก็บไว้เป็นเงินที่จ่ายแทนลูกค้าตามต้นทุน (นอกขอบข่ายภาษี) หากจะลดยอดส่วนนั้น กรุณาเลือกลักษณะรายการเป็น "{{charge}}" ในรายการที่พิมพ์เอง',
  p_note_err_cat_left_own: '{{docs}}: เหลือยอดที่ลดได้ในประเภท "นอกขอบข่ายภาษี (เงินทดรองจ่ายตามต้นทุน)" {{left}} แต่รายการที่พิมพ์เองในใบลดหนี้นี้ลดยอดในประเภทนี้ {{takes}} — มูลค่าที่ไม่มี VAT ที่ยังลดได้อีก {{sibLeft}} ไม่ได้เรียกเก็บไว้เป็นเงินที่จ่ายแทนลูกค้า หากจะลดยอดส่วนนั้น กรุณาเลือกลักษณะรายการเป็น "{{charge}}" ในรายการที่พิมพ์เอง',
  p_doc_void_dn_drawn: 'ใบลดหนี้ของ {{docNo}} ({{nos}}) ลดยอดมูลค่าที่ {{dnNo}} เรียกเก็บในประเภท "{{box}}" — หากยกเลิก ยอดที่ลดหนี้ในประเภท "{{box}}" จะเกินยอดที่เรียกเก็บ กรุณายกเลิกใบลดหนี้เหล่านั้นก่อน',
  p_doc_void_dn_drawn_later: 'ใบลดหนี้ของ {{docNo}} ({{nos}}) ลดยอดบางส่วนจาก {{dnNo}} — หากยกเลิก ส่วนนั้นจะย้ายไปที่ใบเพิ่มหนี้ {{nextNo}} ลงวันที่ {{date}} ซึ่งหลังใบลดหนี้ {{cnNo}} (ลงวันที่ {{cnDate}}) และใบลดหนี้จะลดยอดเอกสารที่ลงวันที่หลังตัวเองไม่ได้ กรุณายกเลิกใบลดหนี้เหล่านั้นก่อน',
  p_doc_void_receipt_debit_credited: 'การยกเลิก {{docNo}} จะทำให้ใบเพิ่มหนี้ {{invoiceNo}} กลับมามียอดค้างชำระ {{outstanding}} ขณะที่ใบลดหนี้ {{nos}} ยังอ้างอิงใบเสร็จรับเงิน {{receiptNos}} ซึ่งใบเพิ่มหนี้นั้นปรับปรุงอยู่ — โปรดยกเลิกใบลดหนี้ก่อน',
  p_doc_void_note_held: 'จะยกเลิก {{docNo}} ได้ก็ต่อเมื่อยกเลิก{{noteWord}} {{noteNo}} แล้ว แต่ขณะนี้ยกเลิก {{noteNo}} ไม่ได้: {{reason}}',
  p_note_type: 'ประเภท *',
  // pager
  p_pager_first: 'หน้าแรก',
  p_pager_last: 'หน้าสุดท้าย',
  p_pager_next: 'ถัดไป',
  p_pager_no_records: 'ไม่มีข้อมูล',
  p_pager_page_of: 'หน้า {{page}} จาก {{pages}}',
  p_pager_per_page: 'ต่อหน้า',
  p_pager_prev: 'ก่อนหน้า',
  p_pager_showing: 'แสดง {{from}}–{{to}} จาก {{total}}',
  // pdf
  p_pdf_failed: 'สร้าง PDF ไม่สำเร็จ',
  // prod
  p_prod_add: '+ เพิ่มรายการ',
  p_prod_confirm_remove: 'ลบ {{name}} หรือไม่?',
  p_prod_empty: 'ยังไม่มีรายการ — เพิ่มสินค้าหรือบริการที่ท่านขาย',
  p_prod_f_name: 'ชื่อ *',
  p_prod_f_price: 'ราคาต่อหน่วย ($t(cur_word))',
  p_prod_f_unit: 'หน่วย (เช่น ชิ้น, ชั่วโมง)',
  p_prod_f_vat_checkbox: 'มี VAT 7%',
  p_prod_f_wht: 'ภาษีหัก ณ ที่จ่ายเริ่มต้น (WHT)',
  p_prod_f_wht_hint: 'สินค้าโดยทั่วไป 0% บริการ 3% ค่าเช่า 5% ผู้ซื้อจะหักไว้และออกหนังสือรับรองการหักภาษี ณ ที่จ่าย (50 ทวิ) ให้ท่าน',
  p_prod_modal_edit: 'แก้ไขรายการ',
  p_prod_modal_new: 'รายการใหม่',
  p_prod_photo_hint: 'แสดงในรายการสินค้าของท่าน',
  p_prod_photo_label: 'รูปภาพ (ไม่บังคับ)',
  p_prod_search_ph: 'ค้นหาชื่อหรือหน่วย…',
  p_prod_th_name: 'ชื่อ',
  p_prod_th_price: 'ราคา ($t(cur_word))',
  p_prod_th_unit: 'หน่วย',
  p_prod_th_vat: 'VAT',
  p_prod_title: 'สินค้า/บริการของฉัน',
  p_prod_vat_yes: '{{rate}}%',
  // prof
  p_prof_address: 'ที่อยู่ที่แสดงบนเอกสาร',
  p_prof_biz_goods: 'สินค้า — ความรับผิดในการเสียภาษีมูลค่าเพิ่มเกิดขึ้นเมื่อส่งมอบสินค้า จึงต้องออกใบกำกับภาษีพร้อมกับการส่งมอบ (มาตรา 78)',
  p_prof_biz_hint: 'ใช้กำหนดประเภทเอกสารเริ่มต้นตอนสร้างเอกสารเท่านั้น — ท่านเปลี่ยนได้ในแต่ละฉบับ',
  p_prof_biz_mixed: 'ทั้งสินค้าและบริการ',
  p_prof_biz_service: 'บริการ — ความรับผิดในการเสียภาษีมูลค่าเพิ่มเกิดขึ้นเมื่อได้รับชำระเงิน (มาตรา 78/1)',
  p_prof_biz_type: 'ประเภทธุรกิจ',
  p_prof_company_name: 'ชื่อบริษัท (แก้ไขไม่ได้)',
  p_prof_default_wht: 'ภาษีหัก ณ ที่จ่ายเริ่มต้นของรายการใหม่',
  p_prof_default_wht_hint: 'ใช้เมื่อรายการนั้นไม่ได้อ้างอิงสินค้า/บริการในระบบ',
  p_prof_doc_cn: 'ใบลดหนี้',
  p_prof_doc_dn: 'ใบเพิ่มหนี้',
  p_prof_doc_inv: 'ใบแจ้งหนี้',
  p_prof_doc_pro: 'ใบแจ้งหนี้ชั่วคราว (Proforma)',
  p_prof_doc_quo: 'ใบเสนอราคา',
  p_prof_doc_rec: 'ใบเสร็จรับเงิน',
  p_prof_doc_tax: 'ใบกำกับภาษี',
  p_prof_email: 'อีเมล',
  p_prof_failed: 'ไม่สำเร็จ',
  p_prof_identity_note: 'ข้อมูลแสดงตัวตนได้รับการตรวจสอบจากเอกสารบริษัทของท่าน และแก้ไขได้โดยทีมงานของเราเท่านั้น',
  p_prof_logo_hint: 'พิมพ์ที่มุมบนซ้ายของใบแจ้งหนี้ ใบเสนอราคา และใบเสร็จรับเงิน',
  p_prof_logo_label: 'โลโก้บริษัท',
  p_prof_month_seg: 'ใส่ปี-เดือนในเลขที่เอกสาร',
  p_prof_month_seg_hint: '— เปิด: INV-{{yyyymm}}-001 · ปิด: INV-001 ปิดตัวเลือกนี้หากเลขที่เอกสารของท่านมีเดือน/ปีอยู่แล้ว',
  p_prof_numbering: 'การออกเลขที่เอกสาร',
  p_prof_numbering_note: '— ความต่อเนื่องของเลขที่เอกสารเป็นความรับผิดชอบของท่าน (ยอมรับเมื่อ {{date}})',
  p_prof_phone: 'โทรศัพท์',
  p_prof_prefix_next: '{{docType}} — คำนำหน้า / เลขที่ถัดไป',
  p_prof_pw_changed: 'เปลี่ยนรหัสผ่านเรียบร้อย ✓',
  p_prof_saved: 'บันทึกแล้ว ✓',
  p_prof_branch: 'สถานประกอบการ (แก้ไขไม่ได้)',
  p_prof_branch_head: 'สำนักงานใหญ่',
  p_prof_branch_hint: 'พิมพ์ถัดจากเลขประจำตัวผู้เสียภาษีบนใบกำกับภาษี ใบลดหนี้ และใบเพิ่มหนี้',
  p_prof_branch_n: 'สาขา',
  p_prof_tax_id: 'เลขประจำตัวผู้เสียภาษี (แก้ไขไม่ได้)',
  p_prof_title: 'ข้อมูลบริษัท',
  p_prof_vat_hint: 'จดทะเบียน ⇒ ใบแจ้งหนี้ของท่านจะคิดภาษีมูลค่าเพิ่ม 7% และใบเสร็จรับเงินของท่านจะเป็นใบกำกับภาษี',
  p_prof_vat_not_registered: 'ไม่ได้จดทะเบียน',
  p_prof_vat_reg: 'การจดทะเบียนภาษีมูลค่าเพิ่ม (VAT)',
  p_prof_vat_registered: 'จดทะเบียนภาษีมูลค่าเพิ่ม (VAT 7%)',
  p_prof_website: 'เว็บไซต์',
  // pw
  p_pw_confirm: 'ยืนยันรหัสผ่านใหม่',
  p_pw_current: 'รหัสผ่านปัจจุบัน',
  p_pw_err_current_required: 'กรุณากรอกรหัสผ่านปัจจุบัน',
  p_pw_err_failed: 'ไม่สำเร็จ',
  p_pw_err_min_length: 'อย่างน้อย 8 ตัวอักษร',
  p_pw_err_mismatch: 'รหัสผ่านไม่ตรงกัน',
  p_pw_hint_current: 'ต้องกรอกรหัสผ่านปัจจุบัน',
  p_pw_hint_first: 'เพื่อความปลอดภัย กรุณาตั้งรหัสผ่านใหม่แทนรหัสชั่วคราว',
  p_pw_new: 'รหัสผ่านใหม่',
  p_pw_title_change: 'เปลี่ยนรหัสผ่าน',
  p_pw_title_first: 'ตั้งรหัสผ่านของท่านเอง',
  // row
  p_row_delete: 'ลบ',
  p_row_edit: 'แก้ไข',
  // search
  p_search_clear: 'ล้าง',
  // select
  p_select_placeholder: '— เลือก —',
  p_pick_search: 'พิมพ์เพื่อค้นหา…',
  p_pick_none: 'ไม่พบข้อมูล — ลองใช้คำน้อยลง',
  p_pick_more: 'แสดง {{shown}} จาก {{total}} — พิมพ์เพื่อค้นหาที่เหลือ',
  p_cust_add_title: 'เพิ่มลูกค้า',
  p_cust_add_save: 'เพิ่มและเลือก',
  p_cust_name_req: 'ต้องระบุชื่อลูกค้า',
  p_cust_add_failed: 'เพิ่มลูกค้าไม่สำเร็จ',
  p_prof_f_prefix: 'คำนำหน้า',
  p_prof_f_next: 'เลขถัดไป',
  p_prof_f_date: 'วันที่ในเลขที่เอกสาร',
  p_prof_f_reset: 'เริ่มนับใหม่',
  p_prof_f_pad: 'จำนวนหลัก',
  p_prof_date_none: 'ไม่มีวันที่',
  p_prof_reset_none: 'ไม่เริ่มใหม่',
  p_prof_reset_year: 'ทุกปี',
  p_prof_reset_month: 'ทุกเดือน',
  p_prof_reset_day: 'ทุกวัน',
  p_prof_scheme_bad: 'ต้องมี {{need}} ในเลขที่เอกสาร มิฉะนั้นเลขเดิมจะซ้ำในรอบถัดไป',
  p_prof_numbering_hint: 'เลขที่เอกสาร = คำนำหน้า · วันที่ · เลขลำดับ การเริ่มนับใหม่ต้องมีวันที่ที่แยกรอบได้: รายปีต้องมี YYYY รายเดือน YYYYMM รายวัน YYYYMMDD การแก้ไขมีผลกับเอกสารที่ออกใหม่เท่านั้น เอกสารที่ออกไปแล้วคงเลขเดิม',
  // รูปแบบเลขที่รายการค่าใช้จ่าย / การจ่ายชำระเจ้าหนี้ของบริษัทเอง (2026-10-28, EXP-8)
  p_regno_head: 'รายการค่าใช้จ่ายและการจ่ายชำระเจ้าหนี้',
  p_regno_exp: 'รายการค่าใช้จ่าย',
  p_regno_pmt: 'การจ่ายชำระเจ้าหนี้',
  p_regno_po: 'ใบสั่งซื้อ',
  p_regno_claim: 'ใบเบิกค่าใช้จ่าย',
  p_regno_f_sep: 'ตัวคั่น',
  p_regno_sep_dash: 'มี “-”',
  p_regno_sep_none: 'ไม่มี',
  p_regno_hint: 'รายการค่าใช้จ่ายและการจ่ายชำระเจ้าหนี้แต่ละรายการได้รับเลขเมื่อสร้างรายการ ท่านกำหนดรูปแบบของเลขได้ที่นี่ เลขที่ออกไปแล้วจะไม่เปลี่ยน เลขถัดไปปรับได้แต่ขึ้นเท่านั้น เมื่อเปลี่ยนรูปแบบ รูปแบบใหม่จะเริ่มจากเลขถัดไปที่แสดงไว้ — คือ 1 เว้นแต่ท่านเลือกเลขที่สูงกว่า ส่วนรูปแบบที่เคยใช้มาก่อนจะนับต่อจากเลขที่ออกไปแล้ว — และเลขที่เคยใช้แล้ว แม้เป็นรายการที่ยกเลิกหรือลบ จะไม่ถูกออกซ้ำ คำนำหน้าที่เลขซึ่งออกไปแล้วใช้อยู่ยังคงเป็นของชุดเดิม เลขถัดไปที่ท่านเลือกมีผลจนกว่าจะเริ่มนับใหม่ในรอบถัดไป',
  p_regno_err_prefix_empty: 'คำนำหน้าต้องเป็นตัวอักษรหรือตัวเลข 1–8 ตัว',
  p_regno_err_prefix_digit_end: 'หากไม่มีตัวคั่น คำนำหน้าต้องลงท้ายด้วยตัวอักษร — “{{prefix}}” จะติดกับวันที่หรือเลขลำดับจนแยกไม่ออก',
  p_regno_err_prefix_taken: '{{prefix}} เป็นคำนำหน้าของ{{other}}อยู่แล้ว — แต่ละชุดต้องมีคำนำหน้าของตนเอง เพื่อให้เลขบอกได้ว่าเป็นเอกสารอะไร',
  p_regno_err_prefix_held: 'เลขที่ของ{{other}}ที่ออกไปแล้วขึ้นต้นด้วย {{prefix}} — แต่ละชุดต้องมีคำนำหน้าของตนเอง เพื่อให้เลขบอกได้ว่าเป็นเอกสารอะไร',
  p_regno_err_scheme_bad: 'รูปแบบนี้ไม่ถูกต้อง',
  p_regno_err_start_bad: 'เลขถัดไปต้องเป็นจำนวนเต็มตั้งแต่ 1 ถึง {{max}}',
  p_regno_err_start_down: 'เลขถัดไปคือ {{next}} แล้ว — ปรับได้แต่ขึ้นเท่านั้น',
  p_regno_checking: 'กำลังตรวจสอบ…',
  p_regno_failed: 'ตรวจสอบรูปแบบนี้ไม่ได้ — โปรดลองอีกครั้ง',
  p_flt_all: 'ทั้งหมด',
  p_flt_custom: 'กำหนดเอง',
  p_flt_clear: 'ล้างตัวกรองวันที่',
  p_flt_period: 'ช่วงเวลา',
  p_flt_show_voided: 'แสดงที่ยกเลิกแล้ว',
  p_imp_open_short: 'นำเข้า',
  p_menu_notes_hint: 'แก้ไขเอกสารที่ออกไปแล้ว',
  p_flt_this_month: 'เดือนนี้',
  p_flt_last_month: 'เดือนที่แล้ว',
  p_flt_from: 'ตั้งแต่วันที่',
  p_flt_to: 'ถึงวันที่',
  // send
  p_send_attachment: 'ไฟล์แนบ',
  p_send_bcc: 'สำเนาถึงท่าน',
  p_send_btn: 'ส่งอีเมล',
  p_send_done: 'เสร็จสิ้น',
  p_send_err_prepare: 'ไม่สามารถเตรียมอีเมลได้',
  p_send_err_send: 'ไม่สามารถส่งอีเมลได้',
  p_send_from: 'จาก',
  p_send_history: 'ประวัติการส่ง',
  p_send_message: 'ข้อความ',
  p_send_no_replyto: '— กรุณาเพิ่มอีเมลบริษัทของท่านในหน้าข้อมูลบริษัทก่อน —',
  p_send_preparing: 'กำลังเตรียม…',
  p_send_preview: 'ดูตัวอย่าง',
  p_send_preview_hint: 'เปิดไฟล์ PDF ฉบับเดียวกับที่ลูกค้าจะได้รับ — ครบทั้งสองฉบับ ทั้งต้นฉบับและสำเนา',
  p_send_replyto: 'ตอบกลับที่',
  p_send_save_addr: 'บันทึกอีเมลนี้ไว้กับ {{customerName}} เพื่อให้กรอกอัตโนมัติในครั้งถัดไป',
  p_send_sending: 'กำลังส่ง…',
  p_send_sent_ok: '✓ ส่งไปยัง {{email}} แล้ว และมีสำเนาอยู่ในกล่องจดหมายของท่าน',
  p_send_subject: 'เรื่อง',
  p_send_subtitle: 'ระบบจะแนบไฟล์ PDF ให้อัตโนมัติ และเมื่อลูกค้าตอบกลับ อีเมลจะกลับมาถึงท่านโดยตรง',
  p_send_title: 'ส่ง {{docNo}}',
  p_send_to_label: 'อีเมลลูกค้า *',
  p_send_to_ph: 'name@example.com',
  // sort
  p_sort_th_tooltip: 'เรียงตาม {{label}}',
  // vat
  p_vat_hint_exempt: 'รายการที่ได้รับยกเว้นภาษีมูลค่าเพิ่ม (มาตรา 81)',
  p_vat_hint_none: 'ไม่อยู่ในบังคับภาษีมูลค่าเพิ่ม / ไม่ได้จดทะเบียนภาษีมูลค่าเพิ่ม',
  p_vat_hint_standard: 'ภาษีมูลค่าเพิ่มจะคิดเพิ่มจากราคาที่ท่านกรอก',
  p_vat_hint_standard_incl: 'ราคาที่ท่านกรอกรวมภาษีมูลค่าเพิ่มแล้ว',
  p_vat_hint_zero: 'อัตราภาษีร้อยละ 0 (มาตรา 80/1) — เสียภาษีในอัตรา 0% ไม่ใช่การยกเว้นภาษี',
  p_vat_opt_exempt: 'ยกเว้นภาษีมูลค่าเพิ่ม',
  p_vat_opt_none: 'ไม่มี VAT',
  p_vat_opt_standard: 'VAT {{rate}}% — แยกนอก',
  p_vat_opt_standard_incl: 'VAT {{rate}}% — รวมใน',
  p_vat_opt_zero: 'VAT 0%',
  // wht
  p_wht_none: 'ไม่หักภาษี ณ ที่จ่าย',
  p_wht_rate: 'หัก ณ ที่จ่าย {{rate}}%',
  p_wht_select_title: 'ภาษีหัก ณ ที่จ่ายที่ผู้ซื้อหักไว้',
};
